import { blinkConstants } from '@thoughtspot/blink-constants';
import { BLINK_FLAGS } from '@thoughtspot/env-flags';
import { jsonConstants } from '@thoughtspot/json-constants';
import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import { Subject } from 'rxjs';
import { getPreference, getTip as getClientStateTip, setPreference, setTip, } from './client-state';
import { canManageVersionControl as _canManageVersionControl, canManageWorksheetViewsTables as _canManageWorksheetViewsTables, canSetupVersionControl as _canSetupVersionControl, getUserPrivileges as _getUserPrivileges, getUserPrivilegesBitMap as _getUserPrivilegesBitMap, hasAccessAnalystStudioPrivilege as _hasAccessAnalystStudioPrivilege, hasAdminPrivileges as _hasAdminPrivileges, hasAuthAdminPrivileges as _hasAuthAdminPrivileges, hasCreateOrEditConnectionsPrivileges as _hasCreateOrEditConnectionsPrivileges, hasCustomCalendarsManagementPrivileges as _hasCustomCalendarsManagementPrivileges, hasDataManagementPrivileges as _hasDataManagementPrivileges, hasDeveloperPrivileges as _hasDeveloperPrivileges, hasDownloadAnswerPrivileges as _hasDownloadAnswerPrivileges, hasDownloadDetailedDataPrivileges as _hasDownloadDetailedDataPrivileges, hasDownloadVisualPrivileges as _hasDownloadVisualPrivileges, hasManageAnalystStudioPrivilege as _hasManageAnalystStudioPrivilege, hasPreviewThoughtspotSagePrivileges as _hasPreviewThoughtspotSagePrivileges, hasPrivilege as _hasPrivilege, hasSchedulingPrivileges as _hasSchedulingPrivileges, hasTagManagementPrivileges as _hasTagManagementPrivileges, hasTrustedAuthPrivileges as _hasTrustedAuthPrivileges, hasUserAdminPrivileges as _hasUserAdminPrivileges, hasViewSystemActivitiesPrivileges as _hasViewSystemActivitiesPrivileges, PRIVILEGES, } from './permissions';
import { CLUSTER_TYPES, CsvUploadMode, DataBaseMode, DEFAULT_PRODUCT_NAME, InviteModalType, PasswordStrategy, PurchaseOption, SubscriptionStatus, SubscriptionType, UserActivationMode, UserPersona, UserState, } from './session-interface';
import { getExposedUserPreference, getExposedUserPreferences, SESSION_USER_PREFERENCES, setExposedUserPreference, } from './user-preferences';
const LOGOUT_TOO_OLD_THRESHOLD = 7 * 24 * 3600 * 1000;
const PREFERENCE_KEY_SAGE_DATA_SOURCE = 'sageDataSource';
const PREFERENCE_KEY_EUREKA_DATA_SOURCE = 'eurekaDataSource';
const OPTIMIZELY_DATAFILE_FETCH_INTERVAL = 300000;
const DEFAULT_ONBOARDING_VIDEO_URL = 'https://fast.wistia.net/embed/iframe/wykwdrk2em';
const TABLE_PRINT_DEFAULT_BATCH_SIZE = 100;
const MAX_NO_OF_FILTER_VALUES = 1000;
const MAX_NO_OF_PAYLOAD_RUNTIME_FILTERS = 1000;
const LIVEBOARD_INSIGHTS_DATA_QUERY_TIMEOUT = 120000;
const logger = create('session-store');
export var RequestAccessLevel;
(function (RequestAccessLevel) {
    RequestAccessLevel[RequestAccessLevel["DISABLE"] = 0] = "DISABLE";
    RequestAccessLevel[RequestAccessLevel["VIEW"] = 1] = "VIEW";
    RequestAccessLevel[RequestAccessLevel["ALL"] = 2] = "ALL";
})(RequestAccessLevel || (RequestAccessLevel = {}));
let sessionInfo = null;
let isInitialized = false;
let locale = 'en-US';
let localeHash = {};
let incidentId = null;
let lastLogoutTime = null;
let automaticallyLoggedOut = false;
let manuallyLoggedOut = false;
let last401Path = '';
let disableHtmlRenderingManually = false;
let flags = null;
const PRIVILEGES_KEY = 'privileges';
const PREFERENCES_KEY = 'preferences';
const DEFAULT_QUARTER_START_MONTH = 1;
const SNOWFLAKE_PARTNER_CONNECT_SOURCE = 'snowflake_partner_connect';
const DISABLE_TS_BRANDING_IN_PDF_KEY = 'disableTSBrandingInPDF';
const DEFAULT_GROUP_MEMBER_SHARE_NOTIFICATION_LIMIT = 500;
const NUM_USERS_IN_ORG = 'numUsersInOrg';
const DEFAULT_MAX_NUM_EXPORT_ROWS = 1000000;
const enliteLocales = new Set(['en', 'en-US', 'en-CA', 'en-GB', 'en-AU']);
export const inIFrame = () => {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        return true;
    }
};
export const isEmbeddedApp = () => {
    return inIFrame() || window.location.href.indexOf('/embed/') > -1;
};
export const setIsInitialized = () => {
    isInitialized = true;
};
export const setSessionInfo = (info, _flags) => {
    sessionInfo = info;
    if (_flags) {
        flags = _flags;
    }
    setIsInitialized();
};
export const getSessionInfo = () => {
    return sessionInfo;
};
export const hasInfo = () => {
    return !!sessionInfo;
};
export const clearSessionInfo = () => {
    sessionInfo = null;
    last401Path = '';
    lastLogoutTime = new Date().getTime();
};
export const getBackendConfig = () => {
    const info = getSessionInfo();
    if (!info || !info.configInfo) {
        return null;
    }
    return info.configInfo;
};
function getConfigInfo(key) {
    const configInfo = getBackendConfig();
    if (!configInfo) {
        return null;
    }
    if (!_.has(configInfo, key)) {
        return null;
    }
    return configInfo[key];
}
export const getIsInitialized = () => {
    return isInitialized;
};
export const setLocale = (localeStr) => {
    locale = localeStr;
};
export const getValue = (flagValue) => {
    if (flagValue.overideValue !== undefined) {
        return flagValue.overideValue;
    }
    const backendReturnValue = getConfigInfo(flagValue.name);
    return _.isNil(backendReturnValue)
        ? flagValue.defaultValue
        : backendReturnValue;
};
export const getLocale = () => {
    return locale;
};
export const getLocaleHash = () => {
    return localeHash[locale] || {};
};
export const setLocaleHash = (hash) => {
    localeHash = hash;
};
export const setTraceId = (id) => {
    incidentId = id;
};
export const clearTraceId = () => {
    incidentId = null;
};
export const getTraceId = () => {
    return incidentId;
};
export const markAutomaticallyLoggedOut = () => {
    automaticallyLoggedOut = true;
};
export const resetAutomaticallyLoggedOut = () => {
    automaticallyLoggedOut = false;
};
export const wasAutomaticallyLoggedOut = () => {
    return automaticallyLoggedOut;
};
export const isLastLogoutTooOld = () => {
    if (_.isUndefined(lastLogoutTime)) {
        return false;
    }
    return new Date().getTime() - lastLogoutTime >= LOGOUT_TOO_OLD_THRESHOLD;
};
export const markManuallyLoggedOut = () => {
    manuallyLoggedOut = true;
};
export const wasManuallyLoggedOut = () => {
    return manuallyLoggedOut;
};
export const setLast401Path = (path) => {
    if (!_.isEqual(path, '/')) {
        last401Path = path;
    }
};
export const getLast401Path = () => {
    return last401Path;
};
export const setDisableHtmlRenderingManually = (isHtmlRenderingDisabled) => {
    disableHtmlRenderingManually = isHtmlRenderingDisabled;
};
export function hasAdminPrivileges() {
    return _hasAdminPrivileges(getSessionInfo());
}
export function hasAuthAdminPrivileges() {
    return _hasAuthAdminPrivileges(getSessionInfo());
}
export function canManageVersionControl() {
    return _canManageVersionControl(getSessionInfo());
}
export function canSetupVersionControl() {
    return _canSetupVersionControl(getSessionInfo());
}
export function hasTagManagementPrivileges() {
    return _hasTagManagementPrivileges(getSessionInfo());
}
export function hasTrustedAuthPrivileges() {
    return _hasTrustedAuthPrivileges(getSessionInfo());
}
export const isRolesEnabled = () => {
    const rolesEnabledFlag = flags?.getValue(BLINK_FLAGS.isRolesEnabled.name);
    if (!_.isUndefined(rolesEnabledFlag)) {
        return rolesEnabledFlag;
    }
    const rolesEnabledClusterFlag = getConfigInfo(jsonConstants.ENABLE_ROLES);
    return _.isNil(rolesEnabledClusterFlag) ? false : rolesEnabledClusterFlag;
};
export function hasDataManagementPrivileges() {
    return _hasDataManagementPrivileges(getSessionInfo());
}
export function canManageWorksheetViewsTables() {
    if (isRolesEnabled()) {
        return (_canManageWorksheetViewsTables(getSessionInfo()) ||
            hasAdminPrivileges());
    }
    return (_hasDataManagementPrivileges(getSessionInfo()) || hasAdminPrivileges());
}
export function hasDeveloperPrivileges() {
    return _hasDeveloperPrivileges(getSessionInfo());
}
export function hasPreviewThoughtspotSagePrivileges() {
    return _hasPreviewThoughtspotSagePrivileges(getSessionInfo());
}
export function hasSchedulingPrivileges() {
    return _hasSchedulingPrivileges(getSessionInfo());
}
export function hasPrivilege(privilege) {
    const sessionInfoObject = getSessionInfo();
    return _hasPrivilege(sessionInfoObject, privilege);
}
export function hasExperimentalFeaturePrivilege() {
    return hasPrivilege(PRIVILEGES.EXPERIMENTALFEATUREPRIVILEGE);
}
export function hasCreateLiveboardPrivilege() {
    return hasPrivilege(PRIVILEGES.CAN_CREATE_LIVEBOARD);
}
export function hasCreateAnswersPrivilege() {
    return hasPrivilege(PRIVILEGES.CAN_CREATE_ANSWERS);
}
export function hasAnalyzeDataPrivilege() {
    return hasPrivilege(PRIVILEGES.CAN_ANALYZE_DATA);
}
export function hasAdministerSchedulesPrivilege() {
    return hasPrivilege(PRIVILEGES.CAN_ADMINISTER_SCHEDULES);
}
export const canShareWithAll = () => {
    return hasPrivilege(PRIVILEGES.SHAREWITHALL);
};
export const canUserManageData = () => {
    return hasDataManagementPrivileges() || hasAdminPrivileges();
};
export function hasDownloadAnswerPrivileges() {
    return _hasDownloadAnswerPrivileges(getSessionInfo());
}
export function hasDownloadDetailedDataPrivileges() {
    return _hasDownloadDetailedDataPrivileges(getSessionInfo());
}
export function hasDownloadVisualPrivileges() {
    return _hasDownloadVisualPrivileges(getSessionInfo());
}
export function hasUserAdminPrivileges() {
    return _hasUserAdminPrivileges(getSessionInfo());
}
export const hasUserDataUploadPrivileges = () => {
    return hasPrivilege(PRIVILEGES.USERDATAUPLOADING);
};
export const getSuperUserGuid = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.SUPER_USER_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.SUPER_USER_GUID];
};
export const getSystemUserGuid = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.SYSTEM_USER_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.SYSTEM_USER_GUID];
};
export const getAdminGroupGuid = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.ADMIN_GROUP_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.ADMIN_GROUP_GUID];
};
export const getSystemGroupGuid = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.SYSTEM_GROUP_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.SYSTEM_GROUP_GUID];
};
export const getPDomDomain = () => {
    const pDomDomain = getConfigInfo(jsonConstants.PDOM_DOMAIN);
    return pDomDomain || 'pdom.thoughtspot.com';
};
export const getTSAdminUserGuid = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.TSADMIN_USER_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.TSADMIN_USER_GUID];
};
export const getHiddenObjectGuid = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.HIDDEN_OBJECT_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.HIDDEN_OBJECT_GUID];
};
export const getAllGroupGUID = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject || !sessionInfoObject[jsonConstants.ALL_GROUP_ID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.ALL_GROUP_ID];
};
export const getAllGroupDisplayName = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.ALL_GROUP_DISPLAY_NAME]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.ALL_GROUP_DISPLAY_NAME];
};
export const getAllGroupUserCount = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.ALL_GROUP_USER_COUNT]) {
        return -1;
    }
    return sessionInfoObject[jsonConstants.ALL_GROUP_USER_COUNT];
};
export const getSkipLiveboardIds = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.SKIP_LIVEBOARDS_ID]) {
        return [];
    }
    return sessionInfoObject[jsonConstants.SKIP_LIVEBOARDS_ID];
};
export const getSkipWorksheetIds = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.SKIP_WORKSHEETS_IDS]) {
        return [];
    }
    return sessionInfoObject[jsonConstants.SKIP_WORKSHEETS_IDS];
};
export const getUserPrivileges = () => {
    return _getUserPrivileges(getSessionInfo());
};
export const getUserPrivilegesBitMap = () => {
    return _getUserPrivilegesBitMap(getSessionInfo());
};
export function isEnliteEnabled(currentLocale) {
    return false;
}
export const isAnswerV2EnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.answerV2Experience || false;
};
export const isAnswerV2Preferred = () => {
    return getPreference(blinkConstants.preferenceKeys.ANSWER_V2);
};
export const isAnswerV2EnabledByDefault = () => {
    const isAnswerV2Enabled = getConfigInfo(jsonConstants.IS_ANSWER_V2_ON_BY_DEFAULT);
    return isAnswerV2Enabled ?? false;
};
export function isCurrentUserPublic() {
    const publicUserName = getConfigInfo('publicUserName');
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject
        ? publicUserName === sessionInfoObject.userName
        : false;
}
export const askSageRightPanelEnabled = () => {
    return !!flags?.getValue(BLINK_FLAGS.enableAskSageRightPanel.name);
};
export const isAnswerV2Enabled = () => {
    if (flags?.getValue(BLINK_FLAGS.isAnswerV2Enabled.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.isAnswerV2Enabled.name);
    }
    if (!isAnswerV2EnabledOnCluster()) {
        return false;
    }
    const userPreference = isAnswerV2Preferred();
    if (_.isBoolean(userPreference)) {
        return userPreference;
    }
    return isAnswerV2EnabledByDefault();
};
export const shouldDisplayAIControls = () => {
    return isAiAnswerGenerationEnabledForUser() || isConvAssistEnabled();
};
export const isAiAnswerGenerationEnabledForUser = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.aiAnswerGenerationEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.AI_ANSWER_GENERATION_ENABLED);
};
export const isNLInstructionsInSpotterEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.isNLInstructionsInSpotterEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.IS_NL_INSTRUCTIONS_IN_SPOTTER_ENABLED);
};
export const isSpotsEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.isSpotEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.IS_SPOT_ENABLED);
};
export const showSpotterPastConversations = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.showSpotterPastConversations.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.SHOW_SPOTTER_PAST_CONVERSATIONS);
};
export const isLLMGatewayV2Enabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.enableLLMGatewayV2.name);
    if (!_.isNil(blinkFlag)) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_LLM_GATEWAY_V2);
};
export const isBYOLLMEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.enableBYOLLM.name);
    if (!_.isNil(blinkFlag)) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_BYO_LLM);
};
export const resetPasswordV2Enabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.resetPasswordV2Enabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.isResetPasswordV2Enabled || false;
};
export const orgStatisticsCountV2Enabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.orgStatisticsCountV2Enabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.isOrgStatisticsCountV2Enabled || false;
};
export const isSageCoachMFEEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.enableSageCoachMFE.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_SAGE_COACH_MFE);
};
export const isNlsFeedbackLearnabilityUiEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.nlsFeedbackLearnabilityUiEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.NLS_FEEDBACK_LEARNABILITY_UI_ENABLED);
};
export const isSpotterExperienceEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.isSpotterExperienceEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.SPOTTER_EXPERIENCE);
};
export const getSpotterExperience = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.spotterExperience.name);
    if (typeof blinkFlag === 'string' && blinkFlag) {
        return blinkFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.spotterExperience || 'SPOTTER3';
};
export const isSpotter3ExperienceEnabled = () => {
    return getSpotterExperience() === 'SPOTTER3';
};
export const isStarterPromptsEnabled = () => {
    if (!isSpotter3ExperienceEnabled()) {
        return false;
    }
    const blinkFlag = flags?.getValue(BLINK_FLAGS.isStarterPromptsEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.IS_STARTER_PROMPTS_ENABLED);
};
export const isSpotterDataSourceDiscoveryEnabled = () => {
    if (!isSpotter3ExperienceEnabled()) {
        return false;
    }
    const blinkFlag = flags?.getValue(BLINK_FLAGS.enableSpotterDataSourceDiscovery.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_SPOTTER_DATA_SOURCE_DISCOVERY);
};
export const isSpotterMultiWorksheetEnabled = () => {
    if (!isSpotter3ExperienceEnabled()) {
        return false;
    }
    const blinkFlag = flags?.getValue(BLINK_FLAGS.enableMultiWorksheet.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_MULTI_WORKSHEET);
};
export const isFeedbackMigrationEnabled = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.isFeedbackMigrationEnabled.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_FEEDBACK_MIGRATION);
};
export const isHighchartsWarmupEnabled = () => {
    const flag = flags?.getValue(BLINK_FLAGS.enableHighchartsWarmup?.name);
    if (!_.isUndefined(flag))
        return flag;
    const configFlag = getConfigInfo(jsonConstants.ENABLE_HIGHCHARTS_WARMUP);
    return configFlag ?? false;
};
export const getTryEverywhereUrl = () => {
    return getConfigInfo(jsonConstants.TRY_EVERYWHERE_URL);
};
export const isNlsFeedbackCRUDUiEnabled = () => {
    return (!!flags?.getValue(BLINK_FLAGS.nlsFeedbackCRUDUiEnabled.name) ||
        !!getConfigInfo(jsonConstants.NLS_FEEDBACK_CRUD_UI_ENABLED));
};
export const isSageWarningMessagesEnabled = () => {
    return (!!flags?.getValue(BLINK_FLAGS.enableSageWarningMessages.name) ||
        !!getConfigInfo(jsonConstants.ENABLE_SAGE_WARNING_MESSAGES));
};
export const isCaptainEnabled = () => {
    return (!!flags?.getValue(BLINK_FLAGS.captainEnabled.name) ||
        !!getConfigInfo(jsonConstants.CAPTAIN_ENABLED));
};
export const isSearchBarCollapsedByDefault = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.collapseSearchBarInitially.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return false;
};
export const isCopyFormulaEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isCopyFormulaEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return false;
};
export const getNLInstructionsCharLimit = () => {
    const nlInstructionsCharLimit = getConfigInfo(jsonConstants.NL_INSTRUCTIONS_CHAR_LIMIT);
    return _.isNull(nlInstructionsCharLimit)
        ? 3000
        : Number(nlInstructionsCharLimit);
};
export const isDataPanelV2EnabledForUserByDefault = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.defaultToNewDataPanelV2 || false;
};
export const isDataPanelV2EnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.dataPanelV2Experience || false;
};
export const showDataPanelV2Switcher = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.showDataPanelV2Switcher.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.showDataPanelV2Switcher ?? false;
};
export const isSpotterAgentEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableSpotterAgentExperience || false;
};
export const isSpotterAgentEnabledByDefault = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.useSpotterAgentByDefault || false;
};
export const isDataPanelPreferred = () => {
    return getPreference(blinkConstants.preferenceKeys.DATA_PANEL_V2);
};
export const isSpotterAgentPreferred = () => {
    return getPreference(blinkConstants.preferenceKeys.SPOTTER_AGENT);
};
export const isAddToCoachingFromChatEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isAddToCoachingFromChatEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableAddToCoachingFromChat || false;
};
export const isMemoryEnabledInSpotter = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isMemoryEnabledInSpotter.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return !!getConfigInfo(jsonConstants.IS_MEMORY_ENABLED_IN_SPOTTER);
};
export const isUserPersonalMemoryDisabled = () => {
    return !!getExposedUserPreference(SESSION_USER_PREFERENCES.USER_SCOPED_PREFERENCES_MEMORY);
};
export const isUserPreferencesMemoryEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isUserPreferencesMemoryEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return !!getConfigInfo(jsonConstants.IS_USER_PREFERENCES_MEMORY_ENABLED);
};
export const isMemorySourcesEnabled = () => {
    return !!flags?.getValue(BLINK_FLAGS.isMemorySourcesEnabled.name);
};
export const isMCPAdminConnectorsEnabled = () => {
    if (!isSpotter3ExperienceEnabled()) {
        return false;
    }
    const urlFlag = flags?.getValue(BLINK_FLAGS.isMCPAdminConnectorsEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableMCPAdminConnectors || false;
};
export const isUpdatedSpotterChatPromptEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.updatedSpotterChatPrompt.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const isSpotter3Enabled = isSpotter3ExperienceEnabled();
    const backendConfig = getBackendConfig();
    const isUpdatedChatPromptEnabled = backendConfig?.updatedSpotterChatPrompt || false;
    return isSpotter3Enabled || isUpdatedChatPromptEnabled;
};
export const isDataPanelV2Enabled = () => {
    if (!isEmbeddedApp() && !showDataPanelV2Switcher()) {
        return true;
    }
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableDataPanelV2.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    if (!isDataPanelV2EnabledOnCluster()) {
        return false;
    }
    const userPreference = isDataPanelPreferred();
    if (_.isBoolean(userPreference)) {
        return userPreference;
    }
    return isDataPanelV2EnabledForUserByDefault();
};
export const isSpotterSearchEnabled = () => {
    return isAiAnswerGenerationEnabledForUser() && isSpotterExperienceEnabled();
};
export const isSpotterAgentExperienceEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableSpotterAgentExperience.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    if (!isSpotterAgentEnabledOnCluster()) {
        return false;
    }
    return true;
};
export const isAToZViewHiddenOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.isAtoZViewHiddenInDataPanelV2 ?? true;
};
export const isAtoZViewHiddenInDataPanelV2 = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.hideAtoZViewInDataPanelV2.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return isAToZViewHiddenOnCluster();
};
export const areNewViewsAndInfoIconEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableNewViewsAndInfoIconInDataPanelV2 ?? true;
};
export const areNewViewsAndInfoIconEnabledInDataPanelV2 = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableNewViewsAndInfoIconInDataPanelV2.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areNewViewsAndInfoIconEnabledOnCluster();
};
export const isCustomColumnGroupsOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableColumnGroupsForModel ?? false;
};
export const isCustomColumnGroupsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableCustomColumnGroups.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return isCustomColumnGroupsOnCluster();
};
export const getInitialCustomColumnGroupsAccordionState = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.dataPanelCustomGroupsAccordionInitialState.name);
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    return undefined;
};
export const showKnowledgeCardByDefault = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.showKnowledgeCardByDefault || false;
};
export const getUrlFlagToControlVirtualization = () => {
    const flag = flags?.getValue(BLINK_FLAGS.useVirtualizationInDataPanelV2.name);
    if (_.isBoolean(flag))
        return flags?.getValue(BLINK_FLAGS.useVirtualizationInDataPanelV2.name);
    return undefined;
};
export const areNewMutedAlertAndErrorDetailsModalEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableNewMutedAlertAndErrorDetailsModal ?? true;
};
export var HomepageVersion;
(function (HomepageVersion) {
    HomepageVersion["V4"] = "v4";
    HomepageVersion["V3"] = "v3";
    HomepageVersion["V2"] = "v2";
    HomepageVersion["V1"] = "v1";
})(HomepageVersion || (HomepageVersion = {}));
export var NavigationVersion;
(function (NavigationVersion) {
    NavigationVersion["V3"] = "v3";
    NavigationVersion["V2"] = "v2";
    NavigationVersion["V1"] = "v1";
})(NavigationVersion || (NavigationVersion = {}));
export var SerpVersion;
(function (SerpVersion) {
    SerpVersion["V2"] = "v2";
    SerpVersion["V1"] = "v1";
})(SerpVersion || (SerpVersion = {}));
export const isHomepageV4Enabled = () => {
    const homepageVersionUrlFlag = flags?.getValue(BLINK_FLAGS.homepageVersion.name);
    if (!_.isUndefined(homepageVersionUrlFlag)) {
        return homepageVersionUrlFlag === HomepageVersion.V4;
    }
    return !!getConfigInfo(jsonConstants.IS_HOMEPAGE_V4_ENABLED);
};
export const getSerpVersion = () => {
    const blinkFlag = flags?.getValue(BLINK_FLAGS.serpVersion.name);
    if (blinkFlag !== undefined) {
        return blinkFlag;
    }
    return getConfigInfo(jsonConstants.SERP_VERSION) || SerpVersion.V2;
};
export const isSerpV2Enabled = () => {
    return getSerpVersion() === SerpVersion.V2;
};
export const areNewMutedAlertAndErrorDetailsModalEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableNewMutedAlertAndErrorDetailsModal.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areNewMutedAlertAndErrorDetailsModalEnabledOnCluster();
};
export const areMandatoryColumnsEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableMandatoryColumns ?? false;
};
export const areMandatoryColumnsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableMandatoryColumns.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areMandatoryColumnsEnabledOnCluster();
};
export const areStrictDateColumnsEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableStrictDateColumns ?? false;
};
export const areStrictDateColumnsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableStrictDateColumns.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areStrictDateColumnsEnabledOnCluster();
};
export const areStrictDateColumnsEnabledByDefaultOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableStrictDateColumnsByDefault ?? false;
};
export const areStrictDateColumnsEnabledByDefault = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableStrictDateColumnsByDefault.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areStrictDateColumnsEnabledByDefaultOnCluster();
};
export const areRestrictedTimeBucketsEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableRestrictedTimeBuckets ?? false;
};
export const areRestrictedTimeBucketsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableRestrictedTimeBuckets.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areRestrictedTimeBucketsEnabledOnCluster();
};
export const areFutureDateFilterKeywordsDisabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.disableFutureDateFilterKeywords ?? false;
};
export const areFutureDateFilterKeywordsDisabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.disableFutureDateFilterKeywords.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return areFutureDateFilterKeywordsDisabledOnCluster();
};
export const shouldDisplayTableSqlQueryByDefault = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.DISPLAY_TABLE_SQL_QUERY_BY_DEFAULT]);
};
export const isParametersEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableParameters.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableParameters ?? false;
};
export const isParametersDynamicDateEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableParametersDynamicDate.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableDynamicDefaultValueForDateParameter ?? false;
};
export const isSageTokenContextMenuEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableSageTokenContextMenu.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableSageTokenContextMenu ?? false;
};
export const isAnswerLevelCohortsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableAnswerLevelCohorts.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableAnswerLevelCohorts ?? false;
};
export const isWriteBackEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableWriteBack ?? false;
};
export const isSecureSuggestionsEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableSecureSuggestions ?? false;
};
export const isAdvancedCohortGroupsBinsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableAdvancedCohortGroupsBins.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableAdvancedCohortGroupsBins ?? false;
};
export const isCallosumDebugInfoEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableCallosumDebugInfo.name);
    return !_.isNil(urlFlag) ? urlFlag : false;
};
export const isCohortsOnSystemWorksheetsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableCohortsOnSystemWorksheets.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableCohortsOnSystemWorksheets ?? false;
};
export const isSpreadsheetsEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableSpreadsheets ?? false;
};
export const isWritebackInSpreadsheetEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableWritebackInSpreadsheet ?? false;
};
export const isAgentDBEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableAgentDB ?? false;
};
export const isPinboardV2TabsEnabled = () => {
    const isPinboardV2TabsEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isPinboardV2TabsEnabled.name);
    if (isPinboardV2TabsEnabledUrlFlag !== undefined) {
        return isPinboardV2TabsEnabledUrlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.isPinboardV2TabsEnabled ?? false;
};
export const isFirstPinExperienceEnabled = () => {
    const clientFlagValue = flags?.getValue(BLINK_FLAGS.firstPinExperience.name);
    const sessionFlagValue = getConfigInfo(jsonConstants.FIRST_PIN_EXPERIENCE_ENABLED);
    return _.isUndefined(clientFlagValue)
        ? !!sessionFlagValue
        : !!clientFlagValue;
};
export const isCustomTileEnabled = () => {
    const isCustomTileEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isCustomTileEnabled.name);
    if (isCustomTileEnabledUrlFlag !== undefined) {
        return isCustomTileEnabledUrlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.isCustomTileEnabled ?? false;
};
export const isNoteTilesEnabled = () => {
    const isNoteTilesEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isNoteTilesEnabled.name);
    if (isNoteTilesEnabledUrlFlag !== undefined) {
        return isNoteTilesEnabledUrlFlag;
    }
    const isNoteTilesEnabledConfig = getConfigInfo(jsonConstants.NOTE_TILES);
    return isNoteTilesEnabledConfig ?? false;
};
export const isMandatoryFilterEnabled = () => {
    const isMandatoryFilterEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isMandatoryFilterEnabled.name);
    if (isMandatoryFilterEnabledUrlFlag !== undefined) {
        return isMandatoryFilterEnabledUrlFlag;
    }
    const isMandatoryFilterEnabledConfig = getConfigInfo(jsonConstants.MANDATORY_FILTER);
    return isMandatoryFilterEnabledConfig ?? false;
};
export const isRollingDateFilterHiddenOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.hideRollingDateFilterSection || false;
};
export const isRollingDateFilterHidden = () => {
    const isRollingDateFilterHiddenUrlFlag = flags?.getValue(BLINK_FLAGS.hideRollingDateFilterSection.name);
    if (_.isBoolean(isRollingDateFilterHiddenUrlFlag)) {
        return isRollingDateFilterHiddenUrlFlag;
    }
    return isRollingDateFilterHiddenOnCluster() ?? false;
};
export const isCompactLayoutEnabled = () => {
    const isCompactLayoutEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isCompactLayoutEnabled.name);
    if (isCompactLayoutEnabledUrlFlag !== undefined) {
        return isCompactLayoutEnabledUrlFlag;
    }
    const isCompactLayoutEnabledConfig = getConfigInfo(jsonConstants.COMPACT_LAYOUT);
    return isCompactLayoutEnabledConfig ?? false;
};
export const isMobileResponsiveLiveboardEnabled = () => {
    const isMobileResponsiveLiveboardEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isMobileResponsiveLiveboardEnabled.name);
    if (isMobileResponsiveLiveboardEnabledUrlFlag !== undefined) {
        return isMobileResponsiveLiveboardEnabledUrlFlag;
    }
    const isMobileResponsiveLiveboardEnabledConfig = getConfigInfo(jsonConstants.MOBILE_RESPONSIVENESS_FOR_LIVEBOARD);
    return isMobileResponsiveLiveboardEnabledConfig ?? false;
};
export const isCrossFiltersEnabled = () => {
    const isCrossFiltersEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isCrossFiltersEnabled.name);
    if (isCrossFiltersEnabledUrlFlag !== undefined) {
        return isCrossFiltersEnabledUrlFlag;
    }
    const isCrossFiltersEnabledConfig = getConfigInfo(jsonConstants.CROSS_FILTERS);
    return isCrossFiltersEnabledConfig ?? false;
};
export const isHiddenColWSAccessible = () => {
    const isHiddenColWSAccessibleUrlFlag = flags?.getValue(BLINK_FLAGS.isHiddenColWSAccessible.name);
    if (isHiddenColWSAccessibleUrlFlag !== undefined) {
        return isHiddenColWSAccessibleUrlFlag;
    }
    const isHiddenColWSAccessibleConfig = getConfigInfo(jsonConstants.HIDDEN_COLUMN_WS_ACCESS);
    return isHiddenColWSAccessibleConfig ?? false;
};
export const isTableWidthScalingEnabled = () => {
    const isTableWidthScalingEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isTableWidthScalingEnabled.name);
    if (isTableWidthScalingEnabledUrlFlag !== undefined) {
        return isTableWidthScalingEnabledUrlFlag;
    }
    const isTableWidthScalingConfig = getConfigInfo(jsonConstants.TABLE_WIDTH_SCALING);
    return isTableWidthScalingConfig ?? false;
};
export const disableTrustedAuthForDevEdition = () => {
    const sessionInfo = getSessionInfo();
    return (sessionInfo?.userProperties?.subscriptionType === 'DEVELOPER_EDITION');
};
export const isAiHighlightsSplashScreenEnabled = () => {
    const isAiHighlightsSplashScreenEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isAiHighlightsSplashScreenEnabled.name);
    return isAiHighlightsSplashScreenEnabledUrlFlag ?? true;
};
export const isCustomSortingEnabled = () => {
    const isCustomSortingEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isCustomSortingEnabled.name);
    if (isCustomSortingEnabledUrlFlag !== undefined) {
        return isCustomSortingEnabledUrlFlag;
    }
    const isCustomSortingEnabledConfig = getConfigInfo(jsonConstants.CUSTOM_SORTING);
    return isCustomSortingEnabledConfig ?? false;
};
let cachedMatchMedia = null;
let cachedReducedMotionMQL = null;
const getReducedMotionMQL = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return null;
    }
    if (cachedMatchMedia !== window.matchMedia) {
        cachedMatchMedia = window.matchMedia;
        cachedReducedMotionMQL = window.matchMedia('(prefers-reduced-motion: reduce)');
    }
    return cachedReducedMotionMQL;
};
export const isCustomLabelProcessingDisabled = () => {
    const mql = getReducedMotionMQL();
    if (!mql || !mql.matches) {
        return false;
    }
    const urlFlag = flags?.getValue(BLINK_FLAGS.isCustomLabelProcessingDisabled.name);
    if (urlFlag !== undefined) {
        return urlFlag;
    }
    const configValue = getConfigInfo(jsonConstants.IS_CUSTOM_LABEL_PROCESSING_DISABLED);
    return configValue ?? false;
};
export const isHighchartCustomisationEnabled = () => {
    const newChartSettingsEnabled = isChartSettingsV2Enabled();
    if (newChartSettingsEnabled) {
        return true;
    }
    const isHighchartCustomisationEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isHighchartCustomisationEnabled.name);
    if (isHighchartCustomisationEnabledUrlFlag !== undefined) {
        return isHighchartCustomisationEnabledUrlFlag;
    }
    const isHighchartCustomisationEnabledConfig = getConfigInfo(jsonConstants.HIGHCHART_CUSTOMISATION);
    return isHighchartCustomisationEnabledConfig ?? false;
};
export const isPieChartEnhancementsEnabled = () => {
    const isPieChartEnhancementsEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isPieChartEnhancementsEnabled.name);
    if (isPieChartEnhancementsEnabledUrlFlag !== undefined) {
        return isPieChartEnhancementsEnabledUrlFlag;
    }
    const isPieChartEnhancementsEnabledConfig = getConfigInfo(jsonConstants.PIE_CHART_ENHANCEMENTS);
    return isPieChartEnhancementsEnabledConfig ?? false;
};
export const isRenamingColumnsEnabled = () => {
    const isRenamingColumnsEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isRenamingColumnsEnabled.name);
    if (isRenamingColumnsEnabledUrlFlag !== undefined) {
        return isRenamingColumnsEnabledUrlFlag;
    }
    const isRenamingColumnsEnabledConfig = getConfigInfo(jsonConstants.RENAMING_COLUMNS_ENABLED);
    return isRenamingColumnsEnabledConfig ?? false;
};
export const isSpotIqObjectTablePageEnabled = () => {
    const isSpotIqObjectTablePageEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.spotIqObjectTablePageEnabled.name);
    if (isSpotIqObjectTablePageEnabledUrlFlag !== undefined) {
        return isSpotIqObjectTablePageEnabledUrlFlag;
    }
    return true;
};
export const isDatePickerEnabled = () => {
    const bachConfig = getConfigInfo(jsonConstants.BACH_CONFIG);
    const isSageAddFilterV2ApiEnabled = !!bachConfig && bachConfig[jsonConstants.USE_SAGE_ADD_FILTER_V2_API];
    if (!(isSageAddFilterV2ApiEnabled && isSageAddFilterV2ApiEnabled === true)) {
        return false;
    }
    const isDatePickerEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isDatePickerEnabled.name);
    if (isDatePickerEnabledUrlFlag !== undefined) {
        return isDatePickerEnabledUrlFlag;
    }
    const isDatePickerEnabledConfig = getConfigInfo(jsonConstants.DATE_PICKER);
    return isDatePickerEnabledConfig ?? false;
};
export const isDateFilterChipMatchingSearchToken = () => {
    const isDateFilterChipMatchingSearchTokenUrlFlag = flags?.getValue(BLINK_FLAGS.isDateFilterChipMatchingSearchToken.name);
    if (isDateFilterChipMatchingSearchTokenUrlFlag !== undefined) {
        return isDateFilterChipMatchingSearchTokenUrlFlag;
    }
    const isDateFilterChipMatchingSearchTokenConfig = getConfigInfo(jsonConstants.DATE_FILTER_CHIP_MATCHING);
    return isDateFilterChipMatchingSearchTokenConfig ?? false;
};
export const isThisPeriodInDateFiltersEnabled = () => {
    const isThisPeriodInDateFiltersEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isThisPeriodInDateFiltersEnabled.name);
    if (isThisPeriodInDateFiltersEnabledUrlFlag !== undefined) {
        return isThisPeriodInDateFiltersEnabledUrlFlag;
    }
    const isThisPeriodInDateFiltersEnabledConfig = getConfigInfo(jsonConstants.THIS_PERIOD_IN_DATE_FILTERS);
    return isThisPeriodInDateFiltersEnabledConfig ?? false;
};
export const isScopedLiveboardFilteringEnabled = () => {
    const isScopedLiveboardFilteringEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isScopedLiveboardFilteringEnabled.name);
    if (isScopedLiveboardFilteringEnabledUrlFlag !== undefined) {
        return isScopedLiveboardFilteringEnabledUrlFlag;
    }
    const isScopedLiveboardFilteringEnabledConfig = getConfigInfo(jsonConstants.SCOPED_LIVEBOARD_FILTERING);
    return isScopedLiveboardFilteringEnabledConfig ?? false;
};
export const isColumnDeletionWithDependentsEnabled = () => {
    const isColumnDeletionWithDependentsEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.enableColumnDeletionWithDependents.name);
    if (isColumnDeletionWithDependentsEnabledUrlFlag !== undefined) {
        return isColumnDeletionWithDependentsEnabledUrlFlag;
    }
    const isColumnDeletionWithDependentsEnabledConfig = getConfigInfo(jsonConstants.ENABLE_COLUMN_DELETION_WITH_DEPENDENTS);
    return isColumnDeletionWithDependentsEnabledConfig ?? false;
};
export const hidePreviewForRollingFiltersWithCC = () => {
    const hidePreviewForRollingFiltersWithCCFlag = flags?.getValue(BLINK_FLAGS.hidePreviewForRollingFiltersWithCC.name);
    if (hidePreviewForRollingFiltersWithCCFlag !== undefined) {
        return hidePreviewForRollingFiltersWithCCFlag;
    }
    const hidePreviewForRollingFiltersWithCCConfig = getConfigInfo(jsonConstants.HIDE_PREVIEW_FOR_ROLLING_FILTERS_WITH_CC);
    return !!hidePreviewForRollingFiltersWithCCConfig;
};
export const isFilterSVSEnabled = () => {
    const isFilterSVSEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isFilterSVSEnabled.name);
    if (isFilterSVSEnabledUrlFlag !== undefined) {
        return isFilterSVSEnabledUrlFlag;
    }
    const isFilterSVSEnabledConfig = getConfigInfo(jsonConstants.FILTER_SINGLE_VALUE_SELECTION);
    return isFilterSVSEnabledConfig ?? false;
};
export const isParameterChipReorderingEnabled = () => {
    const isParameterChipReorderingEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isParameterChipReorderingEnabled.name);
    if (isParameterChipReorderingEnabledUrlFlag !== undefined) {
        return isParameterChipReorderingEnabledUrlFlag;
    }
    const isParameterChipReorderingEnabledConfig = getConfigInfo(jsonConstants.PARAMETER_CHIP_REORDERING_ENABLED);
    return isParameterChipReorderingEnabledConfig ?? false;
};
export const isNewScheduleModalEnabled = () => {
    const isNewScheduleModalEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isNewScheduleModalEnabled.name);
    if (isNewScheduleModalEnabledUrlFlag !== undefined) {
        return isNewScheduleModalEnabledUrlFlag;
    }
    const isNewScheduleModalEnabledConfig = getConfigInfo(jsonConstants.NEW_SCHEDULE_MODAL_ENABLED);
    return isNewScheduleModalEnabledConfig ?? false;
};
export const isReorderedEllipsisMenuEnabled = () => {
    const isReorderedEllipsisMenuEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isReorderedEllipsisMenuEnabled.name);
    if (isReorderedEllipsisMenuEnabledUrlFlag !== undefined) {
        return isReorderedEllipsisMenuEnabledUrlFlag;
    }
    const isReorderedEllipsisMenuEnabledConfig = getConfigInfo(jsonConstants.REORDERED_ELLIPSIS_MENU_ENABLED);
    return isReorderedEllipsisMenuEnabledConfig ?? false;
};
export const isAddAnswerInLiveboardEnabled = () => {
    const isAddAnswerInLiveboardEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isAddAnswerInLiveboardEnabled.name);
    if (isAddAnswerInLiveboardEnabledUrlFlag !== undefined) {
        return isAddAnswerInLiveboardEnabledUrlFlag;
    }
    const isAddAnswerInLiveboardEnabledConfig = getConfigInfo(jsonConstants.ADD_ANSWER_IN_LIVEBOARD_ENABLED);
    return isAddAnswerInLiveboardEnabledConfig ?? false;
};
export const isFilterRenamingEnabled = () => {
    const isFilterRenamingEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isFilterRenamingEnabled.name);
    if (isFilterRenamingEnabledUrlFlag !== undefined) {
        return isFilterRenamingEnabledUrlFlag;
    }
    const isFilterRenamingEnabledConfig = getConfigInfo(jsonConstants.FILTER_RENAMING);
    return isFilterRenamingEnabledConfig ?? false;
};
export const isPersonalisedViewsEnabled = () => {
    const isPersonalisedViewsEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isPersonalisedViewsEnabled.name);
    if (isPersonalisedViewsEnabledUrlFlag !== undefined) {
        return isPersonalisedViewsEnabledUrlFlag;
    }
    const isPersonalisedViewsEnabledConfig = getConfigInfo(jsonConstants.PERSONALISED_VIEWS);
    return isPersonalisedViewsEnabledConfig ?? false;
};
export const isContextMenuEnabledOnLeftClick = () => {
    const isContextMenuEnabledOnLeftClickUrlFlag = flags?.getValue(BLINK_FLAGS.isContextMenuEnabledOnLeftClick.name);
    if (isContextMenuEnabledOnLeftClickUrlFlag !== undefined) {
        return isContextMenuEnabledOnLeftClickUrlFlag;
    }
    const isContextMenuEnabledOnLeftClickConfig = getConfigInfo(jsonConstants.CONTEXT_MENU_ON_LEFT_CLICK);
    return isContextMenuEnabledOnLeftClickConfig ?? false;
};
export const isLiveboardHeaderSticky = () => {
    const isLiveboardHeaderStickyUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardHeaderSticky.name);
    if (isLiveboardHeaderStickyUrlFlag !== undefined) {
        return isLiveboardHeaderStickyUrlFlag;
    }
    const isLiveboardHeaderStickyConfig = getConfigInfo(jsonConstants.LIVEBOARD_STICKY_HEADER);
    return isLiveboardHeaderStickyConfig ?? false;
};
export const isLiveboardHeaderV2Enabled = () => {
    const isLiveboardHeaderV2EnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardHeaderV2Enabled.name);
    if (isLiveboardHeaderV2EnabledUrlFlag !== undefined) {
        return isLiveboardHeaderV2EnabledUrlFlag;
    }
    const isLiveboardHeaderV2EnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_HEADER_V2);
    return isLiveboardHeaderV2EnabledConfig ?? false;
};
export const isLiveboardStylingEnabled = () => {
    const isLiveboardStylingEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardStylingEnabled.name);
    const isLiveboardMasterpiecesEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardMasterpiecesEnabled.name);
    if (isLiveboardStylingEnabledUrlFlag !== undefined ||
        isLiveboardMasterpiecesEnabledUrlFlag !== undefined) {
        return !!(isLiveboardStylingEnabledUrlFlag ||
            isLiveboardMasterpiecesEnabledUrlFlag);
    }
    const isLiveboardStylingEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_STYLING);
    const isLiveboardMasterpiecesEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_MASTERPIECES);
    return (isLiveboardStylingEnabledConfig ||
        isLiveboardMasterpiecesEnabledConfig ||
        false);
};
export const isEveryNMinutesFrequencyDisabled = () => {
    const isEveryNMinutesFrequencyDisabledFlag = flags?.getValue(BLINK_FLAGS.isEveryNMinutesFrequencyDisabled.name);
    if (isEveryNMinutesFrequencyDisabledFlag !== undefined) {
        return isEveryNMinutesFrequencyDisabledFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    return (srConfig?.[jsonConstants.IS_EVERY_N_MINUTES_FREQUENCY_DISABLED] ?? false);
};
export const isLiveboardSchedulesUtilityDisabled = () => {
    const isLiveboardSchedulesUtilityDisabledFlag = flags?.getValue(BLINK_FLAGS.isLiveboardSchedulesUtilityDisabled.name);
    if (isLiveboardSchedulesUtilityDisabledFlag !== undefined) {
        return isLiveboardSchedulesUtilityDisabledFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    return (srConfig?.[jsonConstants.IS_LIVEBOARD_SCHEDULES_UTILITY_DISABLED] ??
        false);
};
export const isInsightTileEnabled = () => {
    const isInsightTileEnabledFlag = flags?.getValue(BLINK_FLAGS.isInsightTileEnabled.name);
    if (isInsightTileEnabledFlag !== undefined) {
        return isInsightTileEnabledFlag;
    }
    const isInsightTileEnabledConfig = getConfigInfo(jsonConstants.INSIGHT_TILE);
    return isInsightTileEnabledConfig ?? false;
};
export const isInsightTileBackgroundEnabled = () => {
    if (!isInsightTileEnabled()) {
        return false;
    }
    const isInsightTileBackgroundEnabledFlag = flags?.getValue(BLINK_FLAGS.isInsightTileBackgroundEnabled.name);
    if (isInsightTileBackgroundEnabledFlag !== undefined) {
        return isInsightTileBackgroundEnabledFlag;
    }
    const isInsightTileBackgroundEnabledConfig = getConfigInfo(jsonConstants.INSIGHT_TILE_BACKGROUND);
    return isInsightTileBackgroundEnabledConfig ?? false;
};
export const isSpotterXEnabled = () => {
    const isSpotterXEnabledFlag = flags?.getValue(BLINK_FLAGS.isSpotterXEnabled.name);
    if (isSpotterXEnabledFlag !== undefined) {
        return isSpotterXEnabledFlag;
    }
    const isSpotterXEnabledConfig = getConfigInfo(jsonConstants.IS_SPOTTER_X_ENABLED);
    return isSpotterXEnabledConfig ?? false;
};
export const isAnswerEditInLiveboardEditModeEnabled = () => {
    const isAnswerEditInLiveboardEditModeEnabledFlag = flags?.getValue(BLINK_FLAGS.isAnswerEditInLiveboardEditModeEnabled.name);
    if (isAnswerEditInLiveboardEditModeEnabledFlag !== undefined) {
        return isAnswerEditInLiveboardEditModeEnabledFlag;
    }
    const isAnswerEditInLiveboardEditModeEnabledConfig = getConfigInfo(jsonConstants.ANSWER_EDIT_IN_LIVEBOARD_EDIT_MODE);
    return isAnswerEditInLiveboardEditModeEnabledConfig ?? false;
};
export const isLiveboardPermissionV2Enabled = () => {
    const isLiveboardPermissionV2EnabledFlag = flags?.getValue(BLINK_FLAGS.isLiveboardPermissionV2Enabled.name);
    if (isLiveboardPermissionV2EnabledFlag !== undefined) {
        return isLiveboardPermissionV2EnabledFlag;
    }
    const isLiveboardPermissionV2EnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_PERMISSION_V2_ENABLED);
    return isLiveboardPermissionV2EnabledConfig ?? false;
};
export const isEnhancedLiveboardInteractivityWithCSREnabled = () => {
    const isEnhancedLiveboardInteractivityWithCSREnabledFlag = flags?.getValue(BLINK_FLAGS.isEnhancedLiveboardInteractivityWithCSREnabled.name);
    if (isEnhancedLiveboardInteractivityWithCSREnabledFlag !== undefined) {
        return isEnhancedLiveboardInteractivityWithCSREnabledFlag;
    }
    const isEnhancedLiveboardInteractivityWithCSREnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_INTERACTIVITY_WITH_CSR_ENABLED);
    return isEnhancedLiveboardInteractivityWithCSREnabledConfig ?? false;
};
export const enableKaiosAgent = () => {
    const enableKaiosAgentFlag = flags?.getValue(BLINK_FLAGS.enableKaiosAgent.name);
    if (enableKaiosAgentFlag !== undefined) {
        return enableKaiosAgentFlag;
    }
    const enableKaiosAgentConfig = getConfigInfo(jsonConstants.KAIOS_AGENT_ENABLED);
    return enableKaiosAgentConfig ?? false;
};
export const getCloudPlatform = () => {
    return getConfigInfo(jsonConstants.CLOUD_PLATFORM) || 'AWS';
};
export const isGCPCluster = () => {
    return getCloudPlatform() === 'GCP';
};
export const isSpotterVizInViewModeEnabled = () => {
    const isSpotterVizInViewModeEnabledFlag = flags?.getValue(BLINK_FLAGS.isSpotterVizInViewModeEnabled.name);
    if (isSpotterVizInViewModeEnabledFlag !== undefined) {
        return isSpotterVizInViewModeEnabledFlag;
    }
    const isSpotterVizInViewModeEnabledConfig = getConfigInfo(jsonConstants.SPOTTER_VIZ_VIEW_MODE_ENABLED);
    return isSpotterVizInViewModeEnabledConfig ?? false;
};
export const enableSpotterVizZeroState = () => {
    if (!enableKaiosAgent()) {
        return false;
    }
    const enableSpotterVizZeroStateFlag = flags?.getValue(BLINK_FLAGS.enableSpotterVizZeroState.name);
    if (enableSpotterVizZeroStateFlag !== undefined) {
        return enableSpotterVizZeroStateFlag;
    }
    const enableSpotterVizZeroStateConfig = getConfigInfo(jsonConstants.SPOTTER_VIZ_ZERO_STATE_ENABLED);
    return enableSpotterVizZeroStateConfig ?? false;
};
export const isWebhooksV2Enabled = () => {
    const isWebhooksV2EnabledFlag = flags?.getValue(BLINK_FLAGS.webhooksV2Enabled.name);
    if (isWebhooksV2EnabledFlag !== undefined) {
        return isWebhooksV2EnabledFlag;
    }
    const isWebhooksV2EnabledConfig = getConfigInfo(jsonConstants.WEBHOOKS_V2_ENABLED);
    return isWebhooksV2EnabledConfig ?? false;
};
export const isTseV2StyleApisEnabled = () => {
    const isTseV2StyleApisEnabledFlag = flags?.getValue(BLINK_FLAGS.tseV2StyleApisEnabled.name);
    if (isTseV2StyleApisEnabledFlag !== undefined) {
        return isTseV2StyleApisEnabledFlag;
    }
    const isTseV2StyleApisEnabledConfig = getConfigInfo(jsonConstants.TSE_V2_STYLE_APIS_ENABLED);
    return isTseV2StyleApisEnabledConfig ?? false;
};
export const comsWebhookHistoryRetentionPeriodInDays = () => {
    const retentionPeriodInDays = getConfigInfo(jsonConstants.COMS_WEBHOOK_HISTORY_RETENTION_PERIOD_IN_DAYS);
    return retentionPeriodInDays ?? 3;
};
export const isThemeBuilderAiMode = () => {
    const isThemeBuilderAiModeFlag = flags?.getValue(BLINK_FLAGS.themeBuilderAiMode.name);
    if (isThemeBuilderAiModeFlag !== undefined) {
        return isThemeBuilderAiModeFlag;
    }
    const isThemeBuilderAiModeConfig = getConfigInfo(jsonConstants.THEME_BUILDER_AI_MODE);
    return isThemeBuilderAiModeConfig ?? true;
};
export const isSpotterCodeEnabled = () => {
    const isSpotterCodeEnabledFlag = flags?.getValue(BLINK_FLAGS.spotterCodeEnabled.name);
    if (isSpotterCodeEnabledFlag !== undefined) {
        return isSpotterCodeEnabledFlag;
    }
    const isSpotterCodeEnabledConfig = getConfigInfo(jsonConstants.SPOTTER_CODE_ENABLED);
    return isSpotterCodeEnabledConfig ?? false;
};
export const isLiveboardPNGExportV2Enabled = () => {
    const isLiveboardPNGExportV2EnabledFlag = flags?.getValue(BLINK_FLAGS.isPNGInScheduledEmailsEnabled.name);
    if (isLiveboardPNGExportV2EnabledFlag !== undefined) {
        return isLiveboardPNGExportV2EnabledFlag;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    if (exportConfig &&
        exportConfig[jsonConstants.IS_LIVEBOARD_PNG_EXPORT_V2_ENABLED]) {
        return exportConfig[jsonConstants.IS_LIVEBOARD_PNG_EXPORT_V2_ENABLED];
    }
    return false;
};
export const isWYSIWYGLiveboardPDFEnabled = () => {
    const isWYSIWYGLiveboardPDFEnabledFlag = flags?.getValue(BLINK_FLAGS.isWYSIWYGLiveboardPDFEnabled.name);
    if (isWYSIWYGLiveboardPDFEnabledFlag !== undefined) {
        return isWYSIWYGLiveboardPDFEnabledFlag;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    if (exportConfig &&
        exportConfig[jsonConstants.IS_WYSIWYG_LIVEBOARD_PDF_ENABLED]) {
        return exportConfig[jsonConstants.IS_WYSIWYG_LIVEBOARD_PDF_ENABLED];
    }
    return false;
};
export const isLiveboardPNGExportV2UrlFlagEnabled = () => {
    const isLiveboardPNGExportV2EnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardPNGExportV2Enabled.name);
    if (isLiveboardPNGExportV2EnabledUrlFlag !== undefined) {
        return isLiveboardPNGExportV2EnabledUrlFlag;
    }
    return false;
};
export const isPNGInScheduledEmailsEnabled = () => {
    const isPNGInScheduledEmailsEnabledFlag = flags?.getValue(BLINK_FLAGS.isPNGInScheduledEmailsEnabled.name);
    if (isPNGInScheduledEmailsEnabledFlag !== undefined) {
        return isPNGInScheduledEmailsEnabledFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    const isPNGInScheduledEmailsEnabledConfig = srConfig[jsonConstants.IS_PNG_IN_SCHEDULED_EMAILS_ENABLED];
    return isPNGInScheduledEmailsEnabledConfig ?? false;
};
export const getEmbedPngScale = () => {
    const embedPngScaleFlag = flags?.getValue(BLINK_FLAGS.embedPngScale.name);
    if (embedPngScaleFlag !== undefined) {
        return embedPngScaleFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    return srConfig?.[jsonConstants.EMBED_PNG_SCALE];
};
export const isAnswerExportV2Enabled = () => {
    const isAnswerExportV2EnabledFlag = flags?.getValue(BLINK_FLAGS.isAnswerExportV2Enabled.name);
    if (isAnswerExportV2EnabledFlag !== undefined) {
        return isAnswerExportV2EnabledFlag;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    return exportConfig?.[jsonConstants.IS_ANSWER_EXPORT_V2_ENABLED] ?? false;
};
export const isSparkflowUsecaseBasedOnboardingEnabled = () => {
    const isSparkflowUsecaseBasedOnboardingEnabledFlag = flags?.getValue(BLINK_FLAGS.enableSparkflowUsecaseBasedOnboarding.name);
    if (isSparkflowUsecaseBasedOnboardingEnabledFlag !== undefined) {
        return isSparkflowUsecaseBasedOnboardingEnabledFlag;
    }
    const sparkflowConfig = getConfigInfo(jsonConstants.SPARK_FLOW_CONFIG);
    return (sparkflowConfig?.[jsonConstants.ENABLE_USECASE_BASED_ONBOARDING] ??
        false);
};
export const isOnboardingQuestionChipsEnabled = () => {
    const flag = flags?.getValue(BLINK_FLAGS.enableOnboardingQuestionChips.name);
    if (flag !== undefined) {
        return flag;
    }
    const sparkflowConfig = getConfigInfo(jsonConstants.SPARK_FLOW_CONFIG);
    return (sparkflowConfig?.[jsonConstants.ENABLE_ONBOARDING_QUESTION_CHIPS] ??
        false);
};
export const isSparkflowV2Enabled = () => {
    return true;
};
export const showSparkflowTrialBannerInLiveboardEditPage = () => {
    const flagValue = flags?.getValue(BLINK_FLAGS.showSparkflowTrialBannerInLiveboardEditPage.name);
    if (flagValue !== undefined) {
        return flagValue;
    }
    const sparkflowConfig = getConfigInfo(jsonConstants.SPARK_FLOW_CONFIG);
    return (sparkflowConfig?.[jsonConstants.SHOW_SPARKFLOW_TRIAL_BANNER_IN_LIVEBOARD_EDIT_PAGE] ?? false);
};
export const enableFullUserJourney = () => {
    const flagValue = flags?.getValue(BLINK_FLAGS.enableFullUserJourney.name);
    if (flagValue !== undefined) {
        return flagValue;
    }
    const trialVersionConfig = getConfigInfo(jsonConstants.TRIAL_VERSION_CONFIG_KEY);
    return (trialVersionConfig?.[jsonConstants.ENABLE_FULL_USER_JOURNEY] ?? false);
};
export const isSendNowLiveboardSchedulingEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isSendNowLiveboardSchedulingEnabled.name);
    if (urlFlagValue !== undefined) {
        return urlFlagValue;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    return (srConfig?.[jsonConstants.IS_SEND_NOW_LIVEBOARD_SCHEDULING_ENABLED] ??
        false);
};
export const isGranularXLSXCSVSchedulesEnabled = () => {
    const isGranularXLSXCSVSchedulesEnabledFlag = flags?.getValue(BLINK_FLAGS.isGranularXLSXCSVSchedulesEnabled.name);
    if (isGranularXLSXCSVSchedulesEnabledFlag !== undefined) {
        return isGranularXLSXCSVSchedulesEnabledFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    if (srConfig &&
        srConfig[jsonConstants.IS_GRANULAR_XLSX_CSV_SCHEDULES_ENABLED]) {
        return srConfig[jsonConstants.IS_GRANULAR_XLSX_CSV_SCHEDULES_ENABLED];
    }
    return false;
};
export const isCombinedXLSXReportEnabled = () => {
    const isCombinedXLSXReportEnabledFlag = flags?.getValue(BLINK_FLAGS.isCombinedXLSXReportEnabled.name);
    if (isCombinedXLSXReportEnabledFlag !== undefined) {
        return isCombinedXLSXReportEnabledFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    if (srConfig && srConfig[jsonConstants.IS_COMBINED_XLSX_REPORT_ENABLED]) {
        return srConfig[jsonConstants.IS_COMBINED_XLSX_REPORT_ENABLED];
    }
    return false;
};
export const isComsForLiveboardSchedulesEnabled = () => {
    const isComsForLiveboardSchedulesEnabledFlag = flags?.getValue(BLINK_FLAGS.isComsForLiveboardSchedulesEnabled.name);
    if (isComsForLiveboardSchedulesEnabledFlag !== undefined) {
        return isComsForLiveboardSchedulesEnabledFlag;
    }
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    if (srConfig &&
        srConfig[jsonConstants.IS_COMS_FOR_LIVEBOARD_SCHEDULES_ENABLED]) {
        return srConfig[jsonConstants.IS_COMS_FOR_LIVEBOARD_SCHEDULES_ENABLED];
    }
    return false;
};
export const isLinkParametersEnabled = () => {
    const isLinkParametersEnabledFlag = flags?.getValue(BLINK_FLAGS.isLinkParametersEnabled.name);
    if (isLinkParametersEnabledFlag !== undefined) {
        return isLinkParametersEnabledFlag;
    }
    const isLinkParametersEnabledConfig = getConfigInfo(jsonConstants.IS_LINK_PARAMETERS_ENABLED);
    return isLinkParametersEnabledConfig ?? false;
};
export const isEmbedInNoteTilesEnabled = () => {
    const isEmbedInNoteTilesEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isEmbedInNoteTilesEnabled.name);
    if (isEmbedInNoteTilesEnabledUrlFlag !== undefined) {
        return isEmbedInNoteTilesEnabledUrlFlag;
    }
    const isEmbedInNoteTilesEnabledConfig = getConfigInfo(jsonConstants.IS_EMBED_IN_NOTE_TILES);
    return isEmbedInNoteTilesEnabledConfig ?? false;
};
export const hideIrrelevantFiltersAtTabLevel = () => {
    const hideIrrelevantFiltersAtTabLevelUrlFlag = flags?.getValue(BLINK_FLAGS.hideIrrelevantFiltersAtTabLevel.name);
    if (hideIrrelevantFiltersAtTabLevelUrlFlag !== undefined) {
        return hideIrrelevantFiltersAtTabLevelUrlFlag;
    }
    const hideIrrelevantFiltersAtTabLevelConfig = getConfigInfo(jsonConstants.HIDE_IRRELEVANT_FILTERS_AT_TAB_LEVEL);
    return hideIrrelevantFiltersAtTabLevelConfig ?? false;
};
export const isPinboardV2EnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.pinboardV2Experience ?? false;
};
export const isPinboardV2Preferred = () => {
    return getPreference(blinkConstants.preferenceKeys.PINBOARD_V2);
};
export const isPinboardV2EnabledByDefault = () => {
    const isPinboardV2Enabled = getBackendConfig()?.isPinboardV2OnByDefault;
    return isPinboardV2Enabled ?? false;
};
export const isPinboardV2Enabled = () => {
    const isPinboardV2EnabledInUrl = flags?.getValue(BLINK_FLAGS.isPinboardV2Enabled.name);
    if (isPinboardV2EnabledInUrl !== undefined) {
        return isPinboardV2EnabledInUrl;
    }
    if (!isPinboardV2EnabledOnCluster()) {
        return false;
    }
    const userPreference = isPinboardV2Preferred();
    if (_.isBoolean(userPreference)) {
        return userPreference;
    }
    return isPinboardV2EnabledByDefault();
};
export const isHomeV2Enabled = () => {
    if (flags?.getValue(BLINK_FLAGS.homeV2Experience.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.homeV2Experience.name);
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableHomepageV2 || false;
};
export const isHomepageAnnouncementEnabled = () => {
    const enableHomepageAnnouncementUrlFlag = flags?.getValue(BLINK_FLAGS.enableHomepageAnnouncement.name);
    if (enableHomepageAnnouncementUrlFlag !== undefined) {
        return enableHomepageAnnouncementUrlFlag;
    }
    const enableHomepageAnnouncement = getConfigInfo(jsonConstants.ENABLE_HOMEPAGE_ANNOUNCEMENT);
    return enableHomepageAnnouncement ?? false;
};
export const isCommandPaletteEnabled = () => {
    const isCommandPaletteEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isCommandPaletteEnabled.name);
    if (isCommandPaletteEnabledUrlFlag !== undefined) {
        return isCommandPaletteEnabledUrlFlag;
    }
    const isCommandPaletteEnabled = getConfigInfo(jsonConstants.ENABLE_COMMAND_PALETTE);
    return isCommandPaletteEnabled ?? false;
};
export const isCortexEnabledOnCluster = () => {
    const a3Config = getConfigInfo(jsonConstants.A3_CONFIG);
    return a3Config?.enableCortexEngine || false;
};
export const isKPIAnomaliesPreferred = () => {
    return getPreference(blinkConstants.preferenceKeys.KPI_ANOMALIES);
};
export const isKPIAnomaliesEnabledOnCluster = () => {
    const a3Config = getConfigInfo(jsonConstants.A3_CONFIG);
    return isCortexEnabledOnCluster() && (a3Config?.enableCortex || false);
};
export const isKPIAnomaliesEnabled = () => {
    if (!isKPIAnomaliesEnabledOnCluster()) {
        return false;
    }
    const userPreference = isKPIAnomaliesPreferred();
    if (_.isBoolean(userPreference)) {
        return userPreference;
    }
    const a3Config = getConfigInfo(jsonConstants.A3_CONFIG);
    return a3Config?.isKPIAnomaliesOnByDefault || true;
};
export const isFullKPIValueOnTooltipEnabled = () => {
    const isFullKPIValueOnTooltipEnabledFlag = flags?.getValue(BLINK_FLAGS.showFullKPIValueOnTooltip.name);
    if (isFullKPIValueOnTooltipEnabledFlag !== undefined) {
        return isFullKPIValueOnTooltipEnabledFlag;
    }
    const isFullKPIValueOnTooltipEnabledConfig = getConfigInfo(jsonConstants.SHOW_FULL_KPI_VALUE_ON_TOOLTIP);
    return isFullKPIValueOnTooltipEnabledConfig ?? false;
};
export const isKPIChartEnhancementsEnabled = () => {
    const isKPIChartEnhancementsEnabledFlag = flags?.getValue(BLINK_FLAGS.enableKPIChartEnhancements.name);
    if (isKPIChartEnhancementsEnabledFlag !== undefined) {
        return isKPIChartEnhancementsEnabledFlag;
    }
    const isKPIChartEnhancementsEnabledConfig = getConfigInfo(jsonConstants.ENABLE_KPI_CHART_ENHANCEMENTS);
    return isKPIChartEnhancementsEnabledConfig ?? false;
};
export const isActionsUserGroupsEnabled = () => {
    if (flags?.getValue(BLINK_FLAGS.actionsUserGroupsEnabled.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.actionsUserGroupsEnabled.name);
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.atlasActionObjectUGFeatureEnabled || false;
};
export const isLegacyActionEnabled = () => {
    if (flags?.getValue(BLINK_FLAGS.useLegacyEmbeddedPinboardActionConfig.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.useLegacyEmbeddedPinboardActionConfig.name);
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.useLegacyEmbeddedPinboardActionConfig || false;
};
export const isReactshellEnabled = () => {
    const reactShellUrlFlag = flags?.getValue(BLINK_FLAGS.reactShellExperience.name);
    if (!_.isUndefined(reactShellUrlFlag)) {
        return reactShellUrlFlag;
    }
    return getConfigInfo(jsonConstants.REACT_SHELL_EXPERIENCE);
};
export const getShowNLS = () => {
    return getPreference(blinkConstants.preferenceKeys.SHOW_NLS);
};
export const setShowNLS = (showNLS) => {
    setPreference(blinkConstants.preferenceKeys.SHOW_NLS, showNLS, !isReactshellEnabled());
};
const globalBannerObserver = new Subject();
export const globalBannerObserverSubscriber = (cb) => {
    const subscription = globalBannerObserver.subscribe(cb);
    return () => subscription.unsubscribe();
};
export const unsubscribeGlobalBannerObserver = () => {
    globalBannerObserver.unsubscribe();
};
export const triggerGlobalBannerObserver = () => {
    globalBannerObserver.next();
};
export const setHomePageEurekaPreferred = (eurekaPreferred) => {
    setPreference(blinkConstants.preferenceKeys.HOMEPAGE_EUREKA, eurekaPreferred, !isReactshellEnabled());
};
export const setIsLiveboardTourCompleted = (visited) => {
    setPreference(blinkConstants.preferenceKeys.LIVEBOARD_GUIDE_COMPLETED, visited, !isReactshellEnabled());
};
export const isLiveboardTourCompleted = () => {
    return !!getPreference(blinkConstants.preferenceKeys.LIVEBOARD_GUIDE_COMPLETED);
};
export const setUserIdToActivationTokenForNebula = (userIdToToken) => {
    setPreference(blinkConstants.preferenceValues.NEBULA_USERID_TO_ACTIVATION_TOKEN, userIdToToken, !isReactshellEnabled());
};
export const getUserIdToActivationTokenForNebula = () => {
    return getPreference(blinkConstants.preferenceValues.NEBULA_USERID_TO_ACTIVATION_TOKEN);
};
export const isHomePageEurekaPreferred = () => {
    const preference = getPreference(blinkConstants.preferenceKeys.HOMEPAGE_EUREKA);
    return isHomeV2Enabled() && (_.isNil(preference) || preference === true);
};
export const isSnapshotCaptureEnabled = () => {
    return (flags?.getValue(BLINK_FLAGS.enableSnapshotCapture.name) ||
        getConfigInfo(jsonConstants.ENABLE_SNAPSHOT_CAPTURE));
};
export const isSnapshotCaptureEnabledForAnswerV2 = () => {
    return (flags?.getValue(BLINK_FLAGS.enableSnapshotCaptureForAnswerV2.name) ||
        getConfigInfo(jsonConstants.ENABLE_SNAPSHOT_CAPTURE_ANSWER_V2));
};
export const isEurekaAdvancedTypeaheadEnabled = () => {
    return (flags?.getValue(BLINK_FLAGS.enableEurekaAdvancedTypeahead.name) ||
        getConfigInfo(jsonConstants.ENABLE_EUREKA_ADVANCED_TYPEAHEAD));
};
export const isEurekaSearchBarInNavEnabled = () => {
    return (flags?.getValue(BLINK_FLAGS.eurekaSearchBarInNav.name) ||
        getConfigInfo(jsonConstants.EUREKA_SEARCH_BAR_IN_NAV));
};
export const isEurekaDebugInfoEnabled = () => {
    return (flags?.getValue(BLINK_FLAGS.enableEurekaDebugInfo.name) ||
        getConfigInfo(jsonConstants.ENABLE_EUREKA_DEBUG_INFO));
};
export const isEurekaTypeaheadRecentObjectsEnabled = () => {
    return (flags?.getValue(BLINK_FLAGS.enableEurekaTypeaheadRecentObjects.name) ||
        getConfigInfo(jsonConstants.ENABLE_EUREKA_TYPEAHEAD_RECENT_OBJECTS));
};
export const isLocalizationEnabled = () => {
    const localizationEnabled = getConfigInfo(jsonConstants.ENABLE_LOCALIZATION);
    if (localizationEnabled === null) {
        return true;
    }
    return localizationEnabled;
};
export const getPreferredLocale = (clientLocale, clusterLocalizationEnabled, browserLocale) => {
    const localeOverride = flags?.getValue(BLINK_FLAGS.locale.name);
    if (localeOverride) {
        return localeOverride;
    }
    if (clusterLocalizationEnabled === false || !isLocalizationEnabled()) {
        return 'en-US';
    }
    const preferredLocale = getExposedUserPreference(SESSION_USER_PREFERENCES.PREFERRED_LOCALE);
    if (preferredLocale) {
        return preferredLocale;
    }
    if (clientLocale) {
        setExposedUserPreference(SESSION_USER_PREFERENCES.PREFERRED_LOCALE, clientLocale);
    }
    const lastSeenLocale = getExposedUserPreference(SESSION_USER_PREFERENCES.LAST_SEEN_LOCALE);
    setExposedUserPreference(SESSION_USER_PREFERENCES.LAST_SEEN_LOCALE, clientLocale ?? browserLocale ?? lastSeenLocale ?? 'en-US');
    return clientLocale ?? browserLocale ?? lastSeenLocale ?? 'en-US';
};
export const getFormatLocales = (clientLocale, clusterLocalizationEnabled, browserLocale) => {
    const preferredLocale = getPreferredLocale(clientLocale, clusterLocalizationEnabled, browserLocale);
    const dateFormatLocale = flags?.getValue(BLINK_FLAGS.dateFormatLocale.name) || preferredLocale;
    const numberFormatLocale = flags?.getValue(BLINK_FLAGS.numberFormatLocale.name) || preferredLocale;
    return {
        preferredLocale,
        dateFormatLocale,
        numberFormatLocale,
    };
};
export const getPreferredCurrencyFormat = () => {
    return flags?.getValue(BLINK_FLAGS.currencyFormat.name);
};
export function isSearchIQModePreferred(currentLocale) {
    return (isEnliteEnabled(currentLocale) &&
        getPreference('SEARCH_MODE') === 'SEARCHIQ');
}
export const getUserGuid = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject ? sessionInfoObject.userGUID : '';
};
export const getUserIndexVersion = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject ? sessionInfoObject.userIndexVersion : 0;
};
export function getUserExpirationTime() {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject ? sessionInfoObject.userExpirationTime : null;
}
export const hasSeenCopilotOnboardingIntroVideo = () => {
    const hasSeenCopilotOnboardingFlag = flags?.getValue(BLINK_FLAGS.hasSeenCopilotOnboarding.name);
    if (!_.isUndefined(hasSeenCopilotOnboardingFlag)) {
        return hasSeenCopilotOnboardingFlag;
    }
    const sessionInfo = getSessionInfo();
    const onboardingActivity = sessionInfo
        ? sessionInfo[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.COPILOT_ONBOARDING_SEEN)
        ? onboardingActivity[jsonConstants.COPILOT_ONBOARDING_SEEN]
        : true;
};
export const setHasSeenCopilotOnboardingFlag = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.COPILOT_ONBOARDING_SEEN] = true;
    }
    flags?.setValue(BLINK_FLAGS.hasSeenCopilotOnboarding.name, true);
};
export const isEdgeOnboardingCompleted = () => {
    const edgeOnboardingCompleted = flags?.getValue(BLINK_FLAGS.edgeOnboardingCompleted.name);
    if (!_.isUndefined(edgeOnboardingCompleted)) {
        return edgeOnboardingCompleted;
    }
    const sessionInfo = getSessionInfo();
    const onboardingActivity = sessionInfo
        ? sessionInfo[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.EDGE_ONBOARDING_COMPLETED)
        ? onboardingActivity[jsonConstants.EDGE_ONBOARDING_COMPLETED]
        : true;
};
export const setEdgeOnboardingCompleted = (isEdgeOnboardingCompleted) => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.EDGE_ONBOARDING_COMPLETED] = isEdgeOnboardingCompleted;
    }
    flags?.setValue(BLINK_FLAGS.edgeOnboardingCompleted.name, isEdgeOnboardingCompleted);
    if (!isEdgeOnboardingCompleted) {
        localStorage.removeItem('isOpenedFromSpotter');
    }
};
export const setOnboardingLocationSelected = (onboardingLocationSelected) => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.ONBOARDING_LOCATION_SELECTED] = onboardingLocationSelected;
    }
};
export const getOnboardingLocationSelected = () => {
    const sessionInfo = getSessionInfo();
    const onboardingActivity = sessionInfo
        ? sessionInfo[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.ONBOARDING_LOCATION_SELECTED)
        ? onboardingActivity[jsonConstants.ONBOARDING_LOCATION_SELECTED]
        : '';
};
export const isEdgeOnboardingPersonalInfoFilled = () => {
    const edgeOnboardingPersonalInfoFilled = flags?.getValue(BLINK_FLAGS.edgeOnboardingPersonalInfoFilled.name);
    if (!_.isUndefined(edgeOnboardingPersonalInfoFilled)) {
        return edgeOnboardingPersonalInfoFilled;
    }
    const sessionInfo = getSessionInfo();
    const onboardingActivity = sessionInfo
        ? sessionInfo[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.EDGE_ONBOARDING_PERSONAL_INFO_FILLED)
        ? onboardingActivity[jsonConstants.EDGE_ONBOARDING_PERSONAL_INFO_FILLED]
        : true;
};
export const setEdgeOnboardingPersonalInfoFilled = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.EDGE_ONBOARDING_PERSONAL_INFO_FILLED] = true;
    }
    flags?.setValue(BLINK_FLAGS.edgeOnboardingPersonalInfoFilled.name, true);
};
export const isEdgeOnboardingIntroVideoSeen = () => {
    const edgeOnboardingIntroVideoSeen = flags?.getValue(BLINK_FLAGS.edgeOnboardingIntroVideoSeen.name);
    if (!_.isUndefined(edgeOnboardingIntroVideoSeen)) {
        return edgeOnboardingIntroVideoSeen;
    }
    const sessionInfo = getSessionInfo();
    const onboardingActivity = sessionInfo
        ? sessionInfo[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.EDGE_ONBOARDING_INTRO_VIDEO_SEEN)
        ? onboardingActivity[jsonConstants.EDGE_ONBOARDING_INTRO_VIDEO_SEEN]
        : true;
};
export const setEdgeOnboardingIntroVideoSeen = (isEdgeOnboardingIntroVideoSeen) => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.EDGE_ONBOARDING_INTRO_VIDEO_SEEN] = isEdgeOnboardingIntroVideoSeen;
    }
    flags?.setValue(BLINK_FLAGS.edgeOnboardingIntroVideoSeen.name, isEdgeOnboardingIntroVideoSeen);
};
export const hasSeenFreeTrialOnboarding = () => {
    const freeTrialOnboardingSeen = flags?.getValue(BLINK_FLAGS.freeTrialOnboardingSeen.name);
    if (!_.isUndefined(freeTrialOnboardingSeen)) {
        return freeTrialOnboardingSeen;
    }
    const sessionInfo = getSessionInfo();
    const onboardingActivity = sessionInfo
        ? sessionInfo[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.FREE_TRIAL_ONBOARDING_SEEN)
        ? onboardingActivity[jsonConstants.FREE_TRIAL_ONBOARDING_SEEN]
        : true;
};
export const getUserDisplayName = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject ? sessionInfoObject.userDisplayName : '';
};
export function getUserEmail() {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject ? sessionInfoObject[jsonConstants.USER_EMAIL] : '';
}
export const getUserName = () => {
    const sessionInfo = getSessionInfo();
    return sessionInfo ? sessionInfo.userName : '';
};
export function getFirstLoginDate() {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject
        ? sessionInfoObject[jsonConstants.FIRST_LOGIN_DATE]
        : null;
}
export const getUserProperties = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject && sessionInfoObject.userProperties
        ? sessionInfoObject.userProperties
        : {};
};
export const getUserPersona = () => {
    const userProperties = getUserProperties();
    const userPersona = flags?.getValue(BLINK_FLAGS.userPersona.name) ||
        userProperties.persona ||
        UserPersona.UNDEFINED;
    if (_.isEmpty(userPersona)) {
        return UserPersona.UNDEFINED;
    }
    return userPersona.toLowerCase();
};
export const getPurchaseOption = () => {
    const userProperties = getUserProperties();
    const purchaseOption = flags?.getValue(BLINK_FLAGS.purchaseOption.name) ||
        userProperties.purchaseOption;
    switch (purchaseOption) {
        case PurchaseOption.CONTACT_SALES:
            return PurchaseOption.CONTACT_SALES;
        case PurchaseOption.BUY_NOW:
            return PurchaseOption.BUY_NOW;
        default:
            return PurchaseOption.CONTACT_SALES;
    }
};
export const getSubscriptionType = () => {
    const userProperties = getUserProperties();
    const subscriptionType = flags?.getValue(BLINK_FLAGS.subscriptionType.name) ||
        userProperties.subscriptionType;
    switch (subscriptionType) {
        case SubscriptionType.FREE_TRIAL:
            return SubscriptionType.FREE_TRIAL;
        case SubscriptionType.TEAMS_EDITION:
            return SubscriptionType.TEAMS_EDITION;
        case SubscriptionType.DEVELOPER_EDITION:
            return SubscriptionType.DEVELOPER_EDITION;
        default:
            return SubscriptionType.NONE;
    }
};
export const getUserState = () => {
    const blinkFlagValue = flags?.getValue(BLINK_FLAGS.userState.name);
    if (!_.isUndefined(blinkFlagValue))
        return blinkFlagValue;
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject && sessionInfoObject.state
        ? sessionInfoObject.state
        : UserState.ACTIVE;
};
export const getBillingCustomerId = () => {
    const userProperties = getUserProperties();
    const billingCustomerId = userProperties.billingCustomerId;
    return billingCustomerId || '';
};
export const getSubscriptionTier = () => {
    const userProperties = getUserProperties();
    const subscriptionTier = userProperties.subscriptionTier;
    return subscriptionTier || '';
};
export const getTeamId = () => {
    const userProperties = getUserProperties();
    const teamId = userProperties.teamId;
    return teamId || '';
};
export const getIsTeamEditionOwner = () => {
    const userProperties = getUserProperties();
    return !!userProperties.isTeamOwner;
};
export const getUserSource = () => {
    const userProperties = getUserProperties();
    return (flags?.getValue(BLINK_FLAGS.userSource.name) || userProperties.source);
};
export const isTrialVersionEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.enableTrialCluster.name)))
        return flags?.getValue(BLINK_FLAGS.enableTrialCluster.name);
    const trialVersionConfig = getConfigInfo(jsonConstants.TRIAL_VERSION_CONFIG_KEY);
    return (!!trialVersionConfig &&
        !!trialVersionConfig[jsonConstants.TRIAL_ENABLED_KEY]);
};
export const getTrialClusterQualifiedUrl = () => {
    const trialVersionConfig = getConfigInfo(jsonConstants.TRIAL_VERSION_CONFIG_KEY);
    return (trialVersionConfig?.[jsonConstants.TRIAL_CLUSTER_QUALIFIED_URL] ?? '');
};
export const isSkipFreeTrialOnboardingStepsEnabled = () => {
    const flagValue = flags?.getValue(BLINK_FLAGS.skipFreeTrialOnboardingSteps.name);
    if (flagValue !== undefined) {
        return flagValue;
    }
    const trialVersionConfig = getConfigInfo(jsonConstants.TRIAL_VERSION_CONFIG_KEY);
    return (trialVersionConfig?.[jsonConstants.SKIP_FREE_TRIAL_ONBOARDING_STEPS] ??
        false);
};
export const deferLayoutWithRaf = () => {
    const deferLayoutWithRafFlag = flags?.getValue(BLINK_FLAGS.deferLayoutWithRaf.name);
    if (deferLayoutWithRafFlag !== undefined) {
        return deferLayoutWithRafFlag;
    }
    const deferLayoutWithRafConfig = getConfigInfo(jsonConstants.DEFER_LAYOUT_WITH_RAF);
    return deferLayoutWithRafConfig ?? false;
};
export const getExpiredAccountLifeDays = () => {
    const trialVersionConfig = getConfigInfo(jsonConstants.TRIAL_VERSION_CONFIG_KEY);
    return (trialVersionConfig &&
        trialVersionConfig[jsonConstants.EXPIRED_ACCOUNT_LIFE_DAYS]);
};
export const getUserActivationMode = () => {
    const userSource = getUserSource();
    const isFreeTrialCluster = isTrialVersionEnabled();
    if (!isFreeTrialCluster) {
        return UserActivationMode.SAAS_PRODUCTION;
    }
    if (userSource === SNOWFLAKE_PARTNER_CONNECT_SOURCE) {
        return UserActivationMode.PARTNER_CONNECT;
    }
    return UserActivationMode.SAAS_FREE_TRIAL;
};
const pathJoin = (parts) => {
    return (parts
        .filter(e => e)
        .map(i => i.replace(/(^\/|\/$)/g, ''))
        .join('/'));
};
export const getReleaseVersion = () => {
    const info = getSessionInfo();
    return info?.[jsonConstants.version.RELEASE] || 'unknown';
};
export const getBaseReleaseVersion = () => {
    const releaseVersion = getReleaseVersion();
    const baseVersionLen = releaseVersion.lastIndexOf('-') !== -1
        ? releaseVersion.lastIndexOf('-')
        : releaseVersion.length;
    return releaseVersion.substr(0, baseVersionLen);
};
export const getOnlineDocUrl = () => {
    const docConfig = getConfigInfo('documentationConfig');
    return docConfig?.onlineDocUrl;
};
export const getInAppHelpUrl = () => {
    const docConfig = getConfigInfo('documentationConfig');
    return docConfig?.inAppHelpUrl || '';
};
const getDocumentationURLBasePath = () => pathJoin([getOnlineDocUrl(), getBaseReleaseVersion()]);
export const getDocumentationLink = (path) => pathJoin([getDocumentationURLBasePath(), path]);
export const isFirstLogin = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject[jsonConstants.IS_FIRST_LOGIN];
};
export const userType = () => {
    if (flags?.getValue(BLINK_FLAGS.userType.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.userType.name);
    }
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject[jsonConstants.USER_TYPE_KEY];
};
export function getJSessionId() {
    return getConfigInfo('partialJSessionGUID');
}
export function getClusterName() {
    return getConfigInfo('selfClusterName');
}
export function getClusterId() {
    return getConfigInfo('selfClusterId');
}
export const isNebulaDevCluster = () => {
    return getClusterId() === blinkConstants.localClusterId;
};
export const getInviteModalType = () => {
    if (flags?.getValue(BLINK_FLAGS.inviteModalType.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.inviteModalType.name);
    }
    return _.isEmpty(getConfigInfo(jsonConstants.INVITE_MODAL_TYPE)) ||
        getConfigInfo(jsonConstants.INVITE_MODAL_TYPE) ===
            InviteModalType.DEFAULT
        ? InviteModalType.DEFAULT
        : InviteModalType.EMAIL_REFERRAL;
};
export const enableOnboardingConfigPerOrg = () => {
    return getConfigInfo(jsonConstants.enableOnboardingConfigPerOrg);
};
export const enableTeamsAdminTab = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.enableTeamsAdminTab.name)))
        return flags?.getValue(BLINK_FLAGS.enableTeamsAdminTab.name);
    const enableTeamsAdminTab = getConfigInfo(jsonConstants.ENABLE_TEAMS_ADMIN_TAB);
    return enableTeamsAdminTab ?? false;
};
export function getSchedhuledMaintainance() {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.SCHEDHULE_MAINTAINANCE]) {
        return null;
    }
    return sessionInfoObject[jsonConstants.SCHEDHULE_MAINTAINANCE];
}
export const getDataBaseMode = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return ((embraceConfig && embraceConfig[jsonConstants.DATABASE_MODE]) ||
        DataBaseMode.MIXED);
};
export const isColumnSummariesEnabledByDefault = () => {
    if (flags?.getValue(BLINK_FLAGS.isColumnSummariesEnabledByDefault.name) !==
        undefined) {
        return flags?.getValue(BLINK_FLAGS.isColumnSummariesEnabledByDefault.name);
    }
    const enableColumnSummariesByDefault = getConfigInfo(jsonConstants.ENABLE_COLUMN_SUMMARIES_BY_DEFAULT);
    return enableColumnSummariesByDefault ?? true;
};
export const isEnableSummariesCustomFormatByDefault = () => {
    const enableSummariesCustomFormatByDefault = getConfigInfo(jsonConstants.ENABLE_SUMMARIES_CUSTOM_FORMAT_BY_DEFAULT);
    return enableSummariesCustomFormatByDefault ?? true;
};
export const isDelayedSearchEnabled = () => {
    const instantSearchUrlFlag = flags?.getValue(BLINK_FLAGS.instantSearch.name);
    if (_.isBoolean(instantSearchUrlFlag)) {
        return !instantSearchUrlFlag;
    }
    const instantSearchClusterFlag = getConfigInfo(jsonConstants.ENABLE_INSTANT_SEARCH);
    return _.isNull(instantSearchClusterFlag)
        ? true
        : !instantSearchClusterFlag;
};
export const shouldShowAutoGeneratedFormulas = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.showAutoGeneratedFormulas.name);
    return urlFlag ?? false;
};
export const isEurekaEnabled = () => {
    const eurekaUrlFlag = flags?.getValue(BLINK_FLAGS.enableEurekaSearch.name);
    if (_.isBoolean(eurekaUrlFlag)) {
        return eurekaUrlFlag;
    }
    return getConfigInfo(jsonConstants.ENABLE_EUREKA_SEARCH);
};
export const isEdgeEnabled = () => {
    const edgeUrlFlag = flags?.getValue(BLINK_FLAGS.edgeEnabled.name);
    if (_.isBoolean(edgeUrlFlag)) {
        return edgeUrlFlag;
    }
    return getConfigInfo(jsonConstants.ENABLE_EDGE) === true;
};
export const isTSEV2ExperienceEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_TSE_V2_EXPERIENCE);
};
export const isRegisterAMAButtonEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_REGISTER_AMA_BUTTON);
};
export const isCopilotEnabled = () => {
    const copilotUrlFlag = flags?.getValue(BLINK_FLAGS.copilotEnabled.name);
    if (_.isBoolean(copilotUrlFlag)) {
        return copilotUrlFlag;
    }
    return getConfigInfo(jsonConstants.ENABLE_COPILOT) === true;
};
export const isEnhancedUserEmailValidationEnabled = () => {
    const enhancedUserEmailValidationEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.enhancedUserEmailValidationEnabled.name);
    if (_.isBoolean(enhancedUserEmailValidationEnabledUrlFlag)) {
        return enhancedUserEmailValidationEnabledUrlFlag;
    }
    return getConfigInfo(jsonConstants.ENABLE_ENHANCED_USER_EMAIL_VALIDATION);
};
export const isStatusPageEnabled = () => {
    const statusPageUrlFlag = flags?.getValue(BLINK_FLAGS.statusPageEnabled.name);
    if (!_.isUndefined(statusPageUrlFlag)) {
        return statusPageUrlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.statusPageEnabled || false;
};
export const getTip = (key) => {
    return (flags?.getValue(BLINK_FLAGS.neverShowTips.name) ||
        (!flags?.getValue(BLINK_FLAGS.alwaysShowTips.name) &&
            getClientStateTip(key)));
};
export const getSageDataSource = () => {
    return getPreference(PREFERENCE_KEY_SAGE_DATA_SOURCE) || [];
};
export const getEurekaDataSource = () => {
    return getPreference(PREFERENCE_KEY_EUREKA_DATA_SOURCE);
};
export const setEurekaDataSource = (dataSourceId, dataSourceName, aiAnswerGenerationDisabled) => {
    setPreference(PREFERENCE_KEY_EUREKA_DATA_SOURCE, {
        id: dataSourceId,
        label: dataSourceName,
        aiAnswerGenerationDisabled,
    }, !isReactshellEnabled());
};
export const setSageDataSource = dataScope => {
    setPreference(PREFERENCE_KEY_SAGE_DATA_SOURCE, dataScope, !isReactshellEnabled());
};
export const getUserFileUploadSize = () => {
    return (getConfigInfo(jsonConstants.USER_FILE_UPLOAD_SIZE) || 50 * 1024 * 1024);
};
export const objectRequestAccessLevel = () => {
    const requestAccessConfig = getConfigInfo(jsonConstants.REQUEST_ACCESS_CONFIG);
    if (requestAccessConfig &&
        Object.prototype.hasOwnProperty.call(requestAccessConfig, jsonConstants.OBJECT_REQUEST_ACCESS_LEVEL)) {
        return RequestAccessLevel[requestAccessConfig[jsonConstants.OBJECT_REQUEST_ACCESS_LEVEL]];
    }
    return RequestAccessLevel.ALL;
};
export const dataRequestAccessLevel = () => {
    const requestAccessConfig = getConfigInfo(jsonConstants.REQUEST_ACCESS_CONFIG);
    if (requestAccessConfig &&
        Object.prototype.hasOwnProperty.call(requestAccessConfig, jsonConstants.DATASOURCE_REQUEST_ACCESS_LEVEL)) {
        return RequestAccessLevel[requestAccessConfig[jsonConstants.DATASOURCE_REQUEST_ACCESS_LEVEL]];
    }
    return RequestAccessLevel.VIEW;
};
export const getIntercomAppId = () => {
    return getConfigInfo(jsonConstants.INTERCOM_APP_ID);
};
export const getIntercomUserHash = () => {
    return getConfigInfo(jsonConstants.INTERCOM_USER_HASH);
};
export const getDefaultCalendar = () => {
    const calendars = getConfigInfo(jsonConstants.CALENDARS_KEY);
    if (!calendars) {
        return null;
    }
    if (!_.has(calendars, jsonConstants.DEFAULT_CALENDAR_KEY)) {
        return null;
    }
    return calendars[jsonConstants.DEFAULT_CALENDAR_KEY];
};
export function isCustomCalendar() {
    const defaultCalendar = getDefaultCalendar();
    let isCustomCalendarState = false;
    _.forIn(defaultCalendar, value => {
        if (value !== 1) {
            isCustomCalendarState = true;
        }
    });
    return isCustomCalendarState;
}
export function getQuarterStartMonth() {
    const defaultCalendar = getDefaultCalendar();
    if (_.has(defaultCalendar, jsonConstants.CALENDAR_QUARTER_START_MONTH_KEY)) {
        return defaultCalendar[jsonConstants.CALENDAR_QUARTER_START_MONTH_KEY];
    }
    return DEFAULT_QUARTER_START_MONTH;
}
export const getSessionTimeZone = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject && sessionInfoObject.timezone;
};
export const getDefaultDataSizeForHighCardinalityCharts = () => {
    return getConfigInfo(jsonConstants.DEFAULT_CHART_DATA_SIZE);
};
export const getMaxDataSizeForHighCardinalityCharts = () => {
    return getConfigInfo(jsonConstants.HIGH_CARDINALITY_MAX_DATA_SIZE);
};
const DEFAULT_CUSTOM_CHART_ROWS_LIMIT = 20000;
export const getCustomChartRowsLimit = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.customChartRowsLimit.name);
    if (Number.isFinite(urlFlag) && urlFlag > 0) {
        return urlFlag;
    }
    return (getConfigInfo(jsonConstants.CUSTOM_CHART_ROWS_LIMIT) ||
        DEFAULT_CUSTOM_CHART_ROWS_LIMIT);
};
export const isFetchPivotSummaryFromBackendEnabled = () => {
    return getConfigInfo(jsonConstants.FETCH_PIVOT_SUMMARY_FROM_BACKEND);
};
export const isPendoEnabled = () => {
    const pendoConfig = getConfigInfo(jsonConstants.ENABLE_PENDO);
    const pendoFlag = flags?.getValue(BLINK_FLAGS.enablePendo.name);
    return !_.isUndefined(pendoFlag) ? pendoFlag : !!pendoConfig;
};
export const getSentryAnonymizedDSN = () => {
    return getConfigInfo(jsonConstants.SENTRY_ANONYMIZED_DSN) ?? '';
};
export const getSentryFullDSN = () => {
    return getConfigInfo(jsonConstants.SENTRY_FULL_DSN) ?? '';
};
export const getReplayLinkBaseUrl = () => {
    return getConfigInfo(jsonConstants.REPLAY_LINK_BASE_URL) ?? '';
};
export const isPendoHelpEnabled = () => {
    const pendoHelp = getConfigInfo(jsonConstants.ENABLE_PENDO_HELP);
    const pendoHelpFlag = flags?.getValue(BLINK_FLAGS.enablePendoHelp.name);
    if (isPendoEnabled()) {
        if (!_.isUndefined(pendoHelpFlag)) {
            return pendoHelpFlag;
        }
        return !!pendoHelp;
    }
    return false;
};
export const canShareDataAccessinSharing = () => {
    const sharingConfiguration = getConfigInfo(jsonConstants.SHARING_CONFIGURATION);
    const enableDataSourceShareBySharerFlag = flags?.getValue(BLINK_FLAGS.enableDataSourceShareBySharer.name);
    if (!sharingConfiguration ||
        !Object.prototype.hasOwnProperty.call(sharingConfiguration, jsonConstants.ENABLE_DATA_SOURCE_SHARING)) {
        return !!enableDataSourceShareBySharerFlag;
    }
    return !_.isUndefined(enableDataSourceShareBySharerFlag)
        ? enableDataSourceShareBySharerFlag
        : sharingConfiguration[jsonConstants.ENABLE_DATA_SOURCE_SHARING];
};
export const isVizSharingEnabled = () => {
    const sharingConfiguration = getConfigInfo(jsonConstants.SHARING_CONFIGURATION);
    const enableVizSharingFlag = flags?.getValue(BLINK_FLAGS.enableVizSharing.name);
    if (!sharingConfiguration ||
        !Object.prototype.hasOwnProperty.call(sharingConfiguration, jsonConstants.ENABLE_VIZ_SHARING)) {
        return !!enableVizSharingFlag;
    }
    return !_.isUndefined(enableVizSharingFlag)
        ? enableVizSharingFlag
        : sharingConfiguration[jsonConstants.ENABLE_VIZ_SHARING];
};
export const isVizSharingWithLinkEnabled = () => {
    const sharingConfiguration = getConfigInfo(jsonConstants.SHARING_CONFIGURATION);
    const enableVizSharingWithLinkFlag = flags?.getValue(BLINK_FLAGS.enableVizSharingWithLink.name);
    if (!sharingConfiguration ||
        !Object.prototype.hasOwnProperty.call(sharingConfiguration, jsonConstants.ENABLE_VIZ_SHARING_WITH_LINK)) {
        return !!enableVizSharingWithLinkFlag;
    }
    return !_.isUndefined(enableVizSharingWithLinkFlag)
        ? enableVizSharingWithLinkFlag
        : sharingConfiguration[jsonConstants.ENABLE_VIZ_SHARING_WITH_LINK];
};
export const isSharingDisabled = () => {
    const sharingInfo = getConfigInfo(jsonConstants.DISABLE_SHARING);
    return !!sharingInfo;
};
export const isAutoWorksheetEnabled = () => {
    const autoWorksheetEnabledFLag = flags?.getValue(BLINK_FLAGS.isAutoWorksheetEnabled.name);
    if (!_.isEmpty(autoWorksheetEnabledFLag)) {
        return autoWorksheetEnabledFLag;
    }
    const autogenConfig = getConfigInfo(jsonConstants.AUTOGEN_CONFIG);
    const autoWorksheetEnabled = autogenConfig[jsonConstants.WORKSHEETGEN_CONFIG][jsonConstants.WORKSHEETGEN_ENABLED];
    return !!autoWorksheetEnabled;
};
export const isDeleteDependentJoinEnbaled = () => {
    return getConfigInfo(jsonConstants.ENABLE_DELETE_DEPENDENT_JOIN) || false;
};
export const isUpdatedJoinsUIEnabled = () => {
    return getConfigInfo(jsonConstants.UPDATED_JOINS_UI_ENABLED) || false;
};
export const isSaaSCluster = () => {
    const saaSFlag = flags?.getValue(BLINK_FLAGS.isSaaSCluster.name);
    if (_.isBoolean(saaSFlag)) {
        return saaSFlag;
    }
    return getConfigInfo(jsonConstants.IS_SAAS_CLUSTER) || false;
};
export const shouldShowWhiteListedDomains = () => {
    const schedulerConfig = getConfigInfo(jsonConstants.SCHEDULER_CONFIG);
    return schedulerConfig[jsonConstants.SHOW_WHITELISTED_DOMAINS];
};
export const getWhiteListedEmailDomainsForReport = () => {
    const schedulerConfig = getConfigInfo(jsonConstants.SCHEDULER_CONFIG);
    const domains = schedulerConfig &&
        schedulerConfig[jsonConstants.EMAIL_DOMAINS_WHITELIST];
    if (!domains) {
        return [];
    }
    return domains;
};
export const getTotalTrialDays = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.totalTrialDays.name)))
        return flags?.getValue(BLINK_FLAGS.totalTrialDays.name);
    const trialVersionConfig = getConfigInfo(jsonConstants.TRIAL_VERSION_CONFIG_KEY);
    return (trialVersionConfig &&
        trialVersionConfig[jsonConstants.TRIAL_ACCOUNT_LIFE_DAYS]);
};
export const isAAQEnabled = () => {
    const isEnabled = getConfigInfo(jsonConstants.AAQ_ENABLED);
    const AAQFlag = flags?.getValue(BLINK_FLAGS.enableAAQ.name);
    return !_.isUndefined(AAQFlag) ? AAQFlag : !!isEnabled;
};
export const hasA3AnalysisPrivileges = () => {
    return hasPrivilege(PRIVILEGES.A3ANALYSIS);
};
export const isA3Enabled = () => {
    const a3Config = getConfigInfo(jsonConstants.A3_CONFIG);
    return a3Config?.[jsonConstants.A3_ENABLED] && hasA3AnalysisPrivileges();
};
export const hasThirdPartyAnalysisPrivileges = () => {
    return hasPrivilege(PRIVILEGES.THIRDPARTY_ANALYSIS);
};
export const hasRAnalysisPrivileges = () => {
    return hasPrivilege(PRIVILEGES.RANALYSIS);
};
export const isCustomRinSearchPageEnabled = () => {
    const a3Config = getConfigInfo(jsonConstants.A3_CONFIG);
    return !!(a3Config && a3Config[jsonConstants.ENABLE_CUSTOM_R_IN_SEARCH_PAGE]);
};
export const isRAnalysisEnabled = () => {
    const isRAnalysisEnabledInUrl = flags?.getValue(BLINK_FLAGS.isRAnalysisEnabled.name);
    if (!isA3Enabled() || !hasRAnalysisPrivileges()) {
        return false;
    }
    if (isRAnalysisEnabledInUrl !== undefined) {
        return isRAnalysisEnabledInUrl;
    }
    return isCustomRinSearchPageEnabled();
};
export const isCJAEnabled = () => {
    return false;
};
export const isPrimaryNavHidden = () => {
    const isPrimaryNavHidden = flags?.getValue(BLINK_FLAGS.primaryNavHidden.name);
    if (!_.isUndefined(isPrimaryNavHidden)) {
        return isPrimaryNavHidden;
    }
    const fullEmbedConfig = getConfigInfo(jsonConstants.FULL_EMBED_CONFIG);
    return fullEmbedConfig && fullEmbedConfig[jsonConstants.PRIMARY_NAV_HIDDEN];
};
export const isFoldersEnabled = () => {
    return getConfigInfo(jsonConstants.FOLDERS_ENABLED) || false;
};
export const isAgentSpotEnabled = () => {
    return sessionInfo?.agentSpotConfig?.enabled || false;
};
export const getAgentSpotUrl = () => {
    return sessionInfo?.agentSpotConfig?.[jsonConstants.AGENT_SPOT_URL] || '';
};
export const isCollectionsPrivilegeEnabled = () => {
    return getConfigInfo(jsonConstants.COLLECTIONS_PRIVILEGE_ENABLED) || false;
};
export const shouldLogoutWithRedirect = () => {
    return getConfigInfo(jsonConstants.LOGOUT_WITH_REDIRECT) || false;
};
export const isIamV2OidcEnabled = () => {
    const oidcConfiguration = getConfigInfo(jsonConstants.OIDC_CONFIG);
    if (oidcConfiguration &&
        !oidcConfiguration[jsonConstants.OIDC_CONFIGURATION_ENABLED]) {
        return false;
    }
    return oidcConfiguration && oidcConfiguration[jsonConstants.OIDC_ENABLED];
};
export const shouldSkipAddUserToMode = () => {
    return getConfigInfo(jsonConstants.SHOULD_SKIP_ADD_USER_TO_MODE);
};
export const getOrgDelimiter = () => {
    const oidcConfiguration = getConfigInfo(jsonConstants.OIDC_CONFIG);
    if (oidcConfiguration) {
        if (!oidcConfiguration[jsonConstants.ORG_DELIMITER]) {
            return '@';
        }
        return oidcConfiguration[jsonConstants.ORG_DELIMITER];
    }
    return '@';
};
export const isMfaEnabled = () => {
    const isMFAEnabled = getConfigInfo(jsonConstants.MFA_ENABLED);
    return isMFAEnabled;
};
export const isMFABannerUIEnabled = () => {
    const isMFABannerUIEnabled = getConfigInfo(jsonConstants.MFA_BANNER_UI_ENABLED);
    return isMFABannerUIEnabled;
};
export const mfaAccepted = () => {
    const mfaAccepted = getConfigInfo(jsonConstants.MFA_ACCEPTED);
    return mfaAccepted;
};
export const isAnalystStudioEnabled = () => {
    const analystStudioConfig = sessionInfo.analystStudioConfig;
    if (!_.isUndefined(analystStudioConfig) && analystStudioConfig != null) {
        return getConfigInfo(jsonConstants.ANALYST_STUDIO_ENABLED);
    }
    return false;
};
export const canAccessAnalystStudio = () => {
    return (_hasManageAnalystStudioPrivilege(getSessionInfo()) ||
        _hasAccessAnalystStudioPrivilege(getSessionInfo()) ||
        hasAdminPrivileges());
};
export const getOrgSubdomainLengthLimit = () => {
    return getConfigInfo(jsonConstants.ORG_SUB_DOMAIN_LIMIT);
};
export const isPrincipalImportExportEnabled = () => {
    const importExportEnabled = getConfigInfo(jsonConstants.PRINCIPAL_TYPE_IMPORT_EXPORT);
    return importExportEnabled;
};
export const isPartitionedCookieEnabled = () => {
    return true;
};
export const getPrincipalObjectExportLimit = () => {
    return getConfigInfo(jsonConstants.EXPORT_OBJECT_LIMIT);
};
export const isSyncUserGroupsAndOrgsFromOIDC = () => {
    const oidcConfiguration = getConfigInfo(jsonConstants.OIDC_CONFIG);
    if (oidcConfiguration &&
        !oidcConfiguration[jsonConstants.OIDC_CONFIGURATION_ENABLED]) {
        return false;
    }
    return (oidcConfiguration &&
        oidcConfiguration[jsonConstants.SYNC_USERS_GROUPS_AND_ORGS]);
};
export const isGroupsWithOrgsEnabled = () => {
    const oidcConfiguration = getConfigInfo(jsonConstants.OIDC_CONFIG);
    return (oidcConfiguration &&
        oidcConfiguration[jsonConstants.GROUPS_WTIH_ORGS_ENABLED]);
};
export const getIdentityDomain = () => {
    const oidcConfiguration = getConfigInfo(jsonConstants.OIDC_CONFIG);
    return (oidcConfiguration && oidcConfiguration[jsonConstants.IDENTITY_DOMAIN]);
};
export const getScimBaseUrl = () => {
    return getSessionInfo()?.scimBaseUrl || '';
};
export const isScimJwtEnabled = () => {
    const scimConfig = getConfigInfo(jsonConstants.SCIM_CONFIG);
    if (scimConfig) {
        return scimConfig[jsonConstants.SCIM_JWT_ENABLED];
    }
    return false;
};
export const shouldRemoveSessionsMeDependency = () => {
    return getConfigInfo(jsonConstants.REMOVE_SESSIONS_ME_DEPENDENCY) || false;
};
export const isScimPerOrgEnabled = () => {
    const scimConfig = getConfigInfo(jsonConstants.SCIM_CONFIG);
    if (scimConfig) {
        return scimConfig[jsonConstants.SCIM_PER_ORG_ENABLED];
    }
    return false;
};
export const areProfileAndHelpHidden = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.profileAndHelpInNavBarHidden.name);
    const backendConfig = getBackendConfig();
    if (envFlag !== undefined) {
        return envFlag;
    }
    const fullEmbedConfig = backendConfig?.fullEmbedConfiguration;
    return fullEmbedConfig?.profileAndHelpOptionsInNavBarHidden;
};
export const isApplicationSwitcherHidden = () => {
    const isApplicationSwitcherHidden = getConfigInfo(jsonConstants.APPLICATION_SWITCHER_HIDDEN);
    const isApplicationSwitcherHiddenFlag = flags?.getValue(BLINK_FLAGS.applicationSwitcherHidden.name);
    if (!_.isUndefined(isApplicationSwitcherHiddenFlag)) {
        return isApplicationSwitcherHiddenFlag;
    }
    return !!isApplicationSwitcherHidden;
};
export const isNotificationOnShareEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_NOTIFICATION_ON_SHARE);
};
export const isHambugerHidden = () => {
    const isHambugerHiddenFlag = flags?.getValue(BLINK_FLAGS.hideHamburger.name);
    if (!_.isUndefined(isHambugerHiddenFlag)) {
        return isHambugerHiddenFlag;
    }
    return false;
};
export const isNotificationHidden = () => {
    const isNotificationHiddenFlag = flags?.getValue(BLINK_FLAGS.hideNotification.name);
    if (!_.isUndefined(isNotificationHiddenFlag)) {
        return isNotificationHiddenFlag;
    }
    return false;
};
export const isObjectSearchHidden = () => {
    const isObjectSearchHiddenFlag = flags?.getValue(BLINK_FLAGS.hideObjectSearch.name);
    if (!_.isUndefined(isObjectSearchHiddenFlag)) {
        return isObjectSearchHiddenFlag;
    }
    return false;
};
export const isOrgSwitcherHidden = () => {
    const isOrgSwitcherHidden = getConfigInfo(jsonConstants.ORG_SWITCHER_HIDDEN);
    const isOrgSwitcherHiddenFlag = flags?.getValue(BLINK_FLAGS.orgSwitcherHidden.name);
    if (!_.isUndefined(isOrgSwitcherHiddenFlag)) {
        return isOrgSwitcherHiddenFlag;
    }
    return !!isOrgSwitcherHidden;
};
export const showOrgSwitcherToUsersWithMultipleOrgs = () => {
    const showOrgSwitcherToUsersWithMultipleOrgs = getConfigInfo(jsonConstants.SHOW_ORG_SWITCHER_TO_USERS_WITH_MULTIPLE_ORGS);
    return !!showOrgSwitcherToUsersWithMultipleOrgs;
};
export const isKPIChartEnabled = () => {
    const backendConfig = getBackendConfig();
    return ((backendConfig?.a3Config?.enableKPIChart || false) &&
        isAnswerV2Enabled());
};
export const isMonitorV2Enabled = () => {
    const backendConfig = getBackendConfig();
    return ((backendConfig?.a3Config?.enableMonitorV2 || false) &&
        isKPIChartEnabled() &&
        isAnswerV2Enabled());
};
export const isMonitorV2TabEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableMonitorV2Tab.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return (!!backendConfig?.a3Config?.enableMonitorV2Tab && isMonitorV2Enabled());
};
export const isWorksheetDisablementEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.enableEurekaWorksheetDisablement.name))) {
        return flags?.getValue(BLINK_FLAGS.enableEurekaWorksheetDisablement.name);
    }
    const enableEurekaWorksheetDisablement = getConfigInfo(jsonConstants.ENABLE_EUREKA_WORKSHEET_DISABLEMENT);
    return isEurekaEnabled() && !!enableEurekaWorksheetDisablement;
};
export const isTableExportEnabled = () => {
    if (flags?.getValue(BLINK_FLAGS.enableTableExport.name)) {
        return true;
    }
    return false;
};
export const isMonitorAlertNotificationChannelEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableMonitorAlertNotificationChannel.name);
    if (envFlag) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.enableWebhook && isMonitorV2Enabled();
};
export const isWebhookLCMEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableWebhookLCM.name);
    if (envFlag) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.enableWebhook && isMonitorV2Enabled();
};
export const isContextualChangeAnalysisEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableContextualChangeAnalysis.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.a3ChangeAnalysisSyncEnabled;
};
export const isCCAConfiguredColumnsEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableCCAConfiguredColumns.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.a3CCAConfiguredColumnsEnabled;
};
export const isSpotIQV2Enabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableSpotIQV2.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.spotIQV2Enabled;
};
export const isForecastAnalysisEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableSpotIQForecast.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return (isSpotIQV2Enabled() && backendConfig?.a3Config?.spotIQForecastEnabled);
};
export const isWhatIfAnalysisEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableWhatIfAnalysis.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return (isSpotIQV2Enabled() && backendConfig?.a3Config?.whatIfAnalysisEnabled);
};
export const isKeyDriversAnalysisEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableKeyDriversAnalysis.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.keyDriversAnalysisEnabled;
};
export const isThirdPartyAnalysisEnabled = () => {
    if (!isA3Enabled() || !hasThirdPartyAnalysisPrivileges()) {
        return false;
    }
    const envFlag = flags?.getValue(BLINK_FLAGS.enableThirdPartyAnalysis.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.thirdPartyAnalysisEnabled;
};
export const isIterativeCCAEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableIterativeCCA.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.enableIterativeCCA;
};
export const isAnomalyBasedAlertEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableAnomalyBasedAlert.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.enableAnomalyBasedAlert;
};
export const isContexualAlertCreationEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableContexualAlertCreation.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.enableContextualAlertCreation;
};
export const isAlertAcrossAttributesEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableAlertAcrossAttributes.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.alertAcrossAttributesEnabled;
};
export const isExplainChangeEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableExplainChange.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return ((backendConfig?.a3Config?.enableExplainChange || false) &&
        isKPIChartEnabled() &&
        isAnswerV2Enabled() &&
        isContextualChangeAnalysisEnabled());
};
export const isHeadlinePinningEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableHeadlinePinning.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const isPinningEnabled = getConfigInfo(jsonConstants.ENABLE_HEADLINE_PINNING);
    return _.isNil(isPinningEnabled) ? true : isPinningEnabled;
};
export const isCenterSingleValueKPIEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableCenterSingleValueKPI.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.a3EnableCenterSingleValueKPI;
};
export const isCustomComparisonOnChartEnabled = () => {
    const isCustomComparisonEnabled = getConfigInfo(jsonConstants.ENABLE_CUSTOM_COMPARISON_ON_CHART);
    return _.isNil(isCustomComparisonEnabled)
        ? false
        : isCustomComparisonEnabled;
};
export const isAdditionalInsightsEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableAdditionalInsights.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const isAdditionalInsightsEnabled = getConfigInfo(jsonConstants.ENABLE_ADDITIONAL_INSIGHTS);
    return _.isNil(isAdditionalInsightsEnabled)
        ? false
        : isAdditionalInsightsEnabled;
};
export const getSummaryTabSessionURLFlag = () => {
    return flags?.getValue(BLINK_FLAGS.enableCCASummaryTab.name);
};
export const isChangeAnalysisByMeasureEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableChangeAnalysisByMeasure.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const isChangeAnalysisByMeasureEnabled = getConfigInfo(jsonConstants.ENABLE_CHANGE_ANALYSIS_BY_MEASURE);
    return !!isChangeAnalysisByMeasureEnabled;
};
export const isAnalyzeButtonEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableAnalyzeButton.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const isAnalyzeButtonEnabled = getConfigInfo(jsonConstants.ENABLE_ANALYZE_BUTTON);
    return _.isNil(isAnalyzeButtonEnabled) ? false : isAnalyzeButtonEnabled;
};
export const isKPIWithAnomalyV2Enabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableKPIWithAnomalyV2.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.a3Experimental?.isKPIWithAnomalyV2Enabled;
};
export const isAnomalyV2Enabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableAnomalyV2.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.a3Experimental?.isAnomalyV2Enabled;
};
export const isKPIChartOnAttributesEnabled = () => {
    const backendConfig = getBackendConfig();
    return ((backendConfig?.a3Config?.enableKPIChartOnAttributes || false) &&
        isKPIChartEnabled());
};
export const isMonitorEnabledOnTse = () => {
    const backendConfig = getBackendConfig();
    return isMonitorV2Enabled() && backendConfig?.a3Config?.enableMonitorOnTse;
};
export const isPoweredFooterHiddenForFullEmbed = () => {
    const fullEmbedConfig = getConfigInfo(jsonConstants.FULL_EMBED_CONFIG);
    return (fullEmbedConfig &&
        !!fullEmbedConfig[jsonConstants.POWERED_FOOTER_HIDDEN]);
};
export const isVisualEmbedV2PlaygroundEnabled = () => {
    const visualEmbedPlaygroundV2Enabled = flags?.getValue(BLINK_FLAGS.enableVisualEmbedV2Playground.name);
    return (!_.isUndefined(visualEmbedPlaygroundV2Enabled) &&
        visualEmbedPlaygroundV2Enabled);
};
export const homepageUserCustomisationEnabled = () => {
    const homepageUserCustomisation = flags?.getValue(BLINK_FLAGS.homepageUserCustomisationEnabled.name);
    if (!_.isUndefined(homepageUserCustomisation)) {
        return homepageUserCustomisation;
    }
    return getConfigInfo(jsonConstants.HOMEPAGE_USER_CUSTOMISATION_ENABLED);
};
export const monitorRuleAutoCreateEnabled = () => {
    const monitorRuleAutoCreate = flags?.getValue(BLINK_FLAGS.monitorRuleAutoCreateEnabled.name);
    if (!_.isUndefined(monitorRuleAutoCreate)) {
        return monitorRuleAutoCreate;
    }
    return getConfigInfo(jsonConstants.MONITOR_RULE_AUTO_CREATE_ENABLED);
};
export const monitorRuleAutoCreateOptimizely = () => {
    return getValue(BLINK_FLAGS.monitorRuleAutoCreateOptimizely);
};
export const isChartAggregatedValOnKPIEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.showChartAggregatedValOnKPI.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return !!backendConfig?.a3Config?.showChartAggregatedValOnKPI;
};
export const scheduleJobAutoCreateEnabled = () => {
    const scheduleJobAutoCreate = flags?.getValue(BLINK_FLAGS.scheduleJobAutoCreateEnabled.name);
    if (!_.isUndefined(scheduleJobAutoCreate)) {
        return scheduleJobAutoCreate;
    }
    return getConfigInfo(jsonConstants.SCHEDULE_JOB_AUTO_CREATE_ENABLED);
};
export const scheduleJobAutoCreateOptimizely = () => {
    return getValue(BLINK_FLAGS.scheduleJobAutoCreateOptimizely);
};
export const flipTooltipToContextMenuEnabled = () => {
    const flipTooltipToContextMenu = flags?.getValue(BLINK_FLAGS.flipTooltipToContextMenuEnabled.name);
    if (!_.isUndefined(flipTooltipToContextMenu)) {
        return flipTooltipToContextMenu;
    }
    return !!getConfigInfo(jsonConstants.FLIP_TOOLTIP_TO_CONTEXT_MENU_ENABLED);
};
export const chartContextMenuExperiencePreferred = () => {
    return (getPreference(blinkConstants.preferenceKeys.CHART_CONTEXT_MENU_EXPERIENCE) || 'both');
};
export const openChartContextMenuOnLeftClick = () => {
    if (!flipTooltipToContextMenuEnabled()) {
        return false;
    }
    return (chartContextMenuExperiencePreferred() === 'left' ||
        chartContextMenuExperiencePreferred() === 'both');
};
export const openChartContextMenuOnRightClick = () => {
    if (!flipTooltipToContextMenuEnabled()) {
        return true;
    }
    return (chartContextMenuExperiencePreferred() === 'right' ||
        chartContextMenuExperiencePreferred() === 'both');
};
export const isContextMenuEnabledOnWhichClick = (enabledOnWhichClick) => {
    let contextMenuEnabledOnWhichClick = flags?.getValue(BLINK_FLAGS.contextMenuEnabledOnWhichClick.name);
    if (_.isUndefined(contextMenuEnabledOnWhichClick)) {
        contextMenuEnabledOnWhichClick = getConfigInfo(jsonConstants.CONTEXT_MENU_ENABLED_ON_WHICH_CLICK);
    }
    if (enabledOnWhichClick === 'left') {
        if (isContextMenuEnabledOnLeftClick()) {
            return true;
        }
        return (contextMenuEnabledOnWhichClick === 'left' ||
            contextMenuEnabledOnWhichClick === 'both');
    }
    return contextMenuEnabledOnWhichClick !== 'left';
};
export const tagManagementEnabled = () => {
    const tagManagement = flags?.getValue(BLINK_FLAGS.tagManagementEnabled.name);
    if (!_.isUndefined(tagManagement)) {
        return tagManagement;
    }
    return !!getConfigInfo(jsonConstants.TAG_MANAGEMENT_ENABLED);
};
export const enableHomeWatchlist = () => {
    const urlFlag = flags.getValue(BLINK_FLAGS.enableHomeWatchlist.name);
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    const homepageWatchlist = getConfigInfo(jsonConstants.ENABLE_HOME_WATCHLIST);
    return !!homepageWatchlist;
};
export const enableWatchlistAutoAlertCreation = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.enableWatchlistAutoAlertCreation;
};
export const enableHtmlSanitisation = () => {
    const enableHtmlSanitisationFlag = flags?.getValue(BLINK_FLAGS.enableHtmlSanitisation.name);
    if (enableHtmlSanitisationFlag !== undefined) {
        return enableHtmlSanitisationFlag;
    }
    const enableHtmlSanitisationConfig = getConfigInfo(jsonConstants.ENABLE_HTML_SANITISATION);
    return !!enableHtmlSanitisationConfig;
};
export const enableAlertOnHomeWatchlist = () => {
    const alertOnHomepageWatchlist = getConfigInfo(jsonConstants.ENABLE_ALERT_ON_HOME_WATCHLIST);
    return !!alertOnHomepageWatchlist;
};
export const isPoweredFooterHiddenForVizEmbed = () => {
    const vizEmbedConfig = getConfigInfo(jsonConstants.VIZ_EMBED_CONFIG);
    return (vizEmbedConfig && !!vizEmbedConfig[jsonConstants.POWERED_FOOTER_HIDDEN]);
};
export const getAllTablesConnectionGuid = () => {
    const autogenConfig = getConfigInfo(jsonConstants.AUTOGEN_CONFIG);
    if (!autogenConfig ||
        !autogenConfig[jsonConstants.ALL_TABLES_CONNECTION_ID]) {
        return '';
    }
    return autogenConfig[jsonConstants.ALL_TABLES_CONNECTION_ID];
};
export const isTeamsEnabled = () => {
    const teamsConfig = getConfigInfo(jsonConstants.TEAMS);
    const teamsFlag = flags?.getValue(BLINK_FLAGS.enableTeams.name);
    if (!_.isUndefined(teamsFlag)) {
        return teamsFlag;
    }
    if (teamsConfig &&
        Object.prototype.hasOwnProperty.call(teamsConfig, jsonConstants.TEAMS_ENABLED)) {
        return teamsConfig[jsonConstants.TEAMS_ENABLED];
    }
    return false;
};
export const getComsConfig = () => {
    return getConfigInfo(jsonConstants.COMS);
};
export const isComsEnabled = () => {
    const comsConfig = getComsConfig();
    const comsFlag = flags?.getValue(BLINK_FLAGS.enableComs.name);
    if (!_.isUndefined(comsFlag)) {
        return comsFlag;
    }
    return !!(comsConfig?.enabled && comsConfig?.v2Enabled);
};
export const getSparkFlowConfig = () => {
    return getConfigInfo(jsonConstants.SPARK_FLOW_CONFIG);
};
export const isSparkFlowEnabled = () => {
    const sparkFlowConfig = getSparkFlowConfig();
    const sparkFlowFlag = flags?.getValue(BLINK_FLAGS.sparkFlowEnabled.name);
    if (!_.isUndefined(sparkFlowFlag)) {
        return sparkFlowFlag;
    }
    return !!sparkFlowConfig?.enabled;
};
export const isOnboardingMTServicesEnabled = () => {
    const sparkFlowConfig = getSparkFlowConfig();
    return !!sparkFlowConfig?.onboardingMTServiceEnabled;
};
export const isDigestCommunicationEnabled = () => {
    const comsConfig = getComsConfig();
    return !!comsConfig?.digestCommEnabled;
};
export const isCustomizeEmailFromAdminEnabled = () => {
    const comsConfig = getComsConfig();
    return !!comsConfig?.customizeEmailFromAdmin;
};
export const isInAppNotificationEnabled = () => {
    const comsConfig = getComsConfig();
    if (!isComsEnabled()) {
        return false;
    }
    const enableInAppNotificationFlag = flags?.getValue(BLINK_FLAGS.enableInAppNotification.name);
    return _.isUndefined(enableInAppNotificationFlag)
        ? !!comsConfig[jsonConstants.IN_APP_NOTIFICATIONS_ENABLED]
        : enableInAppNotificationFlag;
};
export const isSlackBotEnabled = () => {
    const enableSlackBotFlag = flags?.getValue(BLINK_FLAGS.enableSlackBot.name);
    if (!_.isUndefined(enableSlackBotFlag)) {
        return enableSlackBotFlag;
    }
    const comsConfig = getComsConfig();
    if (!isComsEnabled()) {
        return false;
    }
    return !!comsConfig[jsonConstants.IS_SLACK_BOT_ENABLED];
};
export const isMsTeamsBotEnabled = () => {
    const isMsTeamsBotEnabledFlag = flags?.getValue(BLINK_FLAGS.isMsTeamsBotEnabled.name);
    if (!_.isUndefined(isMsTeamsBotEnabledFlag)) {
        return isMsTeamsBotEnabledFlag;
    }
    const comsConfig = getComsConfig();
    if (!isComsEnabled()) {
        return false;
    }
    return !!comsConfig[jsonConstants.IS_MS_TEAMS_BOT_ENABLED];
};
export const isMsTeamsNotificationPreferenceEnabled = () => {
    const isMsTeamsNotificationPreferenceEnabledFlag = flags?.getValue(BLINK_FLAGS.isMsTeamsNotificationPreferenceEnabled.name);
    if (!_.isUndefined(isMsTeamsNotificationPreferenceEnabledFlag)) {
        return isMsTeamsNotificationPreferenceEnabledFlag;
    }
    const comsConfig = getComsConfig();
    if (!isComsEnabled()) {
        return false;
    }
    return !!comsConfig[jsonConstants.IS_MS_TEAMS_NOTIFICATION_PREFERENCE_ENABLED];
};
export const isServerSideComsTokenEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_SERVER_SIDE_COMS_TOKEN) ?? false;
};
export const isMultipleDemoDataSetsEnabled = () => {
    const multipleDemoDatasetsEnabled = getConfigInfo(jsonConstants.MULTIPLE_DEMO_DATASETS_ENABLED);
    const multipleDemoDatasetsEnabledFlag = flags?.getValue(BLINK_FLAGS.enableMultipleDemoDatasets.name);
    if (!_.isUndefined(multipleDemoDatasetsEnabledFlag)) {
        return multipleDemoDatasetsEnabledFlag;
    }
    return !!multipleDemoDatasetsEnabled;
};
export const getTeamsConfig = () => {
    const teamsConfig = getConfigInfo(jsonConstants.TEAMS);
    const planPageURL = flags?.getValue(BLINK_FLAGS.planPageURL.name);
    if (_.isUndefined(planPageURL)) {
        return teamsConfig;
    }
    teamsConfig.offerPageUrl = planPageURL;
    return teamsConfig;
};
export const isSetupV2Enabled = () => {
    const analystOnboardingConfig = getConfigInfo(jsonConstants.ANALYST_ONBOARDING_CONFIG);
    const setupV2Flag = flags?.getValue(BLINK_FLAGS.enableSetupV2.name);
    if (!analystOnboardingConfig ||
        !Object.prototype.hasOwnProperty.call(analystOnboardingConfig, jsonConstants.SETUP_V2_ENABLED)) {
        return setupV2Flag;
    }
    return !_.isUndefined(setupV2Flag)
        ? setupV2Flag
        : analystOnboardingConfig[jsonConstants.SETUP_V2_ENABLED];
};
export const isAnalystOnboardingEnabled = () => {
    const analystOnboardingConfig = getConfigInfo(jsonConstants.ANALYST_ONBOARDING_CONFIG);
    const analystOnboardingUrlFlag = flags.getValue(BLINK_FLAGS.enableAnalystOnboarding.name);
    if (!analystOnboardingConfig ||
        !Object.prototype.hasOwnProperty.call(analystOnboardingConfig, jsonConstants.ANALYST_ONBOARDING_ENABLED)) {
        return analystOnboardingUrlFlag;
    }
    return !_.isUndefined(analystOnboardingUrlFlag)
        ? analystOnboardingUrlFlag
        : analystOnboardingConfig[jsonConstants.ANALYST_ONBOARDING_ENABLED];
};
export const getProductNameWrapper = () => {
    const emailConfig = getConfigInfo(jsonConstants.EMAIL_CONFIG);
    if (!emailConfig ||
        !Object.prototype.hasOwnProperty.call(emailConfig, jsonConstants.EMAIL_PRODUCT_NAME)) {
        return DEFAULT_PRODUCT_NAME;
    }
    return emailConfig[jsonConstants.EMAIL_PRODUCT_NAME];
};
export const getProductName = () => {
    const productName = getProductNameWrapper();
    return productName === DEFAULT_PRODUCT_NAME ? 'ThoughtSpot' : productName;
};
export const isOnlySingleCsvUploadAllowed = () => {
    const singleCsvBool = flags?.getValue(BLINK_FLAGS.onlySingleUploadAllowed.name);
    if (_.isNull(singleCsvBool)) {
        return getConfigInfo(jsonConstants.MAX_CSV_FILE_UPLOAD_COUNT) === 1;
    }
    return !!singleCsvBool;
};
export const isCsvUploadAllowed = () => {
    return getConfigInfo(jsonConstants.MAX_CSV_FILE_UPLOAD_COUNT) !== 0;
};
export const canUploadData = () => {
    return hasAdminPrivileges() || hasUserDataUploadPrivileges();
};
export const isEmbraceCsvUploadMode = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.embraceCsvUploadMode.name);
    if (urlFlagValue === 'true' || urlFlagValue === true) {
        return true;
    }
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    const csvUploadMode = embraceConfig[jsonConstants.CSV_UPLOAD_MODE];
    return csvUploadMode === CsvUploadMode.EMBRACE;
};
export const isFalconCsvUploadMode = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.embraceCsvUploadMode.name);
    if (urlFlagValue === 'true' || urlFlagValue === true) {
        return true;
    }
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    const csvUploadMode = embraceConfig[jsonConstants.CSV_UPLOAD_MODE];
    return csvUploadMode === CsvUploadMode.FALCON;
};
export const getUploadedCsvId = () => {
    const sessionInfoObject = getSessionInfo();
    const csvTables = sessionInfoObject[jsonConstants.CSV_TABLES];
    if (!sessionInfoObject[jsonConstants.CSV_TABLES]) {
        return null;
    }
    return csvTables[csvTables.length - 1];
};
export const setHasSeenOnboardingFlag = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.ONBOARDING_SEEN] = true;
    }
    flags.setValue(BLINK_FLAGS.hasSeenOnboarding.name, true);
};
export const hasSeenOnboarding = () => {
    const hasSeenOnboardingFlag = flags?.getValue(BLINK_FLAGS.hasSeenOnboarding.name);
    if (!_.isUndefined(hasSeenOnboardingFlag)) {
        return hasSeenOnboardingFlag;
    }
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.ONBOARDING_SEEN)
        ? onboardingActivity[jsonConstants.ONBOARDING_SEEN]
        : true;
};
export const setHasSeenWorksheetTour = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.WORKSHEET_TOUR_SEEN] = true;
    }
    flags.setValue(BLINK_FLAGS.hasSeenWorksheetTour.name, true);
};
export const hasSeenWorksheetTour = () => {
    const hasSeenWorksheetTourFlag = flags?.getValue(BLINK_FLAGS.hasSeenWorksheetTour.name);
    if (!_.isUndefined(hasSeenWorksheetTourFlag)) {
        return hasSeenWorksheetTourFlag;
    }
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.WORKSHEET_TOUR_SEEN)
        ? onboardingActivity[jsonConstants.WORKSHEET_TOUR_SEEN]
        : true;
};
export const hasSeenAutoAnswerTour = () => {
    const hasSeenAutoAnswerTourFlag = flags?.getValue(BLINK_FLAGS.hasSeenAutoAnswerTour.name);
    if (!_.isUndefined(hasSeenAutoAnswerTourFlag)) {
        return hasSeenAutoAnswerTourFlag;
    }
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.AUTO_ANSWER_TOUR_SEEN)
        ? onboardingActivity[jsonConstants.AUTO_ANSWER_TOUR_SEEN]
        : true;
};
export const setHasSeenAutoAnswerTour = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.AUTO_ANSWER_TOUR_SEEN] = true;
    }
    flags.setValue(BLINK_FLAGS.hasSeenAutoAnswerTour.name, true);
};
export const isScriptabilityEnabled = () => {
    const blinkScriptabilityFlag = flags?.getValue(BLINK_FLAGS.scriptability.name);
    const callosumScriptabilityFlag = getConfigInfo(jsonConstants.SCRIPTABILITY_CONFIG);
    return (blinkScriptabilityFlag ||
        (callosumScriptabilityFlag && callosumScriptabilityFlag.enabled));
};
export const isTableIndexingModalEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.tableIndexingModalEnabled.name);
    if (urlFlag !== undefined) {
        return !!urlFlag;
    }
    const tableIndexingConfig = getConfigInfo(jsonConstants.TABLE_INDEXING_CONFIG);
    return !!(tableIndexingConfig && tableIndexingConfig.indexingModalEnabled);
};
export const setIsAnalystOnboardingCompleted = (analystOnboardingComplete) => {
    setExposedUserPreference(SESSION_USER_PREFERENCES.ANALYST_ONBOARDING_COMPLETE, analystOnboardingComplete);
};
export const isOnboardingEnabled = () => {
    const onboardingConfig = getConfigInfo(jsonConstants.ONBOARDING_CONFIG);
    const onboardingFlag = flags?.getValue(BLINK_FLAGS.showOnboarding.name);
    if (!_.isUndefined(onboardingFlag)) {
        return onboardingFlag;
    }
    if (onboardingConfig &&
        Object.prototype.hasOwnProperty.call(onboardingConfig, jsonConstants.ONBOARDING_ENABLED)) {
        return onboardingConfig[jsonConstants.ONBOARDING_ENABLED];
    }
    return true;
};
export const getVizRenderingQueueTimeout = () => {
    const vizRenderingQueueTimeoutMS = flags?.getValue(BLINK_FLAGS.vizRenderingQueueTimeoutMS.name);
    if (vizRenderingQueueTimeoutMS) {
        return vizRenderingQueueTimeoutMS;
    }
    return (getBackendConfig() || {})[jsonConstants.VIZ_RENDERING_QUEUE_TIMEOUT_MS];
};
export const getOptimizelyDataFileFetchInterval = () => {
    let optimizelyDataFileFetchInterval = flags?.getValue(BLINK_FLAGS.optimizelyDataFileFetchInterval.name);
    if (!_.isUndefined(optimizelyDataFileFetchInterval)) {
        return optimizelyDataFileFetchInterval;
    }
    optimizelyDataFileFetchInterval = getConfigInfo(jsonConstants.optimizely.OPTIMIZELY_DATA_FILE_FETCH_INTERVAL);
    return (optimizelyDataFileFetchInterval || OPTIMIZELY_DATAFILE_FETCH_INTERVAL);
};
export const setHasSeenPinsFlowFlag = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.PINS_FLOW_SEEN] = true;
    }
    flags.setValue(BLINK_FLAGS.hasSeenPinsFlow.name, true);
};
export const hasSeenPinsFlow = () => {
    const hasSeenPinsFlowFlag = flags?.getValue(BLINK_FLAGS.hasSeenPinsFlow.name);
    if (!_.isUndefined(hasSeenPinsFlowFlag)) {
        return hasSeenPinsFlowFlag;
    }
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.PINS_FLOW_SEEN)
        ? onboardingActivity[jsonConstants.PINS_FLOW_SEEN]
        : true;
};
export const setHasSeenPinsSpotlightFlag = () => {
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    if (onboardingActivity) {
        onboardingActivity[jsonConstants.PINS_SPOTLIGHT_SEEN] = true;
    }
    flags.setValue(BLINK_FLAGS.hasSeenPinsSpotlight.name, true);
};
export const hasSeenPinsSpotlight = () => {
    const hasSeenPinsSpotlightFlag = flags?.getValue(BLINK_FLAGS.hasSeenPinsSpotlight.name);
    if (!_.isUndefined(hasSeenPinsSpotlightFlag)) {
        return hasSeenPinsSpotlightFlag;
    }
    const sessionInfoObject = getSessionInfo();
    const onboardingActivity = sessionInfoObject
        ? sessionInfoObject[jsonConstants.ONBOARDING_ACTIVITY]
        : null;
    return !!onboardingActivity &&
        Object.prototype.hasOwnProperty.call(onboardingActivity, jsonConstants.PINS_SPOTLIGHT_SEEN)
        ? onboardingActivity[jsonConstants.PINS_SPOTLIGHT_SEEN]
        : true;
};
export const getPasswordStrategy = () => {
    const loginInfoConfig = getConfigInfo(jsonConstants.LOGIN_CONFIG);
    if (!loginInfoConfig || _.isEmpty(loginInfoConfig)) {
        return PasswordStrategy.GUESS_ESTIMATE;
    }
    const passwordConfig = loginInfoConfig[jsonConstants.PASSWORD_CONFIG];
    if (!passwordConfig || _.isEmpty(passwordConfig)) {
        return PasswordStrategy.GUESS_ESTIMATE;
    }
    return (passwordConfig[jsonConstants.PASSWORD_STRATEGY_KEY] ||
        PasswordStrategy.GUESS_ESTIMATE);
};
export const getBlackListedWordsForPassword = () => {
    const loginInfoConfig = getConfigInfo(jsonConstants.LOGIN_CONFIG);
    if (!loginInfoConfig || _.isEmpty(loginInfoConfig)) {
        return [];
    }
    const passwordConfig = loginInfoConfig[jsonConstants.PASSWORD_CONFIG];
    if (!passwordConfig || _.isEmpty(passwordConfig)) {
        return [];
    }
    const blacklistedWords = passwordConfig[jsonConstants.BLACK_LISTED_WORDS];
    if (!blacklistedWords) {
        return [];
    }
    return blacklistedWords;
};
export const isAnswerUndoStackEnabled = () => {
    const clientFlagValue = flags?.getValue(BLINK_FLAGS.isAnswerUndoStackEnabled.name);
    const sessionFlagValue = getConfigInfo(jsonConstants.IS_ANSWER_UNDO_STACK_ENABLED);
    return _.isUndefined(clientFlagValue)
        ? !!sessionFlagValue
        : clientFlagValue;
};
export const areMapTilesEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_MAP_TILES_CONFIG_KEY);
};
export const getGeoTileSourceId = () => {
    const geoTileSourceId = flags?.getValue(BLINK_FLAGS.geoTileSourceId.name);
    return geoTileSourceId || getConfigInfo(jsonConstants.GEO_TILE_SOURCE_ID);
};
export const getMapboxAccessToken = () => {
    const mapboxAccessToken = flags?.getValue(BLINK_FLAGS.mapboxAccessToken.name);
    return (mapboxAccessToken || getConfigInfo(jsonConstants.MAPBOX_ACCESS_TOKEN));
};
export const getGeoTileType = () => {
    const geoTileType = flags?.getValue(BLINK_FLAGS.geoTileType.name);
    return geoTileType || getConfigInfo(jsonConstants.GEO_TILE_TYPE);
};
export const shouldAddNullFilterSuggestion = () => {
    const clientFlagValue = flags?.getValue(BLINK_FLAGS.addNullFilterSuggestion.name);
    return _.isUndefined(clientFlagValue)
        ? getConfigInfo(BLINK_FLAGS.addNullFilterSuggestion.name)
        : clientFlagValue;
};
export const getSearchAssistDataSource = () => {
    return (getPreference(blinkConstants.preferenceKeys.SEARCH_ASSIST_DATA_SOURCE) || []);
};
export const setSearchAssistDataSource = dataSource => {
    setPreference(blinkConstants.preferenceKeys.SEARCH_ASSIST_DATA_SOURCE, dataSource, !isReactshellEnabled());
};
export const isAutoAnswerEnabled = () => {
    const autoAnswerEnabledFLag = flags?.getValue(BLINK_FLAGS.isAutoAnswerEnabled.name);
    if (!_.isEmpty(autoAnswerEnabledFLag)) {
        return autoAnswerEnabledFLag;
    }
    const autogenConfig = getConfigInfo(jsonConstants.AUTOGEN_CONFIG);
    const autoAnswerEnabled = autogenConfig[jsonConstants.ANSWER_GEN][jsonConstants.ANSWERGEN_ENABLED];
    return !!autoAnswerEnabled;
};
export const getHelpConfig = () => {
    return getConfigInfo(jsonConstants.HELP_CONFIG);
};
export const getSupportConfigItems = () => {
    return getConfigInfo(jsonConstants.SUPPORT_CONFIG_ITEMS);
};
export function getEmbedUrls() {
    const configInfo = getBackendConfig();
    return _.get(configInfo, jsonConstants.EMBED_URLS_KEY, null);
}
export function getCurrentOrgEmbedUrls() {
    const info = getSessionInfo();
    return _.get(info, jsonConstants.CURRENT_ORG_EMBED_URLS_KEY, null);
}
export function isCustomEmbedUrlEnabled() {
    if (flags?.getValue(BLINK_FLAGS.isCustomEmbedUrlEnabled.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.isCustomEmbedUrlEnabled.name);
    }
    const configInfo = getBackendConfig();
    return _.get(configInfo, jsonConstants.USE_CUSTOM_EMBED_URLS_KEY, false);
}
export const getPanelExpandedState = () => {
    return getPreference(blinkConstants.preferenceKeys.PANEL_STATE);
};
export const setPanelStateInPreference = (panelState) => {
    setPreference(blinkConstants.preferenceKeys.PANEL_STATE, panelState, !isReactshellEnabled());
};
export const isTseScheduleFeatureEnabled = () => {
    const configInfo = getBackendConfig();
    const envFlag = flags?.getValue(BLINK_FLAGS.tseScheduleFeatureEnabled.name);
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IS_TSE_SCHEDULER_FEATURE_ENABLED)) {
        return false;
    }
    return (configInfo[jsonConstants.IS_TSE_SCHEDULER_FEATURE_ENABLED] || envFlag);
};
export const isTseStyleCustomisationsV2Enabled = () => {
    const configInfo = getBackendConfig();
    const envFlag = flags?.getValue(BLINK_FLAGS.tseStyleCustomisationsV2Enabled.name);
    if (_.isUndefined(envFlag) &&
        (!configInfo ||
            !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IS_TSE_STYLE_CUSTOMISATIONS_V2_ENABLED))) {
        return false;
    }
    return (configInfo[jsonConstants.IS_TSE_STYLE_CUSTOMISATIONS_V2_ENABLED] ||
        envFlag);
};
export const isTseMultipleAppActionEnabled = () => {
    const configInfo = getBackendConfig();
    const envFlag = flags?.getValue(BLINK_FLAGS.tseMultipleAppActionEnabled.name);
    if (!configInfo) {
        return false;
    }
    return (configInfo[jsonConstants.IS_TSE_MULTIPLE_APP_ACTION_ENABLED] || envFlag);
};
export const getTseSchedulerClientId = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.TSE_SCHEDULER_SERVICE_CLIENT_ID)) {
        return '';
    }
    return configInfo[jsonConstants.TSE_SCHEDULER_SERVICE_CLIENT_ID];
};
export const isRestAPIV20PlaygroundEnabled = () => {
    const configInfo = getBackendConfig();
    const envFlag = flags?.getValue(BLINK_FLAGS.tseRestApiV20PlaygroundEnabled.name);
    if (!_.isUndefined(envFlag)) {
        return envFlag;
    }
    if (_.isUndefined(envFlag) &&
        (!configInfo ||
            !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IS_TSE_REST_API_2_0_PLAYGROUND_ENABLED))) {
        return false;
    }
    return configInfo[jsonConstants.IS_TSE_REST_API_2_0_PLAYGROUND_ENABLED];
};
export const isTSEEnabled = () => {
    const isEverywhereEnabledFlag = flags?.getValue(BLINK_FLAGS.isEverywhereEnabled.name);
    if (!_.isUndefined(isEverywhereEnabledFlag)) {
        return isEverywhereEnabledFlag;
    }
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IS_EVERYWHERE_ENABLED)) {
        return false;
    }
    return configInfo[jsonConstants.IS_EVERYWHERE_ENABLED];
};
export const isEnableSelectiveUserAccessRestriction = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.ENABLE_SELECTIVE_USER_ACCESS_RESTRICTION)) {
        return false;
    }
    return configInfo[jsonConstants.ENABLE_SELECTIVE_USER_ACCESS_RESTRICTION];
};
export const isDarkCluster = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IS_DARK_CLUSTER)) {
        return false;
    }
    return configInfo[jsonConstants.IS_DARK_CLUSTER];
};
export const getCurrentOrgId = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject?.[jsonConstants.CURRENT_ORG_ID] || 0;
};
export const getCurrentOrgName = () => {
    const sessionInfoObject = getSessionInfo();
    return (sessionInfoObject?.[jsonConstants.CURRENT_ORG_NAME] ||
        jsonConstants.DEFAULT_ORG_NAME);
};
export const isEverywhereEnabled = () => {
    if (hasInfo()) {
        const currentOrgId = getCurrentOrgId().toString();
        const isOrgZero = ['0', '-1', '*'].includes(currentOrgId);
        const tseHideDevelopForOrgs = getConfigInfo(jsonConstants.TSE_HIDE_DEVELOP_FOR_ORGS);
        if (tseHideDevelopForOrgs && !isOrgZero) {
            return false;
        }
        return (!inIFrame() &&
            (flags.getValue(BLINK_FLAGS.embedV2.name) ||
                (isTSEEnabled() &&
                    (hasAdminPrivileges() || hasDeveloperPrivileges()))));
    }
    return false;
};
export const shouldCustomActionsBeVisible = () => {
    if (hasInfo()) {
        const currentOrgId = getCurrentOrgId().toString();
        const isOrgZero = ['0', '-1', '*'].includes(currentOrgId);
        const tseHideDevelopForOrgs = getConfigInfo(jsonConstants.TSE_HIDE_DEVELOP_FOR_ORGS);
        if (tseHideDevelopForOrgs && !isOrgZero) {
            return false;
        }
        return true;
    }
    return false;
};
export const isOrgsHiddenForDevelopTab = () => {
    const tseHideDevelopForOrgs = getConfigInfo(jsonConstants.TSE_HIDE_DEVELOP_FOR_ORGS);
    return !!tseHideDevelopForOrgs;
};
export const isSageEmbedPlaygroundEnabled = () => {
    const sageEmbedPlaygroundEnabled = getBackendConfig()
        .sageEmbedPlaygroundEnabled;
    return !!sageEmbedPlaygroundEnabled;
};
export const isGraphQLPlaygroundEnabled = () => {
    const enableGraphqlV2 = getBackendConfig().graphqlV2Enabled;
    return !!enableGraphqlV2;
};
export const isEverywhereSettingLocked = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IS_EVERYWHERE_SETTING_LOCKED)) {
        return true;
    }
    return configInfo[jsonConstants.IS_EVERYWHERE_SETTING_LOCKED];
};
export const enableMinimumBarHeight = () => {
    const minimumBarHeightFlag = flags?.getValue(BLINK_FLAGS.enableMinimumBarHeight.name);
    if (!_.isUndefined(minimumBarHeightFlag)) {
        if (minimumBarHeightFlag === 'true' || minimumBarHeightFlag === true)
            return true;
        return false;
    }
    const enableMinimumBarHeightConfig = getConfigInfo(jsonConstants.ENABLE_MINIMUM_BAR_HEIGHT);
    return !!enableMinimumBarHeightConfig;
};
export const isHandleMissingValuesEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.handleMissingValues.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return getConfigInfo(jsonConstants.HANDLE_MISSING_VALUES);
};
export const enableGridlines = () => {
    const gridlinesEnabled = flags?.getValue(BLINK_FLAGS.enableGridlines.name);
    if (!_.isUndefined(gridlinesEnabled))
        return gridlinesEnabled;
    const enableGridlinesConfig = getConfigInfo(jsonConstants.ENABLE_GRIDLINES);
    return !!enableGridlinesConfig;
};
export const showChartApplyandResetButtons = () => {
    const showApplyandResetButtons = flags?.getValue(BLINK_FLAGS.showChartApplyandResetButtons.name);
    return (showApplyandResetButtons ??
        getConfigInfo(jsonConstants.SHOW_CHART_APPLY_AND_RESET_BUTTONS));
};
export const applyContrastForDataLabels = () => {
    const applyContrastSettings = flags?.getValue(BLINK_FLAGS.applyContrastForDataLabels.name);
    return (applyContrastSettings ??
        getConfigInfo(jsonConstants.APPLY_CONTRAST_FOR_DATALABELS));
};
export const enableAttributesInPivotDataArea = () => {
    const enableAttributesInPivotDataAreaValue = flags?.getValue(BLINK_FLAGS.enableAttributesInPivotDataArea.name);
    return (enableAttributesInPivotDataAreaValue ??
        getConfigInfo(jsonConstants.ENABLE_ATTRIBUTES_IN_PIVOT_DATA_AREA));
};
export const getClusterType = () => {
    const href = window?.location?.href || '';
    return isNebulaDevCluster() || href.includes('thoughtspotdev.cloud')
        ? CLUSTER_TYPES.DEV
        : CLUSTER_TYPES.PROD;
};
export const isNotAProductionCluster = () => {
    const href = window?.location?.href || '';
    return (isNebulaDevCluster() ||
        href.includes('thoughtspotdev.cloud') ||
        href.includes('thoughtspotstaging.cloud') ||
        [CLUSTER_TYPES.DEV, CLUSTER_TYPES.STAGING].includes(getConfigInfo(jsonConstants.CLUSTER_TYPE)));
};
export const getPublicUserName = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !configInfo[jsonConstants.IS_PUBLIC_USER] ||
        !configInfo[jsonConstants.PUBLIC_USER_NAME]) {
        return null;
    }
    return configInfo[jsonConstants.PUBLIC_USER_NAME];
};
export const getAccessToken = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject ? sessionInfoObject.accessToken : '';
};
export const hideDocsInDevelop = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.DOC_HIDDEN_DEVELOP_TAB)) {
        return false;
    }
    return configInfo[jsonConstants.DOC_HIDDEN_DEVELOP_TAB];
};
export const enableRestApiPlayground = () => {
    const apiPlaygroundconfigInfo = getConfigInfo(jsonConstants.API_PLAYGROUND_CONFIG);
    if (!apiPlaygroundconfigInfo ||
        !Object.prototype.hasOwnProperty.call(apiPlaygroundconfigInfo, jsonConstants.API_PLAYGROUND_ENABLED)) {
        return false;
    }
    return apiPlaygroundconfigInfo[jsonConstants.API_PLAYGROUND_ENABLED];
};
export const getEmbedActionVersion = () => {
    const backendConfig = getBackendConfig();
    const { atlasActionObjectUGFeatureEnabled, atlasActionObjectFeatureEnabled, } = backendConfig;
    if (atlasActionObjectUGFeatureEnabled && atlasActionObjectFeatureEnabled) {
        return 'v4';
    }
    if (!atlasActionObjectUGFeatureEnabled && atlasActionObjectFeatureEnabled) {
        return 'v3';
    }
    return 'v2';
};
export const isDataflowEnabled = () => {
    const sessionInfoObject = getSessionInfo();
    const isEnabled = sessionInfoObject[jsonConstants.ISDATAFLOWENABALED];
    return !!isEnabled;
};
export const getGeoDataBatchSize = () => {
    const geoConfig = getConfigInfo(jsonConstants.GEO_MAP_CONFIG_KEY);
    return geoConfig && geoConfig[jsonConstants.DATA_BATCH_SIZE_KEY];
};
export const isCustomCalendarEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.ENABLE_CUSTOM_CAL_DATABASE_LIST].length !==
            0);
};
export const isEmbraceEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.isEmbraceEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.isEmbraceEnabled.name);
    }
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return embraceConfig && embraceConfig[jsonConstants.TEAMS_ENABLED];
};
export const isEmbraceOnlyMode = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.DATABASE_MODE] === DataBaseMode.EMBRACE);
};
export const isSchedulingJobEnabled = () => {
    const schedulerConfig = getConfigInfo(jsonConstants.SCHEDULER_CONFIG);
    return schedulerConfig
        ? schedulerConfig[jsonConstants.SCHEDULE_REPORTING_ENABLED]
        : false;
};
export const areDatabasesAvailableForCSVUpload = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.DATABASES_WITH_CSV_UPLOAD_ENABLED]
            .length !== 0);
};
export const isEmbraceCsvUploadEnabled = () => {
    return (getDataBaseMode() !== DataBaseMode.FALCON &&
        areDatabasesAvailableForCSVUpload() &&
        isEmbraceCsvUploadMode());
};
export const canRunSQLVQueryForConnection = (connectionType) => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.EXCLUDE_CUSTOM_SQL_DATABASES_LIST].includes(connectionType));
};
export const isOAuthEnabledForConnection = (connectionType) => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.DATABASES_WITH_OAUTH_ENABLED_LIST].includes(connectionType));
};
export const isExternalOAuthEnabledForConnection = (connectionType) => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.DATABASES_WITH_EXTERNAL_OAUTH_ENABLED_LIST].includes(connectionType));
};
export const isCSVUploadEnabledForConnection = (connectionType) => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.DATABASES_WITH_CSV_UPLOAD_ENABLED].includes(connectionType));
};
export const isMultiTenantCalendarConfigEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return !!embraceConfig?.[jsonConstants.IS_MULTI_TENANT_CALENDAR_CONFIG_ENABLED];
};
export const enableConnectionConfigList = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    const connectionConfigurationEnabledCDWs = embraceConfig?.[jsonConstants.CONNECTION_CONFIGURATION_ENABLED_CDW];
    return connectionConfigurationEnabledCDWs;
};
export const getConnectorsWithOptionalDatabaseEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig?.[jsonConstants.CONNECTORS_WITH_OPTIONAL_DATABASE_ENABLED] ?? []);
};
export const getSearchCacheEnabledDatasources = () => {
    const searchCacheEnabledDatasources = flags?.getValue(BLINK_FLAGS.searchCacheEnabledDatasources.name);
    if (!_.isUndefined(searchCacheEnabledDatasources)) {
        return searchCacheEnabledDatasources;
    }
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.SEARCH_CACHE_ENABLED_DATASOURCES]);
};
export const getBooleanSessionFlag = (flagName) => {
    if (BLINK_FLAGS[flagName]) {
        const envFlag = flags?.getValue(BLINK_FLAGS[flagName].name);
        if (envFlag !== undefined) {
            return envFlag;
        }
    }
    return getConfigInfo(flagName);
};
export const getStringSessionFlag = (flagName) => {
    if (BLINK_FLAGS[flagName]) {
        const envFlag = flags?.getValue(BLINK_FLAGS[flagName].name);
        if (envFlag !== undefined) {
            return envFlag;
        }
    }
    return getConfigInfo(flagName);
};
export const getNumberSessionFlag = (flagName) => {
    if (BLINK_FLAGS[flagName]) {
        const envFlag = flags?.getValue(BLINK_FLAGS[flagName].name);
        if (envFlag !== undefined) {
            return envFlag;
        }
    }
    return getConfigInfo(flagName);
};
export const isTSBrandingDisabledInPdf = () => {
    const disableTSBrandingInPDF = flags?.getValue(BLINK_FLAGS.disableTSBrandingInPDF.name);
    if (!_.isUndefined(disableTSBrandingInPDF)) {
        return disableTSBrandingInPDF;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    return !!exportConfig && exportConfig[DISABLE_TS_BRANDING_IN_PDF_KEY];
};
export const getLegalDisclaimerText = () => {
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    return exportConfig && exportConfig[jsonConstants.LEGAL_DISCLAIMER_TEXT];
};
export const getRemoveAttachmentFromScheduledEmails = () => {
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    return (exportConfig &&
        exportConfig[jsonConstants.REMOVE_ATTACHMENT_FROM_SCHEDULED_EMAILS]);
};
export const getTablePrintDataBatchSize = () => {
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    if (!srConfig || !srConfig[jsonConstants.TABLE_PRINT_DATA_BATCH_SIZE]) {
        return TABLE_PRINT_DEFAULT_BATCH_SIZE;
    }
    return Number(srConfig[jsonConstants.TABLE_PRINT_DATA_BATCH_SIZE]);
};
export const getIsXlsxScheduleEnabled = () => {
    const srConfig = getConfigInfo(jsonConstants.SCHEDULE_REPORTING_CONFIG);
    return srConfig && srConfig[jsonConstants.IS_XLSX_SCHEDULE_ENABLED];
};
export const isSourceToggleEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return embraceConfig && embraceConfig[jsonConstants.SOURCE_TOGGLE_ENABLED];
};
export const getOnboardingVideoURL = () => {
    const onboardingConfig = getConfigInfo(jsonConstants.ONBOARDING_CONFIG);
    if (!onboardingConfig ||
        !Object.prototype.hasOwnProperty.call(onboardingConfig, jsonConstants.ONBOARDING_VIDEO_URL)) {
        return DEFAULT_ONBOARDING_VIDEO_URL;
    }
    return onboardingConfig[jsonConstants.ONBOARDING_VIDEO_URL].length === 0
        ? DEFAULT_ONBOARDING_VIDEO_URL
        : onboardingConfig[jsonConstants.ONBOARDING_VIDEO_URL];
};
export const getAnalystOnboardingVideoUrl = () => {
    const analystOnboardingConfig = getConfigInfo(jsonConstants.ANALYST_ONBOARDING_CONFIG);
    if (!analystOnboardingConfig ||
        !Object.prototype.hasOwnProperty.call(analystOnboardingConfig, jsonConstants.ANALYST_ONBOARDING_VIDEO)) {
        return DEFAULT_ONBOARDING_VIDEO_URL;
    }
    return analystOnboardingConfig[jsonConstants.ANALYST_ONBOARDING_VIDEO];
};
export const isCloudBusinessOnboardingEnabled = () => {
    const onboardingConfig = getConfigInfo(jsonConstants.ONBOARDING_CONFIG);
    const onboardingFlag = flags?.getValue(BLINK_FLAGS.showCloudBusinessOnboarding.name);
    if (!_.isUndefined(onboardingFlag)) {
        return onboardingFlag;
    }
    if (onboardingConfig &&
        Object.prototype.hasOwnProperty.call(onboardingConfig, jsonConstants.CLOUD_BUSINESS_ONBOARDING_ENABLED)) {
        return onboardingConfig[jsonConstants.CLOUD_BUSINESS_ONBOARDING_ENABLED];
    }
    return false;
};
export const getDemoWorsheetGuids = () => {
    const demoConfig = getConfigInfo(jsonConstants.DEMO_DATA_CONFIG);
    const demoWorksheetGuidUrlFlag = flags?.getValue(BLINK_FLAGS.demoWorksheetGuids.name);
    if (demoWorksheetGuidUrlFlag) {
        return [demoWorksheetGuidUrlFlag];
    }
    if (!!demoConfig &&
        Object.prototype.hasOwnProperty.call(demoConfig, jsonConstants.DEMO_WORKSHEET_GUIDS)) {
        return demoConfig[jsonConstants.DEMO_WORKSHEET_GUIDS];
    }
    return [];
};
export const isSearchAssistEnabled = () => {
    const searchAssistEnabled = getConfigInfo(jsonConstants.SEARCH_ASSIST_ENABLED);
    const searchAssistFlag = flags?.getValue(BLINK_FLAGS.searchAssist.name);
    return !_.isUndefined(searchAssistFlag)
        ? searchAssistFlag
        : !!searchAssistEnabled;
};
export const isShowUpgradeOptionsEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.highlightProFeatures.name))) {
        return flags?.getValue(BLINK_FLAGS.highlightProFeatures.name);
    }
    const showUpgradeOptions = getConfigInfo(jsonConstants.SHOW_UPGRADE_OPTIONS);
    return !!showUpgradeOptions;
};
export const autoGenerateSynonymsOnSave = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.autoGenerateSynonymsOnSave.name))) {
        return flags?.getValue(BLINK_FLAGS.autoGenerateSynonymsOnSave.name);
    }
    return getConfigInfo(jsonConstants.AUTO_GENERATE_SYNONYMS_ON_SAVE);
};
export const isSynonymGenerationEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.synonymGenerationEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.synonymGenerationEnabled.name);
    }
    return getConfigInfo(jsonConstants.SYNONYM_GENERATION_ENABLED);
};
export const shouldAllowCreatingJoinFromWorksheetToUploadedTable = () => {
    const backendFlagValue = getConfigInfo(jsonConstants.ALLOW_CREATING_WORKSHEET_TO_UPLOADED_TABLE_JOINS);
    return _.isNil(backendFlagValue)
        ? flags?.getValue(BLINK_FLAGS.allowCreatingWSToUploadedTableJoins.name)
        : backendFlagValue;
};
export const worksheetV1AndV2Enabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.createV1AndV2Worksheet.name))) {
        return flags?.getValue(BLINK_FLAGS.createV1AndV2Worksheet.name);
    }
    return getConfigInfo(jsonConstants.WORKSHEET_V1_AND_V2_ENABLED) || false;
};
export const worksheetV2DefaultEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.worksheetV2DefaultEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.worksheetV2DefaultEnabled.name);
    }
    return getConfigInfo(jsonConstants.WORKSHEET_V2_DEFAULT) || false;
};
export const columnGeoRestrictions = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.columnGeoRestrictions.name))) {
        return flags?.getValue(BLINK_FLAGS.columnGeoRestrictions.name);
    }
    return getConfigInfo(jsonConstants.COLUMN_GEO_RESTRICTIONS) || false;
};
export const convertToModel = () => {
    const convertToModelFlag = flags?.getValue(BLINK_FLAGS.convertToModel.name);
    if (!_.isUndefined(convertToModelFlag)) {
        return convertToModelFlag;
    }
    return !!getConfigInfo(jsonConstants.CONVERT_TO_MODEL);
};
export const disableWorksheetEditFlow = () => {
    return !!getConfigInfo(jsonConstants.DISABLE_EDIT_WORKSHEET_FLOW);
};
export const syncIndexSettingWithSource = () => {
    const syncIndexSettingWithSource = flags?.getValue(BLINK_FLAGS.syncIndexSettingWithSource.name);
    if (!_.isUndefined(syncIndexSettingWithSource)) {
        return syncIndexSettingWithSource;
    }
    return !!getConfigInfo(jsonConstants.SYNCINDEX_SETTING_WITH_SOURCE);
};
export const enableV2KnowledgeCard = () => {
    const enableV2KnowledgeCardFlag = flags?.getValue(BLINK_FLAGS.enableV2KnowledgeCard.name);
    if (!_.isUndefined(enableV2KnowledgeCardFlag)) {
        return enableV2KnowledgeCardFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_V2_KNOWLEDGE_CARD);
};
export const enableSlackDisconnectedBanner = () => {
    const enableSlackDisconnectedBanner = flags?.getValue(BLINK_FLAGS.enableSlackDisconnectedBanner.name);
    if (!_.isUndefined(enableSlackDisconnectedBanner)) {
        return enableSlackDisconnectedBanner;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_SLACK_DISCONNECTED_BANNER);
};
export const dropQuestionOnWorksheetPage = () => {
    const dropQuestionOnWorksheetPageFlag = flags?.getValue(BLINK_FLAGS.dropQuestionOnWorksheetPage.name);
    if (!_.isUndefined(dropQuestionOnWorksheetPageFlag)) {
        return dropQuestionOnWorksheetPageFlag;
    }
    return !!getConfigInfo(jsonConstants.DROP_QUESTION_ON_WORKSHEET_PAGE);
};
export const tmlTableRemappingEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.tmlTableRemappingEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.tmlTableRemappingEnabled.name);
    }
    return getConfigInfo(jsonConstants.TML_TABLE_REMAPPING_ENABLED) || false;
};
export const isTmlUtilityV2Enabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.isTmlUtilityV2Enabled.name))) {
        return flags?.getValue(BLINK_FLAGS.isTmlUtilityV2Enabled.name);
    }
    return getConfigInfo(jsonConstants.TML_UTILITY_V2_ENABLED) || false;
};
export const isMetricsFloaterEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.isMetricsFloaterEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.isMetricsFloaterEnabled.name);
    }
    return getConfigInfo(jsonConstants.METRICS_FLOATER_ENABLED) || false;
};
export const enableCacheCleanup = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.enableCacheCleanup.name))) {
        return flags?.getValue(BLINK_FLAGS.enableCacheCleanup.name);
    }
    return getConfigInfo(jsonConstants.ENABLE_CACHE_CLEANUP) || false;
};
export const connectionTmlEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.connectionTMLSupportEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.connectionTMLSupportEnabled.name);
    }
    return getConfigInfo(jsonConstants.CONNECTION_TML_ENABLED) || false;
};
export const collectionTmlImportExportEnabled = () => {
    return (getConfigInfo(jsonConstants.COLLECTION_TML_IMPORT_EXPORT_ENABLED) ||
        false);
};
export const enableJoinRecommendations = () => {
    const enableJoinRecommendationFlag = flags?.getValue(BLINK_FLAGS.enableJoinRecommendations.name);
    if (!_.isUndefined(enableJoinRecommendationFlag)) {
        return enableJoinRecommendationFlag;
    }
    return !!getConfigInfo(jsonConstants.WORKSHEET_JOIN_RECOMMENDATION_ENABLE);
};
export const enableReactWorksheet = () => {
    const enableJoinRecommendationFlag = flags?.getValue(BLINK_FLAGS.enableReactWorksheet.name);
    if (!_.isUndefined(enableJoinRecommendationFlag)) {
        return enableJoinRecommendationFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_REACT_WORKSHEET);
};
export const enableMetadataDetailsInReact = () => {
    const enableMetadataDetailsInReactFlag = flags?.getValue(BLINK_FLAGS.enableMetadataDetailsInReact.name);
    if (!_.isUndefined(enableMetadataDetailsInReactFlag)) {
        return enableMetadataDetailsInReactFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_REACT_METADATA_DETAILS);
};
export const isTmlObjectIdEnabled = () => {
    const scriptabilityConfig = getConfigInfo(jsonConstants.SCRIPTABILITY_CONFIG) || {};
    const customObjectIdConfiguration = getConfigInfo(jsonConstants.CUSTOM_OBJECTID_CONFIGURATION) || {};
    const tmlObjectIdEnabled = scriptabilityConfig.tmlObjectIdEnabled;
    const customObjectIdConfigurationEnabled = customObjectIdConfiguration.enabled;
    return tmlObjectIdEnabled && customObjectIdConfigurationEnabled;
};
export const enableColumnAlias = () => {
    const enableColumnAliasFlag = flags?.getValue(BLINK_FLAGS.enableColumnAlias.name);
    if (!_.isUndefined(enableColumnAliasFlag)) {
        return enableColumnAliasFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_COLUMN_ALIAS);
};
export const enableColumnAliasUdf = () => {
    const enableColumnAliasUdfFlag = flags?.getValue(BLINK_FLAGS.enableColumnAliasUdf.name);
    if (!_.isUndefined(enableColumnAliasUdfFlag)) {
        return enableColumnAliasUdfFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_COLUMN_ALIAS_UDF);
};
export const enableV2ModelShare = () => {
    const enableV2ModelShareFlag = flags?.getValue(BLINK_FLAGS.enableV2ModelShare.name);
    if (!_.isUndefined(enableV2ModelShareFlag)) {
        return enableV2ModelShareFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_V2_MODEL_SHARE);
};
export const syncWithSource = () => {
    const syncWithSourceFlag = flags?.getValue(BLINK_FLAGS.syncWithSource.name);
    if (!_.isUndefined(syncWithSourceFlag)) {
        return syncWithSourceFlag;
    }
    return !!getConfigInfo(jsonConstants.SYNC_WITH_SOURCE);
};
export const enableNonEquiJoinInUI = () => {
    const enableNonEquiJoinInUIFlag = flags?.getValue(BLINK_FLAGS.enableNonEquiJoinInUI.name);
    if (!_.isUndefined(enableNonEquiJoinInUIFlag)) {
        return enableNonEquiJoinInUIFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_NON_EQUI_JOIN_IN_UI);
};
export const enableAiModeling = () => {
    const enableAiModelingFlag = flags?.getValue(BLINK_FLAGS.enableAiModeling.name);
    if (!_.isUndefined(enableAiModelingFlag)) {
        return enableAiModelingFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_AI_MODELING);
};
export const isSpotterModelSectionHiddenInAdminPortal = () => {
    return !!getConfigInfo(jsonConstants.IS_SPOTTER_MODEL_SECTION_HIDDEN_IN_ADMIN_PORTAL);
};
export const isLumosBaseAgentEnabled = () => {
    const lumosConfig = getConfigInfo(jsonConstants.LUMOS_CONFIG) || {};
    return !!lumosConfig.enableBaseAgent;
};
export const isLumosStreamingEnabled = () => {
    const lumosConfig = getConfigInfo(jsonConstants.LUMOS_CONFIG) || {};
    return !!lumosConfig.enableStreaming;
};
export const isLumosEnabledInModelEdit = () => {
    const lumosConfig = getConfigInfo(jsonConstants.LUMOS_CONFIG) || {};
    return !!lumosConfig.enableModelEdit;
};
export const isLumosAiReadinessEnabled = () => {
    const lumosConfig = getConfigInfo(jsonConstants.LUMOS_CONFIG) || {};
    return !!lumosConfig.enableAiReadiness;
};
export const isLumosFormulaEnabled = () => {
    const lumosConfig = getConfigInfo(jsonConstants.LUMOS_CONFIG) || {};
    return !!lumosConfig.enableFormula;
};
export const enableColumnDependencyDelete = () => {
    const enableColumnDependencyDeleteFlag = flags?.getValue(BLINK_FLAGS.enableColumnDependencyDeletion.name);
    if (!_.isUndefined(enableColumnDependencyDeleteFlag)) {
        return enableColumnDependencyDeleteFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_COLUMN_DEPENDENCY_DELETE);
};
export const schemaDetailsJoinInReact = () => {
    const schemaDetailsJoinInReactFlag = flags?.getValue(BLINK_FLAGS.schemaDetailsJoinInReact.name);
    if (!_.isUndefined(schemaDetailsJoinInReactFlag)) {
        return schemaDetailsJoinInReactFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_REACT_SCHEMA_DETAIL_JOINS);
};
export const v1Disabled = () => {
    return true;
};
export const enableReactSchemaViewer = () => {
    const enableReactSchemaViewerFlag = flags?.getValue(BLINK_FLAGS.enableReactSchemaViewer.name);
    if (!_.isUndefined(enableReactSchemaViewerFlag)) {
        return enableReactSchemaViewerFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_REACT_SCHEMA_VIEWER);
};
export const enableColumnsCsvImportExport = () => {
    const enableColumnsCsvImportExportFlag = flags?.getValue(BLINK_FLAGS.enableColumnsCsvImportExport.name);
    if (!_.isUndefined(enableColumnsCsvImportExportFlag)) {
        return enableColumnsCsvImportExportFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_COLUMNS_CSV_IMPORT_EXPORT);
};
export const joinAllowedWithCompatibleDataType = () => {
    const joinAllowedWithCompatibleDataType = flags?.getValue(BLINK_FLAGS.joinAllowedWithCompatibleDataType.name);
    if (!_.isUndefined(joinAllowedWithCompatibleDataType)) {
        return joinAllowedWithCompatibleDataType;
    }
    return !!getConfigInfo(jsonConstants.JOIN_ALLOWED_WITH_COMPATIBLE_DATA_TYPE);
};
export const enableProfileTab = () => {
    const enableProfileTabFlag = flags?.getValue(BLINK_FLAGS.enableProfileInTable.name);
    if (!_.isUndefined(enableProfileTabFlag)) {
        return enableProfileTabFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_PROFILE_TAB);
};
export const canShowDataSampleTab = () => {
    const isAdminUser = hasAdminPrivileges();
    const isSampleTabDisabled = getConfigInfo(jsonConstants.DISABLE_DATA_SAMPLE_TAB);
    if (isSampleTabDisabled && !isAdminUser) {
        return false;
    }
    return true;
};
export const supportDeletionWithDependents = () => {
    const supportDeletionWithDependents = flags?.getValue(BLINK_FLAGS.supportDeletionWithDependents.name);
    if (!_.isUndefined(supportDeletionWithDependents)) {
        return supportDeletionWithDependents;
    }
    return !!getConfigInfo(jsonConstants.SUPPORT_OBJECT_DELETION_WITH_DEPENDENTS);
};
export const showGenericJoinCreationModal = () => {
    const showGenericJoinCreationModal = flags?.getValue(BLINK_FLAGS.showGenericJoinCreationModal.name);
    if (!_.isUndefined(showGenericJoinCreationModal)) {
        return showGenericJoinCreationModal;
    }
    return !!getConfigInfo(jsonConstants.SHOW_GENERIC_JOIN_CREATION_MODAL);
};
export const enableDeleteDependentJoin = () => {
    const enableDeleteDependentJoin = flags?.getValue(BLINK_FLAGS.enableDeleteDependentJoin.name);
    if (!_.isUndefined(enableDeleteDependentJoin)) {
        return enableDeleteDependentJoin;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_DELETE_DEPENDENT_JOIN);
};
export const enableGlobalJoinDirectionReversal = () => {
    const enableGlobalJoinDirectionReversal = flags?.getValue(BLINK_FLAGS.enableGlobalJoinDirectionReversal.name);
    if (!_.isUndefined(enableGlobalJoinDirectionReversal)) {
        return enableGlobalJoinDirectionReversal;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_GLOBAL_JOIN_DIREACTION_REVERSAL);
};
export const columnSecurityRuleEnabled = () => {
    const columnSecurityRuleEnabled = flags?.getValue(BLINK_FLAGS.columnSecurityRuleEnabled.name);
    if (!_.isUndefined(columnSecurityRuleEnabled)) {
        return columnSecurityRuleEnabled;
    }
    const objectSecurityConfig = getConfigInfo(jsonConstants.OBJECT_SECURITY_CONFIG);
    return (objectSecurityConfig &&
        objectSecurityConfig[jsonConstants.OBJECT_SECURITY_CONFIG_OPTIONS
            .COLUMN_SECURITY_RULE_ENABLED]);
};
export const isABACColumnSecurityEnabled = () => {
    const isABACColumnSecurityFlagEnabled = flags?.getValue(BLINK_FLAGS.csrABACEnabled.name);
    if (!_.isUndefined(isABACColumnSecurityFlagEnabled)) {
        return isABACColumnSecurityFlagEnabled && columnSecurityRuleEnabled();
    }
    const objectSecurityConfig = getConfigInfo(jsonConstants.OBJECT_SECURITY_CONFIG);
    return (objectSecurityConfig &&
        objectSecurityConfig[jsonConstants.OBJECT_SECURITY_CONFIG_OPTIONS
            .COLUMN_SECURITY_RULE_ENABLED] &&
        objectSecurityConfig[jsonConstants.OBJECT_SECURITY_CONFIG_OPTIONS
            .COLUMN_SECURITY_ABAC_ENABLED]);
};
export const isRedesignedHomepage = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.redesignedHomepage.name))) {
        return flags?.getValue(BLINK_FLAGS.redesignedHomepage.name);
    }
    const redesignedHomepage = getConfigInfo(jsonConstants.SHOW_REDESIGNED_HOMEPAGE);
    return !!redesignedHomepage;
};
export const enableOrgsOnFTPhase2 = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.enableOrgsOnFTPhase2.name))) {
        return flags?.getValue(BLINK_FLAGS.enableOrgsOnFTPhase2.name);
    }
    const enableOrgsOnFTPhase2 = getConfigInfo(jsonConstants.ENABLE_ORGS_FT_PHASE_2);
    return !!enableOrgsOnFTPhase2;
};
export const isEmbraceV2ExperienceEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.embracev2Experience.name))) {
        return flags?.getValue(BLINK_FLAGS.embracev2Experience.name);
    }
    const embraceV2ExperienceEnabled = getConfigInfo(jsonConstants.EMBRACE_V2_EXPERIENCE);
    return !!embraceV2ExperienceEnabled;
};
export const isNewConnectionsExperienceEnabled = () => {
    const newConnectionsExperienceFlag = flags?.getValue(BLINK_FLAGS.newConnectionsExperience.name);
    if (!_.isUndefined(newConnectionsExperienceFlag)) {
        return newConnectionsExperienceFlag;
    }
    const newConnectionsExperienceEnabled = getConfigInfo(jsonConstants.NEW_CONNECTIONS_EXPERIENCE);
    return !!newConnectionsExperienceEnabled;
};
export const isConnectorsCategorizationEnabled = () => {
    const connectorsCategorizationEnabled = flags?.getValue(BLINK_FLAGS.connectorsCategorizationEnabled.name);
    if (!_.isUndefined(connectorsCategorizationEnabled)) {
        return connectorsCategorizationEnabled;
    }
    return !!getConfigInfo(jsonConstants.CONNECTORS_CATEGORIZATION_ENABLED);
};
export const isSqlEditorAutoCompleteEnabled = () => {
    const sqlEditorAutoCompleteEnabled = flags?.getValue(BLINK_FLAGS.sqlEditorAutoCompleteEnabled.name);
    if (!_.isUndefined(sqlEditorAutoCompleteEnabled)) {
        return sqlEditorAutoCompleteEnabled;
    }
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.SQL_EDITOR_AUTO_COMPLETE_ENABLED]);
};
export const enableDataWorkspaceTableV2 = () => {
    const enableDataWorkspaceTableV2 = flags?.getValue(BLINK_FLAGS.enableDataWorkspaceTableV2.name);
    if (!_.isUndefined(enableDataWorkspaceTableV2)) {
        return enableDataWorkspaceTableV2;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_DATAWORKSPACE_TABLE_V2);
};
export const isOauthAutoRedirectEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.isOauthAutoRedirectEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.isOauthAutoRedirectEnabled.name);
    }
    const isOauthAutoRedirectEnabled = getConfigInfo(jsonConstants.IS_OAUTH_AUTO_REDIRECT_ENABLED);
    return !!isOauthAutoRedirectEnabled;
};
export const isOptimizedPayloadStructureEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return ((embraceConfig &&
        embraceConfig[jsonConstants.ENABLE_OPTIMIZED_PAYLOAD_STRUCTURE]) ||
        false);
};
export const enableConnectionSharingV2Apis = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.enableConnectionSharingV2Apis.name))) {
        return flags?.getValue(BLINK_FLAGS.enableConnectionSharingV2Apis.name);
    }
    const enableConnectionSharingV2Apis = getConfigInfo(jsonConstants.ENABLE_CONNECTION_SHARING_V2_APIS);
    return !!enableConnectionSharingV2Apis;
};
export const isUnsavedChangesModalEnabledForAdhocAnswer = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableNavigationModalPopupForUnsavedAnswer.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableNavigationModalPopupForUnsavedAnswer ?? false;
};
export const singleWindowOAuthEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.singleWindowOAuthEnabled.name))) {
        return flags?.getValue(BLINK_FLAGS.singleWindowOAuthEnabled.name);
    }
    const singleWindowOAuthEnabled = getConfigInfo(jsonConstants.SINGLE_WINDOW_OAUTH_ENABLED);
    return !!singleWindowOAuthEnabled;
};
export const connectionPayloadRestructuringEnabled = () => {
    const connectionPayloadRestructuringEnabled = flags?.getValue(BLINK_FLAGS.connectionPayloadRestructuringEnabled.name);
    if (!_.isUndefined(connectionPayloadRestructuringEnabled)) {
        return connectionPayloadRestructuringEnabled;
    }
    return !!getConfigInfo(jsonConstants.CONNECTION_PAYLOAD_RESTRUCTURING_ENABLED);
};
export const isRemappingV2ExperienceEnabled = () => {
    const remappingV2Experience = flags?.getValue(BLINK_FLAGS.remappingV2Experience.name);
    if (!_.isUndefined(remappingV2Experience)) {
        return remappingV2Experience;
    }
    return !!getConfigInfo(jsonConstants.REMAPPING_V2_EXPERIENCE);
};
export const isTraceDownloadEnabled = () => {
    const enableTraceDownloadsBlinkFlag = flags?.getValue(BLINK_FLAGS.enableTraceDownloads.name);
    if (_.isUndefined(enableTraceDownloadsBlinkFlag)) {
        return false;
    }
    return enableTraceDownloadsBlinkFlag;
};
export const getSharePromotionOptimizelyVariation = () => {
    return getValue(BLINK_FLAGS.enableSharePromotionExp);
};
export const isValueCasingColumnEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (embraceConfig &&
        embraceConfig[jsonConstants.VALUE_CASING_COLUMN_WORKSHEET]);
};
export const trialDaysLeftWrapper = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.trialDaysLeft.name)))
        return flags?.getValue(BLINK_FLAGS.trialDaysLeft.name);
    const expiryTimestamp = getSessionInfo()[jsonConstants.TRIAL_USER_EXPIRATION_TIME];
    return expiryTimestamp;
};
export function getGroupMemberShareNotificationLimit() {
    const notificationConfig = getConfigInfo(jsonConstants.NOTIFICATION_CONFIG);
    if (!notificationConfig ||
        !Object.prototype.hasOwnProperty.call(notificationConfig, jsonConstants.GROUP_MEMBER_SHARE_NOTIFICATION_LIMIT)) {
        return DEFAULT_GROUP_MEMBER_SHARE_NOTIFICATION_LIMIT;
    }
    return notificationConfig[jsonConstants.GROUP_MEMBER_SHARE_NOTIFICATION_LIMIT];
}
export const shouldShowOptimizeSageBtn = () => {
    const shouldShowOptimizeSageBtnFlag = flags?.getValue(BLINK_FLAGS.shouldShowOptimizeSageBtn.name);
    const shouldShowOptimizeSageBtn = getConfigInfo(jsonConstants.SHOULD_SHOW_OPTIMIZE_SAGE_BTN);
    return !_.isUndefined(shouldShowOptimizeSageBtnFlag)
        ? shouldShowOptimizeSageBtnFlag
        : !!shouldShowOptimizeSageBtn;
};
export const shouldShowNLSFeedbackConfirmationModal = () => {
    const shouldShowFragmentConfirmationModalFlag = flags?.getValue(BLINK_FLAGS.shouldShowFragmentConfirmationModal.name);
    const shouldShowFragmentConfirmationModal = getConfigInfo(jsonConstants.SHOULD_SHOW_FRAGMENT_CONFIRMATION_MODAL);
    return !_.isUndefined(shouldShowFragmentConfirmationModalFlag)
        ? shouldShowFragmentConfirmationModalFlag
        : !!shouldShowFragmentConfirmationModal;
};
export const getMaxConcurrentDataCalls = () => {
    const urlOverride = flags?.getValue(BLINK_FLAGS.maxConcurrentDataCalls.name);
    if (_.isFinite(urlOverride)) {
        return urlOverride;
    }
    const maxConcurrentDataCalls = getConfigInfo(jsonConstants.MAX_CONCURRENT_DATA_CALLS);
    return !_.isFinite(maxConcurrentDataCalls) || maxConcurrentDataCalls < 0
        ? 100
        : maxConcurrentDataCalls;
};
export const isOrgUiEnabled = () => {
    const configInfo = getConfigInfo(jsonConstants.ORGS_CONFIGURATION);
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.ORG_UI_ENABLED)) {
        return false;
    }
    const hideOrgAwareAdminPanel = getConfigInfo(jsonConstants.ORGS_HIDDEN);
    if (hideOrgAwareAdminPanel) {
        return false;
    }
    return configInfo[jsonConstants.ORG_UI_ENABLED];
};
export const isOrgsCleanUpEnabled = () => {
    const configInfo = getConfigInfo(jsonConstants.ORGS_CONFIGURATION);
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.ORGS_CLEANUP_ENABLED)) {
        return false;
    }
    return configInfo[jsonConstants.ORGS_CLEANUP_ENABLED];
};
export const isRedirectToV1Disabled = () => {
    return flags?.getValue(BLINK_FLAGS.disableRedirectToV1.name);
};
export const isEnableOnboardingConfigPerOrg = () => {
    const response = getSessionInfo();
    const resp = response.configInfo.enableOnboardingConfigPerOrg;
    if (_.isNil(resp)) {
        return false;
    }
    return resp;
};
export const isEmbedCustomerGuideEnabled = () => {
    const sessionInfo = getSessionInfo();
    return _.isString(sessionInfo?.embedCustomerGuideSrc);
};
export const getEmbedCustomerGuideSrc = () => {
    const sessionInfo = getSessionInfo();
    return sessionInfo?.embedCustomerGuideSrc || '';
};
export const getEmbedCustomerGuideUIToggle = () => {
    const sessionInfo = getSessionInfo();
    if (isEmbedCustomerGuideEnabled()) {
        return sessionInfo?.embedCustomerGuideUIToggle;
    }
    return false;
};
export const isNonEmbedFullAppAccessBlocked = () => {
    const sessionInfo = getSessionInfo();
    if (sessionInfo?.blockNonEmbedAccess !== undefined)
        return sessionInfo.blockNonEmbedAccess;
    if (hasDeveloperPrivileges() || hasAdminPrivileges())
        return false;
    const blockNonEmbedFullAppAccess = flags?.getValue(BLINK_FLAGS.blockNonEmbedFullAppAccess.name);
    if (blockNonEmbedFullAppAccess !== undefined)
        return blockNonEmbedFullAppAccess;
    const backendConfig = getBackendConfig();
    const blockNonEmbedFullAppAccessPerOrg = isOrgUiEnabled()
        ? backendConfig?.blockNonEmbedFullAppAccessPerOrg[getCurrentOrgId().toString()]
        : undefined;
    if (blockNonEmbedFullAppAccessPerOrg !== undefined)
        return (blockNonEmbedFullAppAccessPerOrg === true ||
            blockNonEmbedFullAppAccessPerOrg === 'true');
    return !!backendConfig?.blockNonEmbedFullAppAccess;
};
const isDebugUIDisabled = () => {
    const isDebugUIDisabled = getConfigInfo(jsonConstants.DISABLE_DEBUG_UI);
    return isDebugUIDisabled ?? false;
};
export const isInDebugMode = () => {
    return !isDebugUIDisabled() && flags?.getValue(BLINK_FLAGS.debug?.name);
};
export const hideOrgSwitcherUi = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.hideOrgSwitcherUi.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const hideOrgSwitcher = getConfigInfo(jsonConstants.HIDE_ORG_SWITCHER_UI);
    return !!hideOrgSwitcher;
};
export const enableSecuritySettingsPerOrg = () => {
    const enableSecuritySettingsPerOrg = getConfigInfo(jsonConstants.SECURITY_SETTINGS_PER_ORG_ENABLED);
    return !!enableSecuritySettingsPerOrg;
};
export const enableTokenAuthPerOrg = () => {
    const enableTokenAuth = getSessionInfo();
    return !!enableTokenAuth[jsonConstants.TOKEN_AUTH_PER_ORG_ENABLED];
};
export const isOptimizelyEnabled = () => {
    const flagOverride = flags.getValue(BLINK_FLAGS.enableOptimizely.name);
    if (!_.isUndefined(flagOverride)) {
        return flagOverride;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_OPTIMIZELY_SERVICE);
};
export const getAllOrgUserGroupGUID = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.ALL_ORG_USER_GROUP_ID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.ALL_ORG_USER_GROUP_ID];
};
export const getAllOrgsGroupGUID = () => {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.ALL_ORGS_GROUP_GUID]) {
        return '';
    }
    return sessionInfoObject[jsonConstants.ALL_ORGS_GROUP_GUID];
};
export const getGuidsToSkip = () => {
    const guidsToSkip = [
        getAllGroupGUID(),
        getSuperUserGuid(),
        getSystemUserGuid(),
        getSystemGroupGuid(),
        ...(getAllOrgsGroupGUID() ? [getAllOrgsGroupGUID()] : []),
    ];
    if (getSkipLiveboardIds().length > 0) {
        guidsToSkip.push(...getSkipLiveboardIds());
    }
    if (getSkipWorksheetIds().length > 0) {
        guidsToSkip.push(...getSkipWorksheetIds());
    }
    if (isOrgUiEnabled() && getAllOrgUserGroupGUID()) {
        guidsToSkip.push(getAllOrgUserGroupGUID());
    }
    if (guidsToSkip.includes(getAllGroupGUID())) {
        return guidsToSkip.filter(guid => guid !== '');
    }
    return [getAllGroupGUID(), ...guidsToSkip].filter(guid => guid !== '');
};
export const getAccessibleOrgs = () => {
    return getSessionInfo()[jsonConstants.ACCESSIBLE_ORGS] || [];
};
export const getSubscriptionStatus = () => {
    const userProperties = getUserProperties();
    const subscriptionStatus = flags?.getValue(BLINK_FLAGS.subscriptionStatus.name) ||
        userProperties.subscriptionStatus;
    switch (subscriptionStatus) {
        case SubscriptionStatus.ACTIVE:
            return SubscriptionStatus.ACTIVE;
        case SubscriptionStatus.CANCELED:
            return SubscriptionStatus.CANCELED;
        case SubscriptionStatus.INCOMPLETE:
            return SubscriptionStatus.INCOMPLETE;
        case SubscriptionStatus.INCOMPLETE_EXPIRED:
            return SubscriptionStatus.INCOMPLETE_EXPIRED;
        default:
            return SubscriptionStatus.ACTIVE;
    }
};
export const getPartnerConnectConfig = () => {
    const partnerConnectConfig = getSessionInfo()[jsonConstants.PARTNER_CONNECT_CONFIG];
    return partnerConnectConfig;
};
export const getPartnerServiceConfig = () => {
    return getConfigInfo(jsonConstants.PARTNER_SERVICE_CONFIG);
};
export const getState = () => {
    const sessionInfo = getSessionInfo();
    return sessionInfo ? sessionInfo.state : '';
};
export const getDataWorkspaceConfig = () => {
    const dataWorkspaceConfig = getConfigInfo(jsonConstants.DATA_WORKSPACE_CONFIG);
    return dataWorkspaceConfig;
};
export const hasDataWorkspacePrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.enabled;
};
export const hasDataAppV2Privilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.dataAppV2Enabled;
};
export const hasSqlEditorPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.sqlEditorEnabled;
};
export const hasSpotAppsPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.spotAppEnabled;
};
export const hasSpotAppAdminPrivilege = () => {
    const userProperties = getSessionInfo()[jsonConstants.USER_PROPERTIES];
    if (!userProperties[jsonConstants.SPOTAPPS_VALIDATOR]) {
        return false;
    }
    return userProperties[jsonConstants.SPOTAPPS_VALIDATOR];
};
export const getOktaUserId = () => {
    const userProperties = getSessionInfo()[jsonConstants.USER_PROPERTIES];
    return userProperties[jsonConstants.OKTA_USER_ID];
};
export const hasCreateSpotAppPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.createSpotAppEnabled;
};
export const hasSpotAppsCreationEnabled = () => {
    return hasPrivilege(PRIVILEGES.ENABLESPOTAPPCREATION);
};
export const hasDestinationsPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.tsSyncEnabled;
};
export const hasSchedulerSyncEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.tsSyncScheduleEnabled;
};
export const hasAnswersSyncPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.answersSyncEnabled;
};
export const hasSyncManagementPrivilege = () => {
    return hasPrivilege(PRIVILEGES.SYNCMANAGEMENT);
};
export const hasZendeskPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.zendeskEnabled;
};
export const hasMailchimpPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.mailchimpEnabled;
};
export const hasSnowflakePrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.snowflakeEnabled;
};
export const hasRedshiftPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.redshiftEnabled;
};
export const hasGoogleBigQueryPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.googleBigQueryEnabled;
};
export const isGatingForSyncEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.gatingForSyncEnabled;
};
export const isFullImageSyncEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.fullImageSyncEnabled;
};
export const hasDataCatalogAtlanPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.dataCatalogAtlanEnabled;
};
export const isDataCatalogCollibraEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return (dataWorkspaceConfig && dataWorkspaceConfig.dataCatalogCollibraEnabled);
};
export const isDataAugmentationEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.dataAugmentationEnabled;
};
export const canCreateCatalogPrivilege = () => {
    return hasPrivilege(PRIVILEGES.CAN_CREATE_CATALOG);
};
export const hasDataCatalogEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.dataCatalogEnabled;
};
export const canLiveboardVerifyPrivilege = () => {
    return hasPrivilege(PRIVILEGES.LIVEBOARD_VERIFIER);
};
export const hasRequestVerifyLiveboarddEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.verifyLiveboardEnabled;
};
export const hasVerifyLiveboardPrivilegeEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return (dataWorkspaceConfig &&
        dataWorkspaceConfig.verifyLiveboardPrivilegeEnabled);
};
export const hasHubSpotPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.hubSpotEnabled;
};
export const hasZohoPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.zohoEnabled;
};
export const hasSlackPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.slackEnabled;
};
export const hasDbtMetricPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.dbtMetricsEnabled;
};
export const hasGoogleSheetsPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.googleSheetsEnabled;
};
export const hasSalesforcePrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.salesForceEnabled;
};
export const hasMsTeamsPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.msTeamsEnabled;
};
export const isMsExcelEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.msExcelEnabled;
};
export const hasServiceNowPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.serviceNowEnabled;
};
export const hasGainsightPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.gainsightEnabled;
};
export const hasWebexEnabled = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.webexEnabled;
};
export const hasDBTIntegrationPrivilege = () => {
    const dataWorkspaceConfig = getDataWorkspaceConfig();
    return dataWorkspaceConfig && dataWorkspaceConfig.dbtIntEnabled;
};
export const isSemanticIntegrationEnabled = () => {
    return !!getConfigInfo(jsonConstants.SEMANTIC_INTEGRATION_ENABLED);
};
export const isLiveboardEnabled = () => {
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.isLiveboardEnabled.name))) {
        return !!flags?.getValue(BLINK_FLAGS.isLiveboardEnabled.name);
    }
    return !!getConfigInfo(jsonConstants.IS_LIVEBOARD_ENABLED);
};
const navObserver = new Subject();
export const navObserverSubscriber = (cb) => {
    navObserver.subscribe(cb);
};
export const triggerNavChanges = () => {
    navObserver.next();
};
const sessionConfigPatchObserver = new Subject();
export const sessionConfigPatchObserverSubscriber = (cb) => {
    return sessionConfigPatchObserver.subscribe(cb);
};
export const patchConfigInfo = (featureId, rawValue) => {
    if (!sessionInfo?.configInfo) {
        return;
    }
    const withoutPrefix = featureId.startsWith('orion.')
        ? featureId.slice('orion.'.length)
        : featureId;
    const resolveTypedValue = (existingValue) => {
        if (typeof existingValue === 'boolean') {
            return rawValue === 'true';
        }
        if (Array.isArray(existingValue)) {
            return rawValue ? rawValue.split(',').filter(Boolean) : [];
        }
        return rawValue;
    };
    const hasOwnKey = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
    if (hasOwnKey(sessionInfo.configInfo, withoutPrefix)) {
        const before = sessionInfo.configInfo[withoutPrefix];
        const typedValue = resolveTypedValue(before);
        const patch = { [withoutPrefix]: typedValue };
        sessionInfo = {
            ...sessionInfo,
            configInfo: { ...sessionInfo.configInfo, ...patch },
        };
        sessionConfigPatchObserver.next(patch);
        return;
    }
    if (withoutPrefix.includes('.')) {
        const topKey = withoutPrefix.slice(0, withoutPrefix.indexOf('.'));
        const leafKey = withoutPrefix.slice(withoutPrefix.lastIndexOf('.') + 1);
        if (hasOwnKey(sessionInfo.configInfo, topKey)) {
            const segments = withoutPrefix.split('.');
            const deepSet = (obj, keys) => {
                const [head, ...tail] = keys;
                if (tail.length === 0) {
                    const typed = resolveTypedValue(obj?.[head]);
                    return [{ ...obj, [head]: typed }, typed];
                }
                const [updatedChild, leaf] = deepSet(obj?.[head] ?? {}, tail);
                return [{ ...obj, [head]: updatedChild }, leaf];
            };
            const configInfoMap = sessionInfo.configInfo;
            const existingTop = configInfoMap[topKey] ?? {};
            const [updatedTop, leafTypedValue] = deepSet(existingTop, segments.slice(1));
            sessionInfo = {
                ...sessionInfo,
                configInfo: {
                    ...sessionInfo.configInfo,
                    [topKey]: updatedTop,
                },
            };
            sessionConfigPatchObserver.next({
                [leafKey]: leafTypedValue,
            });
            return;
        }
        if (hasOwnKey(sessionInfo.configInfo, leafKey)) {
            const existingLeaf = sessionInfo.configInfo[leafKey];
            const typedValue = resolveTypedValue(existingLeaf);
            const patch = { [leafKey]: typedValue };
            sessionInfo = {
                ...sessionInfo,
                configInfo: { ...sessionInfo.configInfo, ...patch },
            };
            sessionConfigPatchObserver.next(patch);
        }
    }
};
export const getZoomedRenderWidthForExport = () => {
    const zoomedRenderWidthForExport = flags?.getValue(BLINK_FLAGS.zoomedRenderWidthForExport.name);
    if (!_.isUndefined(zoomedRenderWidthForExport)) {
        return zoomedRenderWidthForExport;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    if (exportConfig &&
        exportConfig[jsonConstants.ZOOMED_RENDER_WIDTH_FOR_EXPORT]) {
        return exportConfig[jsonConstants.ZOOMED_RENDER_WIDTH_FOR_EXPORT];
    }
    return null;
};
export const getZoomedRenderWidthForExportV2 = () => {
    const zoomedRenderWidthForExportV2 = flags?.getValue(BLINK_FLAGS.zoomedRenderWidthForExportV2.name);
    if (!_.isUndefined(zoomedRenderWidthForExportV2)) {
        return zoomedRenderWidthForExportV2;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    if (exportConfig &&
        exportConfig[jsonConstants.ZOOMED_RENDER_WIDTH_FOR_EXPORT_V2]) {
        return exportConfig[jsonConstants.ZOOMED_RENDER_WIDTH_FOR_EXPORT_V2];
    }
    return null;
};
export const isLiveboardTabExportEnabled = () => {
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    if (exportConfig &&
        exportConfig[jsonConstants.IS_LIVEBOARD_TAB_EXPORT_ENABLED] !==
            undefined &&
        exportConfig[jsonConstants.IS_LIVEBOARD_TAB_EXPORT_ENABLED] !== null) {
        return exportConfig[jsonConstants.IS_LIVEBOARD_TAB_EXPORT_ENABLED];
    }
    return true;
};
export const getMaxVizInTabForExport = () => {
    const maxVizInTabForExport = flags?.getValue(BLINK_FLAGS.maxVizInTabForExport.name);
    if (!_.isUndefined(maxVizInTabForExport)) {
        return maxVizInTabForExport;
    }
    const exportConfig = getConfigInfo(jsonConstants.EXPORT_CONFIG);
    if (exportConfig && exportConfig[jsonConstants.MAX_VIZ_IN_TAB_FOR_EXPORT]) {
        return exportConfig[jsonConstants.MAX_VIZ_IN_TAB_FOR_EXPORT];
    }
    return null;
};
export const getIdentityServiceUrl = () => {
    const configInfo = getBackendConfig();
    if (!configInfo ||
        !Object.prototype.hasOwnProperty.call(configInfo, jsonConstants.IDENTITY_SERVICE_URL)) {
        return '';
    }
    return configInfo[jsonConstants.IDENTITY_SERVICE_URL];
};
export const isShareV2Enabled = () => {
    const isEnabled = getConfigInfo(jsonConstants.IS_SHARE_V2_ENABLED);
    const shareV2Flag = flags?.getValue(BLINK_FLAGS.enableShareV2.name);
    return !_.isUndefined(shareV2Flag) ? shareV2Flag : !!isEnabled;
};
export const isDiscountEnabled = () => {
    const isEnabled = getConfigInfo(jsonConstants.IS_DISCOUNT_FOR_TRIAL_USER_ENABLED);
    const discountEnableFlag = flags?.getValue(BLINK_FLAGS.enableDiscountForTrialUser.name);
    return !_.isUndefined(discountEnableFlag)
        ? discountEnableFlag
        : !!isEnabled;
};
export const isOrgsOnFTEnabled = () => {
    const isEnabled = getConfigInfo(jsonConstants.IS_ORGS_ON_FT_ENABLED);
    const orgsEnableFlag = flags?.getValue(BLINK_FLAGS.enableOrgsOnFT.name);
    return !_.isUndefined(orgsEnableFlag) ? orgsEnableFlag : !!isEnabled;
};
export const isSlackInitiallyConnectedByUser = () => {
    const flagValue = getPreference(blinkConstants.preferenceKeys.IS_SLACK_INITIALLY_CONNECTED);
    return flagValue;
};
export const isModularHomeExperiencePreferred = () => {
    const flagValue = getPreference(blinkConstants.preferenceKeys.NEW_HOMEPAGE_EXPERIENCE);
    return flagValue;
};
export const isModularHomeExperienceEnabledByDefault = () => {
    const enabled = getConfigInfo(jsonConstants.IS_NEW_HOMEPAGE_EXPERIENCE_ON_BY_DEFAULT);
    return enabled ?? false;
};
export const isLibraryFilteringImprovementsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isLibraryFilteringImprovementsEnabled.name);
    const backendFlag = getConfigInfo(jsonConstants.IS_LIBRARY_FILTERING_IMPROVEMENTS_ENABLED);
    return !_.isUndefined(urlFlag) ? urlFlag : !!backendFlag;
};
export const isModularHomeExperienceEnabledOnCluster = () => {
    return getConfigInfo(jsonConstants.NEW_HOMEPAGE_EXPERIENCE) || false;
};
export const getIsAnalystOnboardingCompleted = () => {
    const isAnalystOnboardingCompleted = getExposedUserPreference(SESSION_USER_PREFERENCES.ANALYST_ONBOARDING_COMPLETE);
    if (_.isNil(isAnalystOnboardingCompleted)) {
        return true;
    }
    return isAnalystOnboardingCompleted;
};
export const isSortByLastModifiedEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isSortByLastModifiedEnabled.name);
    const backendFlag = getConfigInfo(jsonConstants.IS_SORT_BY_LAST_MODIFIED_ENABLED);
    return !_.isUndefined(urlFlag) ? urlFlag : !!backendFlag;
};
export const getIsSpotterFullExperienceEnabled = () => {
    const isSpotterFullExperienceEnabled = getExposedUserPreference(SESSION_USER_PREFERENCES.SPOTTER_FULL_EXPERIENCE);
    if (!hasAdminPrivileges()) {
        return true;
    }
    if (_.isNil(isSpotterFullExperienceEnabled)) {
        return false;
    }
    return isSpotterFullExperienceEnabled;
};
export const isModularHomeExperienceEnabled = () => {
    return true;
};
export const hideHomepageLeftNav = (isNavigationV3Enabled = false) => {
    if (!(isModularHomeExperienceEnabled() || isNavigationV3Enabled)) {
        return true;
    }
    const isLeftNavHidden = flags?.getValue(BLINK_FLAGS.hideHomepageLeftNav.name);
    if (!_.isUndefined(isLeftNavHidden)) {
        return isLeftNavHidden;
    }
    const fullEmbedConfig = getConfigInfo(jsonConstants.FULL_EMBED_CONFIG);
    return (fullEmbedConfig &&
        !!fullEmbedConfig[jsonConstants.HIDE_HOMEPAGE_LEFT_NAV]);
};
export const disableAuthorNameSort = () => {
    const isAuthorSortDisabled = flags?.getValue(BLINK_FLAGS.disableAuthorNameSort.name);
    if (!_.isUndefined(isAuthorSortDisabled)) {
        return isAuthorSortDisabled;
    }
    return !!getConfigInfo(jsonConstants.DISABLE_AUTHOR_NAME_SORT);
};
export const isInviteEnabledInShareV2 = () => {
    const isInviteEnabled = getConfigInfo(jsonConstants.IS_INVITE_ENABLED_INSHARE_V2);
    const inviteInShareV2Flag = flags?.getValue(BLINK_FLAGS.enableInviteInShareV2.name);
    return !_.isUndefined(inviteInShareV2Flag)
        ? inviteInShareV2Flag
        : !!isInviteEnabled;
};
export const hasUserUpdatedDisplayName = () => {
    const hasUserUpdatedDisplayNameFlag = flags?.getValue(BLINK_FLAGS.hasUserUpdatedDisplayName.name);
    const userProperties = getUserProperties();
    return !_.isUndefined(hasUserUpdatedDisplayName)
        ? hasUserUpdatedDisplayNameFlag
        : !!userProperties.hasUserUpdatedDisplayName;
};
export const isShareEnabledInInvite = () => {
    const isShareEnabledInInviteFlag = flags?.getValue(BLINK_FLAGS.enableShareInInvite.name);
    return !_.isUndefined(isShareEnabledInInviteFlag)
        ? isShareEnabledInInviteFlag
        : getConfigInfo(jsonConstants.ENABLE_SHARE_IN_INVITE);
};
export const setHasUserUpdatedDisplayName = (hasUserUpdatedDisaplyName) => {
    const sessionInfoObject = getSessionInfo();
    const userProperties = getUserProperties();
    userProperties.hasUserUpdatedDisplayName = hasUserUpdatedDisaplyName;
    sessionInfoObject.userProperties = userProperties;
};
export const isUpdateDisplayNameEnabled = () => {
    const isEnabled = getConfigInfo(jsonConstants.UPDATE_DISPLAY_NAME_ENABLED);
    const udpateDisplayNameFlag = flags?.getValue(BLINK_FLAGS.isUpdateDisplayNameEnabled.name);
    return !_.isUndefined(udpateDisplayNameFlag)
        ? udpateDisplayNameFlag
        : !!isEnabled;
};
export const isHtmlRenderingDisabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isHtmlRenderingDisabled.name);
    let isDisabled = !!getConfigInfo(jsonConstants.HTML_RENDERING_DISABLED);
    if (!isDisabled) {
        isDisabled = !!urlFlagValue;
    }
    return isDisabled || disableHtmlRenderingManually;
};
export const maxNumFilterValuesInModal = () => {
    const defaultValue = BLINK_FLAGS.maxNumFilterValuesInModal.defaultValue;
    const noOfFilterValues = Number(getConfigInfo(jsonConstants.MAX_NO_OF_FILTER_VALUES_IN_MODAL));
    return noOfFilterValues <= 0
        ? defaultValue
        : Math.min(noOfFilterValues, MAX_NO_OF_FILTER_VALUES);
};
export const getPayloadRuntimeFilterLimit = () => {
    const payloadRuntimeFilterLimit = Number(getConfigInfo(jsonConstants.PAYLOAD_RUNTIME_FILTER_LIMIT));
    return payloadRuntimeFilterLimit <= 0
        ? MAX_NO_OF_PAYLOAD_RUNTIME_FILTERS
        : Math.min(payloadRuntimeFilterLimit, MAX_NO_OF_PAYLOAD_RUNTIME_FILTERS);
};
export const getNumTimesDisplayNameDialogShown = () => {
    return (getExposedUserPreference(SESSION_USER_PREFERENCES.NUM_TIMES_DISPLAY_NAME_DIALOG_SHOWN) || 0);
};
export const setNumTimesDisplayNameDialogShown = (value) => {
    return setExposedUserPreference(SESSION_USER_PREFERENCES.NUM_TIMES_DISPLAY_NAME_DIALOG_SHOWN, value);
};
export const getMonitorRuleAutoCreatePreference = () => {
    const monitorRuleAutoCreate = getExposedUserPreference(SESSION_USER_PREFERENCES.MONITOR_RULE_AUTO_CREATE);
    if (_.isNil(monitorRuleAutoCreate)) {
        return true;
    }
    return monitorRuleAutoCreate;
};
export const setMonitorRuleAutoCreatePreference = (value) => {
    return setExposedUserPreference(SESSION_USER_PREFERENCES.MONITOR_RULE_AUTO_CREATE, value);
};
export const getScheduleJobAutoCreatePreference = () => {
    const scheduleJobAutoCreate = getExposedUserPreference(SESSION_USER_PREFERENCES.SCHEDULE_JOB_AUTO_CREATE);
    if (_.isNil(scheduleJobAutoCreate)) {
        return true;
    }
    return scheduleJobAutoCreate;
};
export const setScheduleJobAutoCreatePreference = (value) => {
    return setExposedUserPreference(SESSION_USER_PREFERENCES.SCHEDULE_JOB_AUTO_CREATE, value);
};
export const getDemoConnectionGuids = () => {
    const demoConfig = getConfigInfo(jsonConstants.DEMO_DATA_CONFIG);
    return !!demoConfig &&
        Object.prototype.hasOwnProperty.call(demoConfig, jsonConstants.DEMO_CONNECTION_GUIDS)
        ? demoConfig[jsonConstants.DEMO_CONNECTION_GUIDS]
        : [];
};
export const getDemoPinboardGuids = () => {
    const demoConfig = getConfigInfo(jsonConstants.DEMO_DATA_CONFIG);
    return !!demoConfig &&
        Object.prototype.hasOwnProperty.call(demoConfig, jsonConstants.DEMO_PINBOARD_GUIDS)
        ? demoConfig[jsonConstants.DEMO_PINBOARD_GUIDS]
        : [];
};
export const isSearchCacheEnabled = () => {
    const embraceConfig = getConfigInfo(jsonConstants.EMBRACE_CONFIG);
    return (!!flags?.getValue(BLINK_FLAGS.enableSearchCache.name) ||
        !!embraceConfig[jsonConstants.SEARCH_CACHE_ENABLED]);
};
const userDisplayNameObserver = new Subject();
export const subscribeToUserDisplayName = (cb) => {
    return userDisplayNameObserver.subscribe(cb);
};
export const setUserDisplayName = (name) => {
    sessionInfo.userDisplayName = name;
    userDisplayNameObserver.next(name);
};
export const getMaxNumTimesDisplayNameDialogShown = () => {
    return getConfigInfo(jsonConstants.MAX_NUM_TIMES_DISPLAY_NAME_DIALOG_SHOWN);
};
export const isSocialProofEnabled = () => {
    const isSocialProof = getConfigInfo(jsonConstants.SOCIAL_PROOF);
    if (_.isNil(isSocialProof)) {
        return false;
    }
    return !!isSocialProof;
};
export function getMduBannerConfig() {
    const sessionInfoObject = getSessionInfo();
    if (!sessionInfoObject ||
        !sessionInfoObject[jsonConstants.MDU_BANNER_NOTIFICATION]) {
        return null;
    }
    return sessionInfoObject[jsonConstants.MDU_BANNER_NOTIFICATION];
}
export const isLoginEnabledForExpiredUser = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.loginEnabledForExpiredUser.name);
    if (!_.isNil(urlFlagValue)) {
        return urlFlagValue;
    }
    const isLoginEnabled = getValue(BLINK_FLAGS.loginEnabledForExpiredUser);
    if (_.isNil(isLoginEnabled)) {
        return false;
    }
    return isLoginEnabled;
};
export const getAvailableCustomCalendars = () => {
    const sessionInfoObject = getSessionInfo();
    return sessionInfoObject
        ? sessionInfoObject[jsonConstants.AVAILABLE_CALENDARS]
        : {};
};
export const canManageConnections = () => {
    return canUserManageData() && isEmbraceEnabled();
};
export const isCustomRegionEnabled = () => {
    const customRegionConfig = getConfigInfo(jsonConstants.CUSTOM_REGION.CONFIG);
    const customRegionFlag = flags?.getValue(BLINK_FLAGS.enableCustomRegion.name);
    if (!_.isUndefined(customRegionFlag))
        return customRegionFlag;
    return (!!customRegionConfig &&
        customRegionConfig[jsonConstants.CUSTOM_REGION.ENABLED]);
};
export const getCustomRegionMaxFileLimit = () => {
    const customRegionConfig = getConfigInfo(jsonConstants.CUSTOM_REGION.CONFIG);
    if (customRegionConfig)
        return Number(customRegionConfig[jsonConstants.CUSTOM_REGION.MAX_FILE_LIMIT]);
    return 0;
};
export const getCustomRegionMaxFileSize = () => {
    const customRegionConfig = getConfigInfo(jsonConstants.CUSTOM_REGION.CONFIG);
    if (customRegionConfig)
        return Number(customRegionConfig[jsonConstants.CUSTOM_REGION.MAX_FILE_SIZE]);
    return 0;
};
export const isStandardRegionEnabled = () => {
    const standardRegionConfig = getConfigInfo(jsonConstants.STANDARD_REGION.CONFIG);
    const standardRegionFlag = flags?.getValue(BLINK_FLAGS.enableStandardRegion.name);
    if (!_.isUndefined(standardRegionFlag))
        return standardRegionFlag;
    return (!!standardRegionConfig &&
        standardRegionConfig[jsonConstants.STANDARD_REGION.ENABLED]);
};
export const canManageCustomCalendars = () => {
    const blinkFlagValue = flags?.getValue(BLINK_FLAGS.canManageCustomCalendars.name);
    if (!_.isUndefined(blinkFlagValue))
        return blinkFlagValue;
    if (!isCustomCalendarEnabled()) {
        return false;
    }
    if (!isRolesEnabled() && canUserManageData()) {
        return true;
    }
    if (isRolesEnabled() &&
        (_hasCustomCalendarsManagementPrivileges(getSessionInfo()) ||
            hasAdminPrivileges())) {
        return true;
    }
    return false;
};
export const canViewSystemActivities = () => {
    const sessionInfoObject = getSessionInfo();
    return (isRolesEnabled() &&
        _hasViewSystemActivitiesPrivileges(sessionInfoObject));
};
export const canCreateOrEditConnections = () => {
    const blinkFlagValue = flags?.getValue(BLINK_FLAGS.canCreateOrEditConnections.name);
    if (!_.isUndefined(blinkFlagValue))
        return blinkFlagValue;
    const sessionInfoObject = getSessionInfo();
    const hasGranularPrivilege = isRolesEnabled() &&
        _hasCreateOrEditConnectionsPrivileges(sessionInfoObject);
    if (isRolesEnabled()) {
        return hasGranularPrivilege || hasAdminPrivileges();
    }
    return canUserManageData();
};
export const canOnlyCreateOrEditConnections = () => {
    return canCreateOrEditConnections() && !canManageWorksheetViewsTables();
};
export const canOnlyConfigureConnections = () => {
    return canManageWorksheetViewsTables() && !canCreateOrEditConnections();
};
export const isSeedQuestionsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableSeedQuestions.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableSeedQuestions ?? false;
};
export const isSystemFragmentsEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableSystemFragments ?? false;
};
export const isColdStartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableColdStart.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    return false;
};
export const isQueryVisualizerV2Enabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableQueryVisualizerV2.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableQueryVisualizerV2 ?? false;
};
export const isExploreFiltersExpandable = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isExploreFiltersExpandable.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.isExploreFiltersExpandable ?? false;
};
export const isExploreAnswerFilterValuesByGetFilterAPIEnabled = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableExploreAnswerFilterValuesByGetFilterAPI ?? true;
};
export const isConvAssistEnabledForUserByDefault = () => {
    const backendConfig = getBackendConfig();
    return backendConfig?.enableConvAssistByDefault || false;
};
export const isConvAssistExperiencePreferred = () => {
    return getPreference(blinkConstants.preferenceKeys.CONV_ASSIST);
};
export const isConvAssistEnabledOnCluster = () => {
    const backendConfig = getBackendConfig();
    return (backendConfig?.enableConvAssist ||
        backendConfig?.enableConvAssistFromNls ||
        false);
};
export const hasPreviewThougtspotSagePrivileges = () => {
    return (hasPrivilege(PRIVILEGES.PREVIEW_THOUGHTSPOT_SAGE) ||
        hasPrivilege(PRIVILEGES.CAN_USE_SPOTTER) ||
        hasPrivilege(PRIVILEGES.CAN_MANAGE_SPOTTER));
};
export const hasCanManageSpotterPrivilege = () => {
    return hasPrivilege(PRIVILEGES.CAN_MANAGE_SPOTTER);
};
export const isConvAssistEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableConvAssist.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    const isConvAssistEnabledOnCluster = backendConfig?.enableConvAssist ?? false;
    if (!isConvAssistEnabledOnCluster ||
        !hasPreviewThougtspotSagePrivileges()) {
        return false;
    }
    return isConvAssistEnabledForUserByDefault();
};
export const isConvAssistFromNLSEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableConvAssistFromNls.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    const isConvAssistEnabledOnCluster = backendConfig?.enableConvAssistFromNls ?? false;
    if (!isConvAssistEnabledOnCluster) {
        return false;
    }
    const userPreference = isConvAssistExperiencePreferred();
    if (_.isBoolean(userPreference)) {
        return userPreference;
    }
    return isConvAssistEnabledForUserByDefault();
};
export const isShowQueryByDefaultForConvAssistEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableShowQueryByDefaultForConvAssist.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableShowQueryByDefaultForConvAssist || false;
};
export const shouldAllowRegeneratingSeedQuestions = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.allowRegeneratingSeedQuestions.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    return false;
};
export const isDataUsageTabEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableDataUsageTab.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const isTabEnabled = getValue(BLINK_FLAGS.enableDataUsageTab);
    if (_.isNil(isTabEnabled)) {
        return false;
    }
    return !!isTabEnabled;
};
export const isLiveboardGuideExpEnabled = () => {
    return getValue(BLINK_FLAGS.liveboardV2Guide);
};
export const isUplevelMakeACopyExpEnabled = () => {
    return getValue(BLINK_FLAGS.uplevelMakeACopy);
};
export const isRequestAccessExpEnabled = () => {
    return getValue(BLINK_FLAGS.requestAccessExperiment);
};
export const getNumUsersInOrg = () => {
    return parseInt(localStorage.getItem(NUM_USERS_IN_ORG), 10);
};
export const userLimitAlertEnabledForTE = () => {
    const canManageUsers = () => {
        return (hasAdminPrivileges() ||
            (isRolesEnabled() && hasUserAdminPrivileges()));
    };
    const softlimitEnabledForTeamsEdition = () => {
        const urlFlag = flags?.getValue(BLINK_FLAGS.softlimitEnabledForTeamsEdition.name);
        if (!_.isNil(urlFlag)) {
            return urlFlag;
        }
        const isSoftLimitEnabled = getValue(BLINK_FLAGS.softlimitEnabledForTeamsEdition);
        if (_.isNil(isSoftLimitEnabled)) {
            return false;
        }
        return !!isSoftLimitEnabled;
    };
    if (!_.isUndefined(flags?.getValue(BLINK_FLAGS.userLimitAlertEnabledForTE.name))) {
        return flags?.getValue(BLINK_FLAGS.userLimitAlertEnabledForTE.name);
    }
    const isUserLimitAlertEnabledForTE = getConfigInfo(jsonConstants.USER_LIMIT_ALERT_ENABLED_TE);
    return (!!isUserLimitAlertEnabledForTE &&
        softlimitEnabledForTeamsEdition() &&
        canManageUsers() &&
        getSubscriptionType() === 'TEAMS_EDITION');
};
export const optimizeAddUserFlowEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.optimizeAddUserFlowEnabled.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const optimizeAddUserFlowEnabled = getValue(BLINK_FLAGS.optimizeAddUserFlowEnabled);
    if (_.isNil(optimizeAddUserFlowEnabled)) {
        return false;
    }
    return !!optimizeAddUserFlowEnabled;
};
export const isFreeTrialAnalystOptionsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.freeTrialAnalystOptions.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const isExperimentEnabled = getValue(BLINK_FLAGS.freeTrialAnalystOptions);
    if (_.isNil(isExperimentEnabled)) {
        return false;
    }
    return (getValue(BLINK_FLAGS.freeTrialAnalystOptions) &&
        isTrialVersionEnabled());
};
export const isDataManagementCheckEnabled = () => {
    const userPersonaConfig = getConfigInfo(jsonConstants.USER_PERSONA_CONFIGURATION);
    const dataManagementCheckEnabledUrlFlag = flags.getValue(BLINK_FLAGS.enableDataManagementCheck.name);
    if (!userPersonaConfig ||
        !Object.prototype.hasOwnProperty.call(userPersonaConfig, jsonConstants.DATA_MANAGEMENT_CHECK_ENABLED)) {
        return dataManagementCheckEnabledUrlFlag;
    }
    return !_.isUndefined(dataManagementCheckEnabledUrlFlag)
        ? dataManagementCheckEnabledUrlFlag
        : userPersonaConfig[jsonConstants.DATA_MANAGEMENT_CHECK_ENABLED];
};
export const isCustomChartEnabled = () => {
    const urlFlagValue = flags?.getValue(jsonConstants.ENABLE_CUSTOM_CHARTS);
    if (!_.isUndefined(urlFlagValue)) {
        if (urlFlagValue === 'true' || urlFlagValue === true)
            return true;
        return false;
    }
    const enableCustomCharts = getConfigInfo(jsonConstants.ENABLE_CUSTOM_CHARTS);
    return !!enableCustomCharts;
};
export const isAdvancedPivotTableEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.enableAdvancedPivotTable.name);
    if (!_.isUndefined(urlFlagValue))
        return urlFlagValue;
    const enableAdvancedPivotTable = getConfigInfo(jsonConstants.ENABLE_ADVANCED_PIVOT_TABLE);
    return !!enableAdvancedPivotTable;
};
export const isCustomerLLMAccount = () => {
    const isCustomerLLMAccount = getConfigInfo(jsonConstants.IS_CUSTOMER_LLM_ACCOUNT);
    return !!isCustomerLLMAccount;
};
export const aiModelType = () => {
    const aiModelType = getConfigInfo(jsonConstants.AI_MODEL_TYPE);
    return aiModelType;
};
export const isTooltipCustomizationEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isTooltipCustomizationEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return !!getConfigInfo(jsonConstants.TOOLTIP_CUSTOMIZATION);
};
export const getLiveboardVizQueryStatusConfig = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableLiveboardQueryStatus.name);
    if (!_.isUndefined(urlFlag))
        return { enabled: urlFlag, waitTime: 3000, pollingInterval: 3000 };
    const featureConfig = getConfigInfo(jsonConstants.LIVEBOARD_QUERY_STATUS);
    return {
        enabled: !!featureConfig?.enabled,
        waitTime: featureConfig?.waitTime || 3000,
        pollingInterval: featureConfig?.pollingInterval || 3000,
    };
};
export const getLiveboardVizDataCacheConfig = () => {
    const defaultConfig = {
        enable: false,
        allowAll: false,
        liveboardIds: [],
    };
    const tscliEnabled = !!getBackendConfig()?.liveboardVizDataCacheEnabled;
    if (!tscliEnabled) {
        return defaultConfig;
    }
    if (isOrgUiEnabled()) {
        const orgEnabled = !!getConfigInfo(jsonConstants.LIVEBOARD_VIZ_DATA_CACHE_ORG_ENABLED);
        if (!orgEnabled) {
            return defaultConfig;
        }
    }
    const currentOrgKey = getCurrentOrgId().toString();
    const allLiveboardsPerOrg = getConfigInfo(jsonConstants.LIVEBOARD_VIZ_DATA_CACHE_ALL_LIVEBOARDS_PER_ORG);
    const liveboardIdsPerOrg = getConfigInfo(jsonConstants.LIVEBOARD_VIZ_DATA_CACHE_LIVEBOARD_IDS_PER_ORG);
    const orgAllowAll = !!allLiveboardsPerOrg?.[currentOrgKey];
    const orgLiveboardIds = Array.isArray(liveboardIdsPerOrg?.[currentOrgKey])
        ? liveboardIdsPerOrg[currentOrgKey].filter((id) => typeof id === 'string')
        : [];
    return {
        enable: true,
        allowAll: orgAllowAll,
        liveboardIds: orgLiveboardIds,
    };
};
export const getMuzeChartIdbCacheEnabled = () => {
    return !!getBackendConfig()?.muzeChartIdbCacheEnabled;
};
export const getVizRenderScrollDebounceMs = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.vizRenderScrollDebounceMs.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const config = getConfigInfo(jsonConstants.VIZ_RENDER_SCROLL_DEBOUNCE_MS) ?? 0;
    return config < 0 ? 0 : config;
};
export const getLoadContextBookAPIDebounceMs = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.loadContextBookAPIDebounceMs.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const config = getConfigInfo(jsonConstants.LOAD_CONTEXT_BOOK_API_DEBOUNCE_MS) ?? 0;
    return config < 0 ? 0 : config;
};
const DEFAULT_LIVEBOARD_MIXED_CACHE_THRESHOLD_MINUTES = 30;
export const getLiveboardMixedCacheThresholdMs = () => {
    const config = getConfigInfo(jsonConstants.LIVEBOARD_MIXED_CACHE_THRESHOLD_MINUTES);
    const minutes = config === null || config === undefined || config <= 0
        ? DEFAULT_LIVEBOARD_MIXED_CACHE_THRESHOLD_MINUTES
        : config;
    return minutes * 60 * 1000;
};
export const isViewportAwareVizRenderEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableViewportAwareVizRender.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return (getConfigInfo(jsonConstants.ENABLE_VIEWPORT_AWARE_VIZ_RENDER) ?? false);
};
export const skipClientStateSchemaCheck = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.skipClientStateSchemaCheck.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return !!getConfigInfo(jsonConstants.SKIP_CLIENT_STATE_SCHEMA_CHECK);
};
export const getCustomNumberFormatPatternForGeoChart = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.customNumberFormatPatternForGeoChart.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return getConfigInfo(jsonConstants.CUSTOM_NUMBER_FORMAT_PATTERN_FOR_GEO_CHART);
};
export const includeMilliSeconds = () => {
    const dataManagerServiceConfig = getConfigInfo(jsonConstants.DATA_MANAGER_SERVICE_CONFIG);
    return (!!dataManagerServiceConfig &&
        dataManagerServiceConfig[jsonConstants.DATA_MANAGER_SERVICE_CONFIG_OPTIONS
            .INCLUDE_MILLI_SECONDS]);
};
export const isHiddenTableColumnsEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isHiddenTableColumnsEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.HIDDEN_TABLE_COLUMNS);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const isEnhancedCfEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isEnhancedCfEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.ENHANCED_CF);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const isGradientColoringEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isGradientColoringEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.GRADIENT_COLORING);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const isChartSettingsV2Enabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isChartSettingsV2Enabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.CHART_SETTINGS_V2);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const isAttributeNfEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isAttributeNfEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.ATTRIBUTE_NF);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const isChartSkillsEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.isChartSkillsEnabled.name);
    if (!_.isUndefined(urlFlagValue)) {
        return urlFlagValue.startsWith('true');
    }
    const tscliFlagValue = getConfigInfo(jsonConstants.CHART_SKILLS);
    if (tscliFlagValue === 'inherit') {
        return enableKaiosAgent();
    }
    return tscliFlagValue === 'true';
};
export const getProductType = () => {
    const info = getSessionInfo();
    return (info?.[jsonConstants.PRODUCT_TYPE] ||
        jsonConstants.productType.ENTERPRISE);
};
export const getDemoHostClusterURL = () => {
    return getConfigInfo(jsonConstants.DEMO_HOST_CLUSTER_URL);
};
export const getDemoLiveboardGuid = () => {
    return getConfigInfo(jsonConstants.DEMO_LIVEBOARD_GUID);
};
export const isBypassRLSPrivilegeEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_BYPASS_RLS_PRIVILEGE) || false;
};
export const isPinboardCreationPrivilegeEnabled = () => {
    const fullEmbedConfig = getConfigInfo(jsonConstants.FULL_EMBED_CONFIG);
    const pinboardCreationPrivilegeEnabledFlag = flags?.getValue(BLINK_FLAGS.pinboardCreationPrivilegeEnabled.name);
    return _.isUndefined(pinboardCreationPrivilegeEnabledFlag)
        ? !!fullEmbedConfig[jsonConstants.PINBOARD_CREATION_PRIVILEGE_ENABLED]
        : pinboardCreationPrivilegeEnabledFlag;
};
export const getAdminPrivilegeSegregation = () => {
    return getConfigInfo(jsonConstants.ADMIN_PRIVILEGE_SEGREGATION);
};
export const isStrictPrivilegeManagementEnabled = () => {
    return (getConfigInfo(jsonConstants.STRICT_PRIVILEGE_MANAGEMENT_ENABLED) ||
        false);
};
export const mergePivotExportedRowsAndColumns = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.mergePivotExportedRowsAndColumns.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return getConfigInfo(jsonConstants.MERGE_PIVOT_EXPORTED_ROWS_AND_COLUMNS);
};
export const isGitIntegrationEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isGitIntegrationEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const gitIntegrationConfig = getConfigInfo(jsonConstants.GIT_INTEGRATION);
    return gitIntegrationConfig && gitIntegrationConfig.enabled;
};
export const isVersionHistoryEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isVersionHistoryEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const sessionInfoObject = getSessionInfo();
    const isVersionHistoryEnabled = sessionInfoObject &&
        sessionInfoObject[jsonConstants.IS_VERSION_HISTORY_ENABLED];
    return !!isVersionHistoryEnabled;
};
export const isUsersGroupsV2ExperienceEnabledByDefault = () => {
    const enabled = getConfigInfo(jsonConstants.IS_USERS_GROUPS_V2_ON_BY_DEFAULT);
    return enabled ?? false;
};
export const isUsersGroupsV2ExperienceEnabled = () => {
    const usersGroupsV2ExperienceFlag = flags?.getValue(BLINK_FLAGS.usersGroupsV2Experience.name);
    if (!_.isUndefined(usersGroupsV2ExperienceFlag)) {
        return usersGroupsV2ExperienceFlag;
    }
    const usersGroupsV2ExperienceEnabled = getConfigInfo(jsonConstants.USERS_GROUPS_V2_EXPERIENCE);
    if (!usersGroupsV2ExperienceEnabled) {
        return false;
    }
    return isUsersGroupsV2ExperienceEnabledByDefault();
};
export const isAdminAuthV2PagesExperienceEnabledOnCluster = () => {
    return getConfigInfo(jsonConstants.AUTH_V2_EXPERIENCE) || false;
};
export const isAdminAuthV2PagesExperienceEnabledByDefault = () => {
    const enabled = getConfigInfo(jsonConstants.IS_AUTH_V2_ON_BY_DEFAULT);
    return enabled ?? false;
};
export const isAdminAuthV2PagesExperienceEnabled = () => {
    if (flags?.getValue(BLINK_FLAGS.authV2Experience.name) !== undefined) {
        return flags?.getValue(BLINK_FLAGS.authV2Experience.name);
    }
    if (!isAdminAuthV2PagesExperienceEnabledOnCluster()) {
        return false;
    }
    return isAdminAuthV2PagesExperienceEnabledByDefault();
};
export const isOrgPerUrlEnabled = () => {
    const enabled = getConfigInfo(jsonConstants.ORG_PER_URL_ENABLED);
    return enabled ?? false;
};
export const isGitIntegrationUIEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isGitIntegrationUIEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    return !!getConfigInfo(jsonConstants.GIT_INTEGRATION_UI);
};
export const shouldHideDataTabOnNoManageDataPermission = () => {
    return (flags.getValue(BLINK_FLAGS.hideDataTabOnNoManageDataPermission.name) ||
        getConfigInfo(jsonConstants.HIDE_DATA_TAB_ON_NO_MANAGE_DATA_PERMISSION));
};
export const isManagePermissionsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableManageMCPConnectorPermissions.name);
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    const backendEnabled = getBackendConfig()?.enableManageMCPConnectorPermissions ?? false;
    return isMCPAdminConnectorsEnabled() && backendEnabled;
};
export const isMCPConnectorSharingEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableMCPConnectorSharing.name);
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    const backendEnabled = getBackendConfig()?.enableMCPConnectorSharing ?? false;
    return isMCPAdminConnectorsEnabled() && backendEnabled;
};
export const isConnectorMarketplaceEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableConnectorMarketplace.name);
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    return getBackendConfig()?.enableConnectorMarketplace ?? false;
};
export const isAdminTabVisible = () => {
    if (getMduBannerConfig()) {
        return false;
    }
    if (hasAdminPrivileges() ||
        hasPrivilege(PRIVILEGES.USER_ADMINISTRATION) ||
        hasPrivilege(PRIVILEGES.APPLICATION_ADMINISTRATION) ||
        hasPrivilege(PRIVILEGES.GROUP_ADMINISTRATION) ||
        hasPrivilege(PRIVILEGES.BACKUP_ADMINISTRATION) ||
        hasPrivilege(PRIVILEGES.SYSTEM_INFO_ADMINISTRATION)) {
        return true;
    }
    if (isRolesEnabled() &&
        (hasPrivilege(PRIVILEGES.ROLE_ADMINISTRATION) ||
            hasPrivilege(PRIVILEGES.AUTHENTICATION_ADMINISTRATION) ||
            hasPrivilege(PRIVILEGES.BILLING_INFO_ADMINISTRATION) ||
            hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) ||
            hasPrivilege(PRIVILEGES.CAN_SETUP_VERSION_CONTROL))) {
        return true;
    }
    return false;
};
export const hasAdminOrRbacPrivilege = (privilege) => {
    const sessionInfo = getSessionInfo();
    if (!sessionInfo || !sessionInfo[PRIVILEGES_KEY]) {
        return false;
    }
    return (hasAdminPrivileges() ||
        (isRolesEnabled() &&
            sessionInfo[PRIVILEGES_KEY].some(priv => priv === privilege)));
};
export const isLiveboardHighlightsEnabled = () => {
    if (!isContextualChangeAnalysisEnabled() || !isCortexEnabledOnCluster()) {
        return false;
    }
    const isLiveboardHighlightsEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardHighlightsEnabled.name);
    if (isLiveboardHighlightsEnabledUrlFlag !== undefined) {
        return isLiveboardHighlightsEnabledUrlFlag;
    }
    const isLiveboardHighlightsEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_HIGHLIGHTS);
    return isLiveboardHighlightsEnabledConfig ?? false;
};
export const isLiveboardHighlightsToastEnabled = () => {
    const isLiveboardHighlightsToastEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardHighlightsToastEnabled.name);
    if (isLiveboardHighlightsToastEnabledUrlFlag !== undefined) {
        return isLiveboardHighlightsToastEnabledUrlFlag;
    }
    const isLiveboardHighlightsToastEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_HIGHLIGHTS_TOAST);
    return isLiveboardHighlightsToastEnabledConfig ?? false;
};
export const liveboardHighlightsQueryTimeout = () => {
    const defaultValue = BLINK_FLAGS.liveboardHighlightsQueryTimeout.defaultValue;
    const liveboardHighlightsQueryTimeoutConfig = Number(getConfigInfo(jsonConstants.LIVEBOARD_HIGHLIGHTS_QUERY_TIMEOUT)) ?? LIVEBOARD_INSIGHTS_DATA_QUERY_TIMEOUT;
    return liveboardHighlightsQueryTimeoutConfig <= 0
        ? defaultValue
        : liveboardHighlightsQueryTimeoutConfig;
};
export const hasAnalystEquivalentPriveleges = () => {
    if (hasAdminPrivileges())
        return true;
    if (isRolesEnabled()) {
        return canManageWorksheetViewsTables() && canCreateOrEditConnections();
    }
    return hasDataManagementPrivileges();
};
export const shouldShowServiceAccountOnly = () => {
    return (!!flags.getValue(BLINK_FLAGS.showServiceAccountOnly.name) ||
        !!getConfigInfo(jsonConstants.SHOW_SERVICE_ACCOUNT_ONLY));
};
export const isSoftwareCluster = () => {
    const softwareFlag = flags?.getValue(BLINK_FLAGS.isSoftwareCluster.name);
    if (_.isBoolean(softwareFlag)) {
        return softwareFlag;
    }
    return getConfigInfo(jsonConstants.IS_SOFTWARE_CLUSTER) || false;
};
export const showDeleteCaptainWorkspace = () => {
    return (!!flags.getValue(BLINK_FLAGS.showDeleteCaptainWorkspace.name) ||
        !!getConfigInfo(jsonConstants.SHOW_DELETE_CAPTAIN_WORKSPACE));
};
export const isDynamicColumnWidthForPivotEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isDynamicColumnWidthForPivotEnabled.name);
    if (urlFlag !== undefined) {
        return urlFlag;
    }
    const dynamicColumnWidthForPivot = getConfigInfo(jsonConstants.IS_DYNAMIC_COLUMN_WIDTH_FOR_PIVOT_ENABLED);
    return dynamicColumnWidthForPivot ?? false;
};
export const isShareDebugArtifactsEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableShareDebugArtifacts.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableShareDebugArtifacts ?? false;
};
export const isWelcomeEmailEnabled = () => {
    const emailConfig = getConfigInfo(jsonConstants.EMAIL_CONFIG);
    if (!emailConfig ||
        !Object.prototype.hasOwnProperty.call(emailConfig, jsonConstants.WELCOME_EMAIL_CONFIG)) {
        return true;
    }
    return Object.prototype.hasOwnProperty.call(emailConfig.welcomeEmailConfig, jsonConstants.WELCOME_EMAIL_ENABLED)
        ? emailConfig.welcomeEmailConfig[jsonConstants.WELCOME_EMAIL_ENABLED]
        : true;
};
export const isAllowPVShareEnabled = () => {
    const isAllowPVShareEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isAllowPVShareEnabled.name);
    if (isAllowPVShareEnabledUrlFlag !== undefined) {
        return isAllowPVShareEnabledUrlFlag;
    }
    const isAllowPVShareEnabledConfig = getConfigInfo(jsonConstants.ALLOW_PV_SHARE_ENABLED);
    return isAllowPVShareEnabledConfig ?? true;
};
export const getDigestFrequency = () => {
    return getSessionInfo()[jsonConstants.DIGEST_FREQUENCY];
};
export const isMeasureNamesEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isMeasureNamesEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.MEASURE_NAMES_ENABLED);
    if (!_.isUndefined(urlFlag))
        return urlFlag && isEnabled;
    return isEnabled;
};
export const isAccessibilityWithFormattingEnabled = () => {
    return !!getConfigInfo(jsonConstants.ACCESSIBILITY_WITH_FORMATTING);
};
export const isLiveboardNewBreakPointsEnabled = () => {
    const isLiveboardNewBreakPointsEnabledFlag = flags?.getValue(BLINK_FLAGS.isLiveboardNewBreakPointsEnabled.name);
    if (isLiveboardNewBreakPointsEnabledFlag !== undefined) {
        return isLiveboardNewBreakPointsEnabledFlag;
    }
    const isLiveboardNewBreakPointsEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_BREAKPOINTS);
    return isLiveboardNewBreakPointsEnabledConfig ?? false;
};
export const isScheduleOnHomeEnabled = () => {
    const isScheduleOnHomeEnabledFlag = flags?.getValue(BLINK_FLAGS.isScheduleOnHomeEnabled.name);
    if (isScheduleOnHomeEnabledFlag !== undefined) {
        return isScheduleOnHomeEnabledFlag;
    }
    const isScheduleOnHomeEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_SCHEDULE_HOME_ENABLED);
    return isScheduleOnHomeEnabledConfig ?? false;
};
export const isContextBookPrefetchEnabled = () => {
    const prefetchContextUrl = flags?.getValue(BLINK_FLAGS.isContextBookPrefetchEnabled.name);
    if (prefetchContextUrl !== undefined) {
        return prefetchContextUrl;
    }
    const prefetchContextbookConfig = getConfigInfo(jsonConstants.PREFETCH_CONTEXTBOOK_ENABLED);
    return prefetchContextbookConfig ?? false;
};
export const gradientBufferEnabled = () => {
    const gradientBufferEnabledFlag = flags?.getValue(BLINK_FLAGS.gradientBufferEnabled.name);
    if (!_.isUndefined(gradientBufferEnabledFlag)) {
        return gradientBufferEnabledFlag;
    }
    return getConfigInfo(jsonConstants.GRADIENT_BUFFER_ENABLED) ?? false;
};
export const isInternalAuthEnabledForOkta = () => {
    const allowInternalAuthDisableForOkta = getConfigInfo(jsonConstants.INTERNAL_AUTHWITH_OKTA);
    return !!allowInternalAuthDisableForOkta;
};
export const isPerOrgSubDomainEnabled = () => {
    const enablePerOrgSubDomain = getConfigInfo(jsonConstants.PER_ORG_SUBDOMAIN_ENABLED);
    return !!enablePerOrgSubDomain;
};
export const isPerOrgIdpConfigEnabled = () => {
    const enableIdpConfigPerOrg = getConfigInfo(jsonConstants.IDP_CONFIG_PER_ORG_ENABLED);
    return !!enableIdpConfigPerOrg;
};
export const isRevampedCCAModalEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableRevampedCCAModal.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.a3Config?.enableRevampedCCAModal;
};
export const maxNumExportRows = () => {
    const maxNumExportRowsConfig = Number(getConfigInfo(jsonConstants.MAX_NUM_EXPORT_ROWS));
    return maxNumExportRowsConfig <= 0
        ? DEFAULT_MAX_NUM_EXPORT_ROWS
        : maxNumExportRowsConfig;
};
export const isNarrowCurrencySymbolEnabled = () => {
    const isNarrowCurrencySymbolEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isNarrowCurrencySymbolEnabled.name);
    if (isNarrowCurrencySymbolEnabledUrlFlag !== undefined) {
        return isNarrowCurrencySymbolEnabledUrlFlag;
    }
    return !!getConfigInfo(jsonConstants.NARROW_CURRENCY_SYMBOL_ENABLED);
};
export const getNarrowCurrencyCodes = () => {
    const currenyCodesFromURL = flags?.getValue(BLINK_FLAGS.narrowCurrencyCodes.name);
    if (!_.isEmpty(currenyCodesFromURL))
        return currenyCodesFromURL.toUpperCase();
    const currencyCodesFromConfig = getConfigInfo(jsonConstants.NARROW_CURRENCY_CODES);
    return currencyCodesFromConfig
        ? currencyCodesFromConfig.map((currencyCode) => currencyCode.toUpperCase())
        : [];
};
export const isVizOverrideConfigEnabled = () => {
    return getConfigInfo(jsonConstants.VIZ_OVERRIDE_CONFIG_ENABLED);
};
export const getOrgVizOverrideConfig = () => {
    return getConfigInfo(jsonConstants.ORG_VIZ_OVERRIDE_CONFIG);
};
export const isGenericCustomCompareEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableGenericCustomCompare.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const isGenericCustomCompareEnabled = getConfigInfo(jsonConstants.ENABLE_GENERIC_CUSTOM_COMPARE);
    return _.isNil(isGenericCustomCompareEnabled)
        ? false
        : isGenericCustomCompareEnabled;
};
export const getChangeAnalysisSignificantChangeColor = () => {
    return getConfigInfo(jsonConstants.CHANGE_ANALYSIS_SIGNIFICANT_CHANGE_COLOR);
};
export const isLiveboardScheduleEmailRecipientEnabled = () => {
    const sendToExternal = flags?.getValue(BLINK_FLAGS.isLiveboardScheduleEmailRecipientEnabled.name);
    if (!_.isUndefined(sendToExternal)) {
        return sendToExternal;
    }
    const schedulerConfig = getConfigInfo(jsonConstants.SCHEDULER_CONFIG);
    return schedulerConfig?.isLiveboardScheduleEmailRecipientEnabled ?? true;
};
export const isLiveboardTileGroupingEnabled = () => {
    const isLiveboardTileGroupingEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardTileGroupingEnabled.name);
    if (isLiveboardTileGroupingEnabledUrlFlag !== undefined) {
        return isLiveboardTileGroupingEnabledUrlFlag;
    }
    const isLiveboardTileGroupingEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_TILE_GROUPING);
    return isLiveboardTileGroupingEnabledConfig ?? false;
};
export const isDataUploadVisible = () => {
    if (isTrialVersionEnabled() || isTeamsEnabled()) {
        return true;
    }
    if (getDataBaseMode() === DataBaseMode.FALCON || isFalconCsvUploadMode()) {
        return true;
    }
    return areDatabasesAvailableForCSVUpload();
};
export const isHideChartComponentsForSmallViewport = () => {
    return getConfigInfo(jsonConstants.HIDE_CHART_COMPONENTS_FOR_SMALL_VIEWPORT);
};
export const shouldUsePrismForIAMFlows = () => {
    return getConfigInfo(jsonConstants.USE_PRISM_FOR_IAM_FLOWS);
};
export const isDefaultNumberFormatUnitNone = () => {
    const isDefaultNumberFormatUnitNoneUrlFlag = flags?.getValue(BLINK_FLAGS.defaultNumberFormatUnitNone.name);
    if (isDefaultNumberFormatUnitNoneUrlFlag !== undefined) {
        return isDefaultNumberFormatUnitNoneUrlFlag;
    }
    return !!getConfigInfo(jsonConstants.DEFAULT_NUMBER_FORMAT_UNIT_NONE);
};
export const enableInferCurrencyFromBrowserAtViz = () => {
    const isEnabled = flags?.getValue(BLINK_FLAGS.enableInferCurrencyFromBrowserAtViz.name);
    return (isEnabled ??
        !!getConfigInfo(jsonConstants.ENABLE_INFER_CURRENCY_FROM_BROWSER_AT_VIZ));
};
export const getZeroDecimalCurrencies = () => {
    const zeroDecimalCurrencies = flags?.getValue(BLINK_FLAGS.zeroDecimalCurrencies.name);
    if (zeroDecimalCurrencies !== undefined) {
        return zeroDecimalCurrencies?.toUpperCase();
    }
    const currencyCodesFromConfig = getConfigInfo(jsonConstants.ZERO_DECIMAL_CURRENCIES);
    return currencyCodesFromConfig
        ? currencyCodesFromConfig.map((currencyCode) => currencyCode.toUpperCase())
        : [];
};
export const isDynamicTitleSupported = () => {
    const isDynamicTitleSupported = flags?.getValue(BLINK_FLAGS.isDynamicTitleSupported.name);
    if (isDynamicTitleSupported !== undefined) {
        return isDynamicTitleSupported;
    }
    return !!getConfigInfo(jsonConstants.DYNAMIC_TITLE_SUPPORTED);
};
export const enableHostedChartSameOriginSafari = () => {
    const enableHostedChartSameOriginSafari = flags?.getValue(BLINK_FLAGS.enableHostedChartSameOriginSafari.name);
    if (enableHostedChartSameOriginSafari !== undefined) {
        return enableHostedChartSameOriginSafari;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_HOSTED_CHART_SAME_ORIGIN_SAFARI);
};
export const isAboutInstanceModalEnabled = () => {
    const enableAboutInstanceModalFlag = flags?.getValue(BLINK_FLAGS.enableAboutInstanceModal.name);
    if (enableAboutInstanceModalFlag !== undefined) {
        return enableAboutInstanceModalFlag;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_ABOUT_INSTANCE_MODAL);
};
export const enableMuzeStudio = () => {
    const enableMuzeStudio = flags?.getValue(BLINK_FLAGS.enableMuzeStudio.name);
    if (enableMuzeStudio !== undefined) {
        return enableMuzeStudio;
    }
    return !!getConfigInfo(jsonConstants.MUZE_STUDIO_ENABLED);
};
export const enableMuzeAiChat = () => {
    const enableMuzeAiChat = flags?.getValue(BLINK_FLAGS.enableMuzeAiChat.name);
    if (enableMuzeAiChat !== undefined) {
        return enableMuzeAiChat;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_MUZE_AI_CHAT);
};
export const enableClientSideMnMvCalculation = () => {
    const enableClientSideMnMvCalculation = flags?.getValue(BLINK_FLAGS.enableClientSideMnMvCalculation.name);
    if (enableClientSideMnMvCalculation !== undefined) {
        return enableClientSideMnMvCalculation;
    }
    return !!getConfigInfo(jsonConstants.ENABLE_CLIENT_SIDE_MN_MV_CALCULATION);
};
export const muzeStudioRowsLimit = () => {
    const muzeStudioRowsLimit = flags?.getValue(BLINK_FLAGS.muzeStudioRowsLimit.name);
    if (muzeStudioRowsLimit !== undefined) {
        return muzeStudioRowsLimit;
    }
    return getConfigInfo(jsonConstants.MUZE_STUDIO_ROWS_LIMIT) || 20000;
};
export const isTrimCurrencySpacingEnabled = () => {
    return !!getConfigInfo(jsonConstants.TRIM_CURRENCY_SPACING_ENABLED);
};
export const overrideNullWithValue = () => {
    const overrideNullWithValueUrlFlag = flags?.getValue(BLINK_FLAGS.overrideNullWithValue.name);
    if (overrideNullWithValueUrlFlag !== undefined) {
        return overrideNullWithValueUrlFlag;
    }
    return getConfigInfo(jsonConstants.OVERRIDE_NULL_WITH_VALUE);
};
export const overrideEmptyWithValue = () => {
    const overrideEmptyWithValueUrlFlag = flags?.getValue(BLINK_FLAGS.overrideEmptyWithValue.name);
    if (overrideEmptyWithValueUrlFlag !== undefined) {
        return overrideEmptyWithValueUrlFlag;
    }
    return getConfigInfo(jsonConstants.OVERRIDE_EMPTY_WITH_VALUE);
};
export const getMaxFileSizeBytes = () => {
    return getConfigInfo(jsonConstants.MAX_LOGO_FILE_SIZE_IN_BYTES);
};
export const shouldQueryDataWithSetTMLString = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.shouldQueryDataWithSetTMLString.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.shouldQueryDataWithSetTMLString ?? false;
};
export const arePdfCoverFilterPageCheckboxesEnabled = () => {
    const arePdfCoverFilterPageCheckboxesEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.arePdfCoverFilterPageCheckboxesEnabled.name);
    if (arePdfCoverFilterPageCheckboxesEnabledUrlFlag !== undefined) {
        return arePdfCoverFilterPageCheckboxesEnabledUrlFlag;
    }
    const arePdfCoverFilterPageCheckboxesEnabled = getConfigInfo(jsonConstants.PDF_OPTIONS.ARE_PDF_COVER_FILTER_PAGE_CHECKBOXES_ENABLED);
    return arePdfCoverFilterPageCheckboxesEnabled ?? false;
};
export const arePdfCoverFilterPageDefaultsChecked = () => {
    const arePdfCoverFilterPageDefaultsCheckedFlag = flags?.getValue(BLINK_FLAGS.arePdfCoverFilterPageDefaultsChecked.name);
    if (arePdfCoverFilterPageDefaultsCheckedFlag !== undefined) {
        return arePdfCoverFilterPageDefaultsCheckedFlag;
    }
    const arePdfCoverFilterPageDefaultsChecked = getConfigInfo(jsonConstants.PDF_OPTIONS.ARE_PDF_COVER_FILTER_PAGE_DEFAULTS_CHECKED);
    return arePdfCoverFilterPageDefaultsChecked ?? true;
};
export const isRerunQueryEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableRerunQuery.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableRerunQuery ?? false;
};
export const isQueryCancellationEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableQueryCancellationFeature.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableQueryCancellation ?? false;
};
export const isStopAnswerGenerationEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableStopAnswerGeneration.name);
    if (!_.isNil(urlFlag)) {
        return urlFlag;
    }
    const backendConfig = getBackendConfig();
    return backendConfig?.enableStopAnswerGeneration ?? true;
};
export const isShareChatEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isShareChatEnabled.name);
    const backendConfig = getBackendConfig();
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    return backendConfig?.isShareChatEnabled ?? false;
};
export const isChunkedMetadataFetchEnabled = () => {
    return getConfigInfo(jsonConstants.ENABLE_CHUNKED_METADATA_FETCH) ?? false;
};
export const isOnlyWebhookForAlertCreationEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableOnlyWebhookForAlertCreation.name);
    if (!_.isUndefined(envFlag)) {
        return envFlag;
    }
    return !!getBackendConfig()?.a3Config?.enableOnlyWebhookForAlertCreation;
};
export const isGroupSubscriptionsInMonitorEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.enableGroupSubscriptionsInMonitor.name);
    if (!_.isUndefined(envFlag)) {
        return envFlag;
    }
    return !!getBackendConfig()?.a3Config?.enableGroupSubscriptionsInMonitor;
};
export const isLiveboardXLSXCSVDownloadEnabled = () => {
    const isLiveboardXLSXCSVDownloadEnabledUrlFlag = flags?.getValue(BLINK_FLAGS.isLiveboardXLSXCSVDownloadEnabled.name);
    if (isLiveboardXLSXCSVDownloadEnabledUrlFlag !== undefined) {
        return isLiveboardXLSXCSVDownloadEnabledUrlFlag;
    }
    const isLiveboardXLSXCSVDownloadEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_XLSX_CSV_DOWNLOAD);
    return isLiveboardXLSXCSVDownloadEnabledConfig ?? false;
};
export const isCentralizedLiveboardFilterUXEnabled = () => {
    const envFlag = flags?.getValue(BLINK_FLAGS.isCentralizedLiveboardFilterUXEnabled.name);
    if (envFlag !== undefined) {
        return envFlag;
    }
    const isCentralizedLiveboardFilterUXEnabledConfig = getConfigInfo(jsonConstants.LIVEBOARD_CENTRALIZED_FILTER_UX);
    return isCentralizedLiveboardFilterUXEnabledConfig ?? false;
};
export const smartSortV2Enabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.smartSortV2Enabled.name);
    if (urlFlag !== undefined) {
        return urlFlag;
    }
    const smartSortV2Enabled = getConfigInfo(jsonConstants.SMART_SORT_V2_ENABLED);
    return smartSortV2Enabled ?? false;
};
export const isObjectPublishingEnabled = () => {
    const config = getConfigInfo(jsonConstants.OBJECT_PUBLISHING.CONFIG);
    if (!config)
        return false;
    const publishingEnabled = config[jsonConstants.OBJECT_PUBLISHING.ENABLED];
    if (!publishingEnabled)
        return false;
    const urlFlag = flags?.getValue(BLINK_FLAGS.isObjectPublishingEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const publishingUIEnabled = config[jsonConstants.OBJECT_PUBLISHING.UI_ENABLED];
    return !!publishingUIEnabled;
};
export const isVariablesUIEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.isVariablesUIEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const configFlag = getConfigInfo(jsonConstants.VARIABLES_UI_ENABLED);
    return !!configFlag;
};
export const isCustomBannersEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.enableCustomBanners.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const configFlag = getConfigInfo(jsonConstants.ENABLE_CUSTOM_BANNERS);
    return !!configFlag;
};
export const isAutomaticTranslation = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.automaticTranslationOfObjectMetadata.name);
    const orgId = getCurrentOrgId().toString();
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    const clusterFlag = !!getConfigInfo(jsonConstants.ENABLE_AUTOMATIC_TRANSLATION);
    if (!clusterFlag) {
        return false;
    }
    const perOrgOverrides = getConfigInfo('automaticTranslationOfObjectMetadataPerOrg');
    if (perOrgOverrides) {
        if (perOrgOverrides[orgId] !== undefined) {
            const result = !!perOrgOverrides[orgId];
            return result;
        }
    }
    return clusterFlag;
};
export const isManualTranslation = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.manualTranslationOfObjectMetadata.name);
    const orgId = getCurrentOrgId().toString();
    if (!_.isUndefined(urlFlag)) {
        return urlFlag;
    }
    const clusterFlag = !!getConfigInfo(jsonConstants.ENABLE_MANUAL_TRANSLATION);
    if (!clusterFlag) {
        return false;
    }
    const perOrgOverrides = getConfigInfo(jsonConstants.ENABLE_MANUAL_TRANSLATION_PER_ORG);
    if (perOrgOverrides) {
        if (perOrgOverrides[orgId] !== undefined) {
            const result = !!perOrgOverrides[orgId];
            return result;
        }
    }
    return clusterFlag;
};
export const isManualTranslationConfigEnabled = () => {
    return !!getConfigInfo('manualTranslationConfig')?.enabled;
};
export const getDisableLiveboardTranslation = () => {
    const clusterFlag = getConfigInfo(jsonConstants.DISABLE_LIVEBOARD_TRANSLATION);
    let configArray = [];
    if (typeof clusterFlag === 'string') {
        configArray = clusterFlag
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }
    else if (Array.isArray(clusterFlag)) {
        configArray = clusterFlag;
    }
    else if (clusterFlag) {
        configArray = [clusterFlag];
    }
    return configArray;
};
export const isSplitDownloadPrivilegesEnabled = () => {
    return getConfigInfo(jsonConstants.SPLIT_DOWNLOAD_PRIVILEGES) ?? false;
};
export const isLiveboardRBACPrivilegesV2Enabled = () => {
    return (getConfigInfo(jsonConstants.LIVEBOARD_RBAC_PRIVILEGES_V2_ENABLED) ??
        false);
};
export const canCreateLiveboard = () => {
    if (!isLiveboardRBACPrivilegesV2Enabled()) {
        return true;
    }
    const sessionInfo = getSessionInfo();
    if (!sessionInfo || !sessionInfo.privileges) {
        return true;
    }
    return hasCreateLiveboardPrivilege();
};
export const canCreateAnswers = () => {
    if (!isLiveboardRBACPrivilegesV2Enabled()) {
        return true;
    }
    const sessionInfo = getSessionInfo();
    if (!sessionInfo || !sessionInfo.privileges) {
        return true;
    }
    return hasCreateAnswersPrivilege();
};
export const canAnalyzeData = () => {
    if (!isLiveboardRBACPrivilegesV2Enabled()) {
        return true;
    }
    const sessionInfo = getSessionInfo();
    if (!sessionInfo || !sessionInfo.privileges) {
        return true;
    }
    return hasAnalyzeDataPrivilege();
};
export const canAdministerSchedules = () => {
    const sessionInfo = getSessionInfo();
    if (!isLiveboardRBACPrivilegesV2Enabled() ||
        !sessionInfo ||
        !sessionInfo.privileges) {
        return hasAdminPrivileges();
    }
    return hasAdministerSchedulesPrivilege();
};
export var BannerLevel;
(function (BannerLevel) {
    BannerLevel["CLUSTER"] = "CLUSTER";
    BannerLevel["ORG"] = "ORG";
})(BannerLevel || (BannerLevel = {}));
export const getCustomBannerConfig = (level) => {
    const sessionInfo = getSessionInfo();
    if (!sessionInfo) {
        return null;
    }
    const customBanners = sessionInfo[jsonConstants.CUSTOM_BANNERS];
    if (!customBanners || !level) {
        return customBanners;
    }
    return customBanners[level] || null;
};
export const muzeChartPhase1Enabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeChartPhase1Enabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeChartPhase1Enabled = getConfigInfo(jsonConstants.MUZE_CHART_PHASE_1_ENABLED);
    return muzeChartPhase1Enabled ?? false;
};
export const muzeDonutChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeDonutChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeDonutChartEnabled = getConfigInfo(jsonConstants.MUZE_DONUT_CHART_ENABLED);
    return muzeDonutChartEnabled ?? false;
};
export const muzeCandlestickChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeCandlestickChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeCandlestickChartEnabled = getConfigInfo(jsonConstants.MUZE_CANDLESTICK_CHART_ENABLED);
    return muzeCandlestickChartEnabled ?? false;
};
export const muzeScatterChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeScatterChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeScatterChartEnabled = getConfigInfo(jsonConstants.MUZE_SCATTER_CHART_ENABLED);
    return muzeScatterChartEnabled ?? false;
};
export const muzeBubbleChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeBubbleChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeBubbleChartEnabled = getConfigInfo(jsonConstants.MUZE_BUBBLE_CHART_ENABLED);
    return muzeBubbleChartEnabled ?? false;
};
export const muzeHeatmapChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeHeatmapChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeHeatmapChartEnabled = getConfigInfo(jsonConstants.MUZE_HEATMAP_CHART_ENABLED);
    return muzeHeatmapChartEnabled ?? false;
};
export const muzeWaterfallChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeWaterfallChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeWaterfallChartEnabled = getConfigInfo(jsonConstants.MUZE_WATERFALL_CHART_ENABLED);
    return muzeWaterfallChartEnabled ?? false;
};
export const muzeParetoChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeParetoChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeParetoChartEnabled = getConfigInfo(jsonConstants.MUZE_PARETO_CHART_ENABLED);
    return muzeParetoChartEnabled ?? false;
};
export const muzeFunnelChartEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeFunnelChartEnabled.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeFunnelChartEnabled = getConfigInfo(jsonConstants.MUZE_FUNNEL_CHART_ENABLED);
    return muzeFunnelChartEnabled ?? false;
};
export const muzeChartPhase1EnabledGA = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.muzeChartPhase1EnabledGA.name);
    if (!_.isUndefined(urlFlag))
        return urlFlag;
    const muzeChartPhase1EnabledGA = getConfigInfo(jsonConstants.MUZE_CHART_PHASE_1_ENABLED_GA);
    return muzeChartPhase1EnabledGA ?? false;
};
export const getRefreshTokenConfig = () => {
    const backendConfig = getBackendConfig();
    if (!backendConfig) {
        return {
            usageThreshold: 0.8,
            minRemainingSeconds: 60,
            proactiveRefreshApis: [],
        };
    }
    const defaultRefreshThresholdPct = backendConfig?.defaultRefreshThresholdPct
        ? backendConfig?.defaultRefreshThresholdPct / 100
        : 0.8;
    const proactiveRefreshApis = backendConfig?.proactiveRefreshApis || [
        '/query',
        'prism/?op=SendMessage',
    ];
    return {
        usageThreshold: defaultRefreshThresholdPct,
        minRemainingSeconds: 60,
        proactiveRefreshApis,
    };
};
export const enableThemeBuilder = () => {
    return getConfigInfo(jsonConstants.ENABLE_THEME_BUILDER) ?? true;
};
export const isKpiRenderOptimizationEnabled = () => {
    const enableKpiRenderOptimizationFlag = flags?.getValue(BLINK_FLAGS.enableKpiRenderOptimization?.name);
    if (!_.isUndefined(enableKpiRenderOptimizationFlag))
        return enableKpiRenderOptimizationFlag;
    const enableKpiRenderOptimization = getConfigInfo(jsonConstants.ENABLE_KPI_RENDER_OPTIMIZATION);
    return enableKpiRenderOptimization ?? false;
};
export const isLayoutOptimizationsEnabled = () => {
    const enableLayoutOptimizationsFlag = flags?.getValue(BLINK_FLAGS.enableLayoutOptimizations?.name);
    if (!_.isUndefined(enableLayoutOptimizationsFlag))
        return enableLayoutOptimizationsFlag;
    const enableLayoutOptimizations = getConfigInfo(jsonConstants.ENABLE_LAYOUT_OPTIMIZATIONS);
    return enableLayoutOptimizations ?? false;
};
export const isSafariOptimizationEnabled = () => {
    return flags?.getValue(BLINK_FLAGS.enableSafariOptimization?.name) ?? false;
};
export const isAdminModernExperienceEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.adminModernExperienceEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const config = getConfigInfo(jsonConstants.ADMIN_MODERN_EXPERIENCE.CONFIG);
    if (!config)
        return false;
    return !!config[jsonConstants.ADMIN_MODERN_EXPERIENCE.ENABLED];
};
export const isModernFeatureManagementEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.adminFeatureManagementEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const config = getConfigInfo(jsonConstants.ADMIN_MODERN_EXPERIENCE.CONFIG);
    if (!config)
        return false;
    return !!config[jsonConstants.ADMIN_MODERN_EXPERIENCE.FEATURE_MANAGEMENT_ENABLED];
};
export const isAdminRoutingEnabled = () => {
    const urlFlag = flags?.getValue(BLINK_FLAGS.adminRoutingEnabled.name);
    if (_.isBoolean(urlFlag)) {
        return urlFlag;
    }
    const config = getConfigInfo(jsonConstants.ADMIN_MODERN_EXPERIENCE.CONFIG);
    if (!config)
        return false;
    return !!config[jsonConstants.ADMIN_MODERN_EXPERIENCE.ADMIN_ROUTING_ENABLED];
};
export const isRadiantThemeEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.radiantThemeEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.RADIANT_THEME_ENABLED);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const isImpersonationEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.impersonationEnabled.name);
    const impersonationConfig = getConfigInfo(jsonConstants.IMPERSONATION.CONFIG);
    const isEnabled = !!impersonationConfig?.[jsonConstants.IMPERSONATION.ENABLED];
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const getImpersonationInfo = () => {
    return getSessionInfo()?.impersonationInfo;
};
export const isImpersonatedSession = () => {
    return !!getImpersonationInfo()?.isImpersonated;
};
export const isFrontendTracingEnabled = () => {
    const urlFlagValue = flags?.getValue(BLINK_FLAGS.frontendTracingEnabled.name);
    const isEnabled = !!getConfigInfo(jsonConstants.FRONTEND_TRACING_ENABLED);
    return !_.isUndefined(urlFlagValue) ? urlFlagValue : isEnabled;
};
export const getDeveloperNavConfig = () => {
    const backendConfig = getBackendConfig();
    if (!backendConfig) {
        return {
            hideRestV1Playground: true,
            hideGraphQLPlayground: true,
        };
    }
    return {
        hideRestV1Playground: backendConfig?.hideRestV1Playground ?? true,
        hideGraphQLPlayground: backendConfig?.hideGraphQLPlayground ?? true,
    };
};
export { PRIVILEGES, getExposedUserPreferences, setExposedUserPreference, SESSION_USER_PREFERENCES, getPreference, setPreference, setTip, };
//# sourceMappingURL=session-store.js.map